import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { performAction, type CatalogMode } from '../api/sophia';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { getTagColor, getContrastColor, parseWeborderTags } from '../utils/tagRendering';
import OptimizationConfigEditor from './OptimizationConfigEditor';
import NewProjectModal from './NewProjectModal';
import { SkuOrderForecastBadges, compareTaglie, type TaglieBreakdown } from './SkuOrderForecastBadges';
import { buildSkuOrdersByShopParams, parseShopIds, type SkuOrdersShopEntry } from './ordersByShop';
import { fetchThread, selectThreadAppState, setHasUnsavedMpChanges } from '../features/thread/threadSlice';
import { hasSucceededRun } from '../features/thread/optimizationDraft';

interface ConstraintMetric {
  actual: number;
  lowerBound?: number;
  upperBound?: number;
  active: boolean;
  // Capacita' del piano come sta a database, prima dello scalo per la run collegata.
  // `upperBound` e' la residua: e' quella che si mostra e si modifica, ma salvare quella
  // consumerebbe il piano a ogni salvataggio, perche' alla lettura dopo verrebbe scalata
  // di nuovo. Al salvataggio si manda sempre `rawUpperBound`, spostato dello stesso delta
  // applicato dall'utente. Senza run collegata i due valori coincidono.
  rawUpperBound?: number;
}

interface SkuConstraintRow {
  sku: string;
  // Ordini gia' piazzati e run collegata, per il tooltip accanto allo SKU.
  orderQuantity?: number;
  taglieOrder?: TaglieBreakdown;
  orderQuantityVenduta?: number;
  taglieOrderVenduta?: TaglieBreakdown;
  linkedOrderQuantity?: number;
  taglieLinkedOrder?: TaglieBreakdown;
  description?: string;
  minValue: number;
  lbQuantity: number;
  ubQuantity: number;
  lbQuantityPerShop: number;
  ubQuantityPerShop: number;
  modelCode?: string;
  tipoFilatoDs?: string;
  tipoFilatoMp?: string;
  priceWhs?: number;
  priceRtl?: number;
  forecast?: number;
  imagePath?: string;
  weborderTags?: string[];
  taglieLocked?: boolean;
  taglieLockSource?: string;
  notInPerimeter?: boolean;
}

interface TipoFilatoSection {
  tipoFilato: string;
  lowBoundaryValue: number;
  editableMinValue: number;
  skuRows: SkuConstraintRow[];
  constraints: {
    economicValue: ConstraintMetric;
    items: ConstraintMetric;
    uniqueModels: ConstraintMetric;
    // numero di SKU distinti previsto dal MP (colonna SKU COUNT): presente solo nei
    // formati che la contengono, altrimenti resta inattivo
    skuCount: ConstraintMetric;
    avgPrice: ConstraintMetric;
  };
}

interface BudgetConstraintGroup {
  actuals: Array<{
    consegna: string;
    value: number;
    items: number;
  }>;
  upperBounds: Record<string, { value?: number; items?: number; active: boolean }>;
}

interface ShopMerchandisingPlan {
  shopId: string;
  shopLabel: string;
  budget: BudgetConstraintGroup;
  tipoFilatoSections: TipoFilatoSection[];
}

interface MerchandisingPlanPageData {
  configId: string;
  shops: Array<{ id: string; label: string }>;
  defaultShopId: string;
  byShop: Record<string, ShopMerchandisingPlan>;
}

const EMPTY_PAGE_DATA: MerchandisingPlanPageData = {
  configId: '',
  shops: [],
  defaultShopId: '',
  byShop: {},
};

const EXCEL_BASE_BLOCK_START_COL = 12;
const EXCEL_BLOCK_WIDTH_COLS = 19;
const EXCEL_MAX_SIZE_COLS = 16;

const excelCellValue = (ws: XLSX.WorkSheet, row: number, col: number): unknown => ws[XLSX.utils.encode_cell({ r: row - 1, c: col - 1 })]?.v;

const normalizeExcelToken = (value: unknown): string => String(value ?? '').trim();

const normalizeExcelCodTipoTaglia = (value: unknown): string => {
  const raw = normalizeExcelToken(value);
  return /^\d+$/.test(raw) ? String(Number(raw)).padStart(2, '0') : raw;
};

const findExcelHeaderRow = (ws: XLSX.WorkSheet): number => {
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:A1');
  for (let row = range.s.r + 1; row <= range.e.r + 1; row += 1) {
    if (normalizeExcelToken(excelCellValue(ws, row, 1)).toUpperCase() === '#PO') return row;
  }
  return 8;
};

const findExcelSizeLegendRow = (ws: XLSX.WorkSheet, blockStartCol: number, headerRow: number, iArr: number): number | null => {
  for (let row = 1; row < headerRow; row += 1) {
    if (Number(excelCellValue(ws, row, blockStartCol)) === Number(iArr)) return row;
  }
  return null;
};

function isUsableMediaValue(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lowered = trimmed.toLowerCase();
  if (['none', 'null', 'undefined', 'nan'].includes(lowered)) return false;
  if (trimmed.startsWith('/Users/')) return false;
  if (trimmed.startsWith('file://')) return false;
  return true;
}

function extractImageCandidates(value: unknown): string[] {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string' && isUsableMediaValue(item))
      .map((item) => item.trim());
  }

  if (typeof value !== 'string') {
    if (value && typeof value === 'object') {
      return Object.values(value as Record<string, unknown>)
        .filter((item): item is string => typeof item === 'string' && isUsableMediaValue(item))
        .map((item) => item.trim());
    }
    return [];
  }

  const trimmed = value.trim();
  if (!isUsableMediaValue(trimmed)) return [];

  try {
    if (trimmed.startsWith('{') && trimmed.endsWith('}') && !trimmed.includes(':')) {
      return trimmed
        .slice(1, -1)
        .split(',')
        .map((item) => item.trim())
        .filter(isUsableMediaValue);
    }

    const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
    if (Array.isArray(parsed)) {
      return parsed
        .filter((item): item is string => typeof item === 'string' && isUsableMediaValue(item))
        .map((item) => item.trim());
    }
    if (parsed && typeof parsed === 'object') {
      return Object.values(parsed)
        .filter((item): item is string => typeof item === 'string' && isUsableMediaValue(item))
        .map((item) => item.trim());
    }
  } catch {
  }

  return trimmed
    .replace(/[{}]/g, '')
    .split(',')
    .map((item) => item.trim())
    .filter(isUsableMediaValue);
}

function extractFirstImagePath(info: any): string | undefined {
  const images = [
    ...extractImageCandidates(info?.url_img_list),
    ...extractImageCandidates(info?.sketch_url),
    ...extractImageCandidates(info?.image_path),
  ];

  return images.find(Boolean);
}

function normalizeTags(value: any): string[] {
  const parsed = parseWeborderTags(value);
  if (parsed.length > 0) return parsed.map((tag) => String(tag).trim()).filter(Boolean);

  if (Array.isArray(value)) {
    return value.map((tag) => String(tag).trim()).filter(Boolean);
  }

  if (value && typeof value === 'object') {
    return Object.values(value)
      .map((tag) => String(tag).trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsedJson = JSON.parse(trimmed.replace(/'/g, '"'));
      if (Array.isArray(parsedJson)) {
        return parsedJson.map((tag) => String(tag).trim()).filter(Boolean);
      }
      if (parsedJson && typeof parsedJson === 'object') {
        return Object.values(parsedJson).map((tag) => String(tag).trim()).filter(Boolean);
      }
    } catch {
    }

    return trimmed
      .replace(/[{}]/g, '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  return [];
}

function formatSingleShopName(rawName: string): string {
  return rawName
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function getSingleShopDisplayName(shopId: string, shopMapping?: Record<string, string>): string {
  const cleanId = shopId.replace(/\|$/, '').trim();
  if (shopMapping && shopMapping[cleanId]) {
    return formatSingleShopName(shopMapping[cleanId]);
  }
  return cleanId;
}

function hasDefinedBound(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

function formatTransferMetricValue(metric: TipoFilatoTransferMetric, value: number): string {
  if (metric === 'economicValue') {
    return `€ ${Math.round(value).toLocaleString('it-IT')}`;
  }
  return `${Math.round(value).toLocaleString('it-IT')} pezzi`;
}

function hasTipoFilatoMetricConstraint(section: TipoFilatoSection, metric: TipoFilatoTransferMetric): boolean {
  return hasDefinedBound(section.constraints[metric].upperBound);
}

function getShopDisplayName(shopId: string, shopMapping?: Record<string, string>): string {
  if (shopId.includes('|,|')) {
    const shopIds = shopId
      .split('|,|')
      .map((id) => id.replace(/\|$/, '').trim())
      .filter((id) => id.length > 0);

    return shopIds.map((id) => getSingleShopDisplayName(id, shopMapping)).join(', ');
  }

  return getSingleShopDisplayName(shopId, shopMapping);
}

const SHOP_MAPPING_STORAGE_PREFIX = 'mp-shop-mapping:';
const AREA_TAB_ID = '__AREA__';
// SPJ-893: nascondere il pulsante di import SKU da Excel dalla UI, mantenendo
// la funzionalità nel codice (handleImportFixedTaglieExcel). Rimettere a true
// per riesporre il pulsante.
const SHOW_EXCEL_SKU_IMPORT_BUTTON = false;

function readPersistedMerchandisingShopMapping(threadId?: string | null): Record<string, string> | undefined {
  if (typeof window === 'undefined' || !threadId) return undefined;
  try {
    const raw = window.localStorage.getItem(`${SHOP_MAPPING_STORAGE_PREFIX}${threadId}`);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed as Record<string, string> : undefined;
  } catch (error) {
    console.warn('Failed to read persisted merchandising shop mapping', error);
    return undefined;
  }
}

function applyShopLabels(
  pageData: MerchandisingPlanPageData,
  shopMapping?: Record<string, string>,
): MerchandisingPlanPageData {
  if (!shopMapping || Object.keys(shopMapping).length === 0) {
    return pageData;
  }

  return {
    ...pageData,
    shops: pageData.shops.map((shop) => ({
      ...shop,
      label: shopMapping[shop.id] || shop.label,
    })),
    byShop: Object.fromEntries(
      Object.entries(pageData.byShop).map(([shopId, shop]) => [
        shopId,
        {
          ...shop,
          shopLabel: shopMapping[shopId] || shop.shopLabel,
        },
      ]),
    ),
  };
}

function parseOptionalNumericId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  if (value && typeof value === 'object' && 'id' in value) {
    return parseOptionalNumericId((value as { id?: unknown }).id);
  }

  return null;
}

function distributeByBounds(
  total: number,
  entries: Array<{ consegna: string }>,
  getBound: (consegna: string) => number | undefined,
): Record<string, number> {
  if (!entries.length) {
    return {};
  }

  const weights = entries.map((entry) => {
    const rawBound = getBound(entry.consegna);
    const bound = typeof rawBound === 'number' && Number.isFinite(rawBound) ? rawBound : Number(rawBound);
    return Math.max(0, Number.isFinite(bound) ? bound : 0);
  });

  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  if (totalWeight > 0) {
    return Object.fromEntries(
      entries.map((entry, index) => [entry.consegna, (total * weights[index]) / totalWeight]),
    );
  }

  const equalShare = total / entries.length;
  return Object.fromEntries(entries.map((entry) => [entry.consegna, equalShare]));
}

function mapContextResponseToPageData(contextResponse: any, fallback: MerchandisingPlanPageData): MerchandisingPlanPageData {
  const contexts = Array.isArray(contextResponse?.configs) ? contextResponse.configs : [];
  if (!contexts.length) return fallback;

  const shopMapping = contextResponse?.shop_mapping && typeof contextResponse.shop_mapping === 'object'
    ? contextResponse.shop_mapping as Record<string, string>
    : undefined;
  const shops = contexts.map((entry: any) => ({
    id: String(entry?.shop ?? ''),
    label: getShopDisplayName(String(entry?.shop ?? ''), shopMapping),
  })).filter((shop: { id: string }) => shop.id.length > 0);

  if (!shops.length) return fallback;

  const byShop = Object.fromEntries(
    contexts.map((entry: any) => {
      const shopId = String(entry?.shop ?? '');
      const skuInfos = entry?.sku_infos && typeof entry.sku_infos === 'object' ? entry.sku_infos : {};
      const mpConstraints = entry?.constraints?.mp && typeof entry.constraints.mp === 'object' ? entry.constraints.mp : {};
      const budgetConstraints = entry?.constraints?.budget && typeof entry.constraints.budget === 'object' ? entry.constraints.budget : {};

      const skuRowsByTipoFilato: Record<string, SkuConstraintRow[]> = {};
      Object.entries(skuInfos).forEach(([sku, info]: [string, any]) => {
        const forecast = Number(info?.forecast ?? 0) || 0;
        const priceWhs = Number(info?.price_whs ?? 0) || 0;
        const priceRtl = Number(info?.price_rtl ?? 0) || 0;
        const lbQuantity = Number(info?.lb_quantity ?? info?.min_allowed_value ?? 0) || 0;
        const ubQuantity = Number(info?.ub_quantity ?? 100000000000000000) || 100000000000000000;
        const lbQuantityPerShop = Number(info?.lb_quantity_per_shop ?? lbQuantity) || 0;
        const ubQuantityPerShop = Number(info?.ub_quantity_per_shop ?? ubQuantity ?? 100000000000000000) || 100000000000000000;
        const tipoFilato = String(info?.tipo_filato_mp || 'Senza Tipo Filato');

        const row: SkuConstraintRow = {
          sku,
          orderQuantity: Number(info?.order_quantity ?? 0) || 0,
          taglieOrder: info?.taglie_order ?? undefined,
          orderQuantityVenduta: Number(info?.order_quantity_venduta ?? 0) || 0,
          taglieOrderVenduta: info?.taglie_order_venduta ?? undefined,
          linkedOrderQuantity: Number(info?.linked_order_quantity ?? 0) || 0,
          taglieLinkedOrder: info?.taglie_linked_order ?? undefined,
          description: info?.model || undefined,
          minValue: lbQuantityPerShop,
          lbQuantity,
          ubQuantity,
          lbQuantityPerShop,
          ubQuantityPerShop,
          modelCode: info?.model || undefined,
          tipoFilatoDs: info?.tipo_filato ? String(info.tipo_filato) : undefined,
          tipoFilatoMp: info?.tipo_filato_mp ? String(info.tipo_filato_mp) : undefined,
          priceWhs,
          priceRtl,
          forecast,
          imagePath: extractFirstImagePath(info),
          taglieLocked: Boolean(info?.taglie_locked),
          taglieLockSource: info?.taglie_lock_source ? String(info.taglie_lock_source) : undefined,
          weborderTags: (() => {
            const direct = normalizeTags(info?.weborder_tags ?? info?.tags ?? info?.season_tags);
            if (direct.length > 0) return direct;
            const source = `${sku} ${info?.model || ''} ${info?.weborder_tags || ''} ${info?.tags || ''} ${info?.season_tags || ''}`.toUpperCase();
            const inferred: string[] = [];
            if (source.includes('SPRING')) inferred.push('SPRING');
            if (source.includes('RESORT')) inferred.push('RESORT');
            return inferred;
          })(),
        };

        if (!skuRowsByTipoFilato[tipoFilato]) {
          skuRowsByTipoFilato[tipoFilato] = [];
        }
        skuRowsByTipoFilato[tipoFilato].push(row);
      });

      // SPJ-929: dentro lo stesso tipo filato, ordina le SKU per prezzo whs decrescente (tie-break stabile per codice)
      Object.values(skuRowsByTipoFilato).forEach((rows) => {
        rows.sort((a, b) => {
          const priceDiff = (b.priceWhs ?? 0) - (a.priceWhs ?? 0);
          if (priceDiff !== 0) return priceDiff;
          return a.sku.localeCompare(b.sku);
        });
      });

      const mpEntries = Object.entries(mpConstraints) as Array<[string, any]>;
      const normalizedMpEntries: Array<[string, any]> = mpEntries.length ? mpEntries : [];

      const baseSections: TipoFilatoSection[] = normalizedMpEntries.map(([tipoFilatoRaw, values]) => {
        const tipoFilato = String(tipoFilatoRaw);
        const tfRows = skuRowsByTipoFilato[tipoFilato] || [];
        const hasValueConstraint = values?.value !== undefined && values?.value !== null && values?.value !== '';
        const hasItemConstraint = values?.items !== undefined && values?.items !== null && values?.items !== '';
        const hasModelsConstraint = values?.models_unique !== undefined && values?.models_unique !== null && values?.models_unique !== '';
        const hasSkuCountConstraint = values?.sku_count !== undefined && values?.sku_count !== null && values?.sku_count !== '';
        const hasAvgPriceConstraint = values?.avg_price !== undefined && values?.avg_price !== null && values?.avg_price !== '';
        const valueConstraint = hasValueConstraint ? Number(values.value) || 0 : undefined;
        const itemConstraint = hasItemConstraint ? Number(values.items) || 0 : undefined;
        const modelsConstraint = hasModelsConstraint ? Number(values.models_unique) || 0 : undefined;
        const skuCountConstraint = hasSkuCountConstraint ? Number(values.sku_count) || 0 : undefined;
        const avgPriceConstraint = hasAvgPriceConstraint ? Number(values.avg_price) || 0 : undefined;
        // Capacita' a database. Il backend la manda accanto alla residua proprio perche'
        // lo scalo non e' invertibile: dove il residuo supera la capacita' la residua viene
        // azzerata, e da uno zero non si risale al valore originale.
        const rawValueConstraint = values?.value_raw !== undefined && values?.value_raw !== null
          ? Number(values.value_raw) || 0 : valueConstraint;
        const rawItemConstraint = values?.items_raw !== undefined && values?.items_raw !== null
          ? Number(values.items_raw) || 0 : itemConstraint;

        return {
          tipoFilato,
          lowBoundaryValue: 0,
          editableMinValue: 0,
          skuRows: tfRows,
          constraints: {
            economicValue: {
              actual: valueConstraint ?? 0,
              lowerBound: hasValueConstraint ? 0 : undefined,
              upperBound: valueConstraint,
              rawUpperBound: rawValueConstraint,
              active: hasValueConstraint,
            },
            items: {
              actual: itemConstraint ?? 0,
              lowerBound: itemConstraint,
              upperBound: itemConstraint,
              rawUpperBound: rawItemConstraint,
              active: hasItemConstraint,
            },
            uniqueModels: {
              actual: modelsConstraint ?? 0,
              lowerBound: modelsConstraint,
              upperBound: modelsConstraint,
              active: hasModelsConstraint,
            },
            skuCount: {
              actual: skuCountConstraint ?? 0,
              lowerBound: skuCountConstraint,
              upperBound: skuCountConstraint,
              active: hasSkuCountConstraint,
            },
            avgPrice: {
              actual: avgPriceConstraint ?? 0,
              lowerBound: hasAvgPriceConstraint ? 0 : undefined,
              upperBound: avgPriceConstraint,
              active: hasAvgPriceConstraint,
            },
          },
        };
      });

      const coveredTf = new Set(baseSections.map((section) => section.tipoFilato));
      const extraSections: TipoFilatoSection[] = Object.entries(skuRowsByTipoFilato)
        .filter(([tipoFilato]) => !coveredTf.has(tipoFilato))
        .map(([tipoFilato, tfRows]) => {
          const tfRowsMinSum = tfRows.reduce((sum, row) => sum + row.minValue, 0);
          return {
            tipoFilato,
            lowBoundaryValue: 0,
            editableMinValue: 0,
            skuRows: tfRows,
            constraints: {
              economicValue: { actual: 0, lowerBound: undefined, upperBound: undefined, active: false },
              items: { actual: 0, lowerBound: undefined, upperBound: undefined, active: false },
              uniqueModels: { actual: 0, lowerBound: undefined, upperBound: undefined, active: false },
              skuCount: { actual: 0, lowerBound: undefined, upperBound: undefined, active: false },
              avgPrice: { actual: 0, lowerBound: undefined, upperBound: undefined, active: false },
            },
          };
        });

      const tipoFilatoSections: TipoFilatoSection[] = [...baseSections, ...extraSections];

      const budgetActuals = Object.entries(budgetConstraints).map(([consegna, values]: [string, any]) => ({
        consegna,
        value: Number(values?.value ?? 0) || 0,
        items: Number(values?.items ?? 0) || 0,
      }));

      const upperBounds = Object.fromEntries(
        Object.entries(budgetConstraints).map(([consegna, values]: [string, any]) => {
          const hasValue = values?.value !== undefined && values?.value !== null && values?.value !== '';
          const hasItems = values?.items !== undefined && values?.items !== null && values?.items !== '';
          return [
            consegna,
            {
              value: hasValue ? Number(values.value) || 0 : undefined,
              items: hasItems ? Number(values.items) || 0 : undefined,
              active: hasValue || hasItems,
            },
          ];
        }),
      );

      const shopData: ShopMerchandisingPlan = {
        shopId,
        shopLabel: getShopDisplayName(shopId, shopMapping),
        budget: {
          actuals: budgetActuals,
          upperBounds,
        },
        tipoFilatoSections,
      };

      return [shopId, shopData];
    }),
  );

  const areaConstraints = contextResponse?.area?.constraints;
  if (areaConstraints && typeof areaConstraints === 'object') {
    const allSections = Object.values(byShop).flatMap((shop) => shop.tipoFilatoSections);
    const groupedRows = allSections.reduce<Record<string, SkuConstraintRow[]>>((acc, section) => {
      if (!acc[section.tipoFilato]) {
        acc[section.tipoFilato] = [];
      }

      const rowsBySku = new Map(acc[section.tipoFilato].map((row) => [row.sku, row]));
      section.skuRows.forEach((row) => {
        const existing = rowsBySku.get(row.sku);
        if (!existing) {
          rowsBySku.set(row.sku, { ...row, weborderTags: row.weborderTags ? [...row.weborderTags] : [] });
          return;
        }

        rowsBySku.set(row.sku, {
          ...existing,
          description: existing.description || row.description,
          modelCode: existing.modelCode || row.modelCode,
          priceWhs: Number(existing.priceWhs ?? row.priceWhs ?? 0) || 0,
          priceRtl: Number(existing.priceRtl ?? row.priceRtl ?? 0) || 0,
          forecast: (Number(existing.forecast ?? 0) || 0) + (Number(row.forecast ?? 0) || 0),
          imagePath: existing.imagePath || row.imagePath,
          taglieLocked: Boolean(existing.taglieLocked || row.taglieLocked),
          taglieLockSource: existing.taglieLockSource || row.taglieLockSource,
          weborderTags: Array.from(new Set([...(existing.weborderTags || []), ...(row.weborderTags || [])])),
          minValue: (Number(existing.minValue) || 0) + (Number(row.minValue) || 0),
          lbQuantity: (Number(existing.lbQuantity) || 0) + (Number(row.lbQuantity) || 0),
          lbQuantityPerShop: (Number(existing.lbQuantityPerShop) || 0) + (Number(row.lbQuantityPerShop) || 0),
          ubQuantity: (Number(existing.ubQuantity) || 0) + (Number(row.ubQuantity) || 0),
          ubQuantityPerShop: (Number(existing.ubQuantityPerShop) || 0) + (Number(row.ubQuantityPerShop) || 0),
        });
      });

      acc[section.tipoFilato] = Array.from(rowsBySku.values());
      return acc;
    }, {});

    const areaMpConstraints = areaConstraints.mp && typeof areaConstraints.mp === 'object' ? areaConstraints.mp : {};
    const areaBudgetConstraints = areaConstraints.budget && typeof areaConstraints.budget === 'object' ? areaConstraints.budget : {};

    const areaSections: TipoFilatoSection[] = [
      ...Object.entries(areaMpConstraints).map(([tipoFilato, values]: [string, any]) => ({
        tipoFilato,
        lowBoundaryValue: 0,
        editableMinValue: 0,
        skuRows: groupedRows[tipoFilato] || [],
        constraints: {
          economicValue: { actual: 0, lowerBound: undefined, upperBound: values?.value ?? undefined, active: values?.value !== undefined && values?.value !== null && values?.value !== '' },
          items: { actual: 0, lowerBound: undefined, upperBound: values?.items ?? undefined, active: values?.items !== undefined && values?.items !== null && values?.items !== '' },
          uniqueModels: { actual: 0, lowerBound: undefined, upperBound: values?.models_unique ?? undefined, active: values?.models_unique !== undefined && values?.models_unique !== null && values?.models_unique !== '' },
          skuCount: { actual: 0, lowerBound: undefined, upperBound: values?.sku_count ?? undefined, active: values?.sku_count !== undefined && values?.sku_count !== null && values?.sku_count !== '' },
          avgPrice: { actual: 0, lowerBound: undefined, upperBound: values?.avg_price ?? undefined, active: values?.avg_price !== undefined && values?.avg_price !== null && values?.avg_price !== '' },
        },
      })),
      ...Object.entries(groupedRows)
        .filter(([tipoFilato]) => !(tipoFilato in areaMpConstraints))
        .map(([tipoFilato, skuRows]) => ({
          tipoFilato,
          lowBoundaryValue: 0,
          editableMinValue: 0,
          skuRows,
          constraints: {
            economicValue: { actual: 0, lowerBound: undefined, upperBound: undefined, active: false },
            items: { actual: 0, lowerBound: undefined, upperBound: undefined, active: false },
            uniqueModels: { actual: 0, lowerBound: undefined, upperBound: undefined, active: false },
            skuCount: { actual: 0, lowerBound: undefined, upperBound: undefined, active: false },
            avgPrice: { actual: 0, lowerBound: undefined, upperBound: undefined, active: false },
          },
        })),
    ];

    const areaBudgetActuals = Object.entries(areaBudgetConstraints).map(([consegna, values]: [string, any]) => ({
      consegna,
      value: Number(values?.value ?? 0) || 0,
      items: Number(values?.items ?? 0) || 0,
    }));

    const areaUpperBounds = Object.fromEntries(
      Object.entries(areaBudgetConstraints).map(([consegna, values]: [string, any]) => [
        consegna,
        {
          value: values?.value ?? undefined,
          items: values?.items ?? undefined,
          active: values?.value !== undefined || values?.items !== undefined,
        },
      ]),
    );

    byShop[AREA_TAB_ID] = {
      shopId: AREA_TAB_ID,
      shopLabel: 'AREA ★',
      budget: { actuals: areaBudgetActuals, upperBounds: areaUpperBounds },
      tipoFilatoSections: areaSections,
    };
    shops.unshift({ id: AREA_TAB_ID, label: 'AREA ★' });
  }

  return {
    configId: fallback.configId,
    shops,
    defaultShopId: shops[0].id,
    byShop,
  };
}

function buildCloudinaryImageFromPath(fullPath: string | null | undefined, width: number, height: number): string | null {
  if (!fullPath) return null;
  if (/^https?:\/\//i.test(fullPath)) return fullPath;
  return `https://media.brunellocucinelli.com/image/upload/c_fill,w_${width},h_${height},g_center/${fullPath}`;
}

function getEconomicConstraintSortValue(section: TipoFilatoSection): number {
  const economicValueConstraint = section.constraints.economicValue;
  const upperBound = Number(economicValueConstraint.upperBound);
  const lowerBound = Number(economicValueConstraint.lowerBound);

  if (Number.isFinite(upperBound) && upperBound > 0) return upperBound;
  if (Number.isFinite(lowerBound) && lowerBound > 0) return lowerBound;
  return 0;
}

function getConfiguredMinAllowedForRow(row: SkuConstraintRow, sectionTipoFilato: string, config: any): number {
  const constraints = config?.constraints && typeof config.constraints === 'object' ? config.constraints : {};
  const fallback = Number(constraints.min_allowed_value ?? 0) || 0;
  const perTipoFilato = constraints.min_allowed_value_per_tipo_filato && typeof constraints.min_allowed_value_per_tipo_filato === 'object'
    ? constraints.min_allowed_value_per_tipo_filato
    : {};
  const candidates = [row.tipoFilatoDs, row.tipoFilatoMp, sectionTipoFilato].filter((value): value is string => Boolean(value));
  const matched = candidates.find((tipoFilato) => hasDefinedBound(perTipoFilato[tipoFilato]));
  return Math.max(0, Number(matched ? perTipoFilato[matched] : fallback) || 0);
}

function getEffectiveSkuQuantity(row: SkuConstraintRow, sectionTipoFilato: string, config: any): number {
  const configuredValue = Number(row.minValue) || 0;
  if (configuredValue <= 0) return 0;
  const effectiveUb = Number(row.ubQuantityPerShop ?? row.ubQuantity);
  if (Number.isFinite(effectiveUb) && effectiveUb === configuredValue) {
    return configuredValue;
  }
  return Math.max(configuredValue, getConfiguredMinAllowedForRow(row, sectionTipoFilato, config));
}

function computeSectionActuals(section: TipoFilatoSection, config?: any) {
  const items = section.skuRows.reduce((sum, row) => sum + getEffectiveSkuQuantity(row, section.tipoFilato, config), 0);
  const economicValue = section.skuRows.reduce(
    (sum, row) => sum + ((Number(row.priceWhs) || 0) * getEffectiveSkuQuantity(row, section.tipoFilato, config)),
    0,
  );
  const uniqueModels = new Set(
    section.skuRows
      .filter((row) => getEffectiveSkuQuantity(row, section.tipoFilato, config) > 0)
      .map((row) => (row.modelCode || row.description || '').trim())
      .filter((value) => value.length > 0),
  ).size;
  const skuCount = section.skuRows.filter(
    (row) => getEffectiveSkuQuantity(row, section.tipoFilato, config) > 0,
  ).length;
  const avgPrice = items > 0 ? economicValue / items : 0;

  return {
    economicValue,
    items,
    uniqueModels,
    skuCount,
    avgPrice,
  };
}

function MiniBar({ label, actual, bound, tone = 'rose' }: { label: string; actual: number; bound?: number; tone?: 'rose' | 'blue' | 'sand' }) {
  const hasBound = bound !== undefined && bound !== null;
  const max = Math.max(actual, bound ?? 0, 1);
  const actualPct = (actual / max) * 100;
  const boundPct = ((bound ?? 0) / max) * 100;
  const tones = {
    rose: { actual: '#9F6460', bound: '#e7cfcc', text: '#7d4f4b' },
    blue: { actual: '#5f7c96', bound: '#d8e3ec', text: '#40586d' },
    sand: { actual: '#a98b63', bound: '#efe4d2', text: '#7d6647' },
  }[tone];

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[11px] font-medium text-gray-600">
        <span>{label}</span>
        <span>
          {actual}
          {' / '}
          {hasBound ? bound : 'NA'}
        </span>
      </div>
      <div className="relative h-2 overflow-hidden rounded-full bg-gray-100">
        {hasBound && (
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{ width: `${boundPct}%`, backgroundColor: tones.bound }}
          />
        )}
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ width: `${actualPct}%`, backgroundColor: tones.actual }}
        />
      </div>
    </div>
  );
}

type MerchandisingPlanEntryMode = 'new-optimization' | 'from-result';

type OptimizationResultContext = {
  runId: number | null;
  selectedShopId: string | null;
  selectedResultTab: number;
};

type SkuValidationReviewRow = {
  sku: string;
  valid: boolean;
  issues: string[];
};

type SkuValidationReview = {
  rows: SkuValidationReviewRow[];
  targetShopIds: string[];
};

type TipoFilatoTransferMetric = 'items' | 'economicValue';

type TipoFilatoTransferModalState = {
  sourceTipoFilato: string;
  targetTipoFilato: string;
  metric: TipoFilatoTransferMetric;
  amount: string;
  error: string | null;
};

type LinkableOrderRun = {
  run_id: number;
  dataset_id?: number;
  config_id?: number;
  status?: string;
  type?: string;
  started_at?: string;
  finished_at?: string;
  total_quantity?: number;
  total_value?: number;
  shops?: string[];
};

type ConsegnaPriorityState = 'prioritized' | 'neutral' | 'avoid';

const CONSEGNA_PRIORITY_STATES: ConsegnaPriorityState[] = ['neutral', 'prioritized', 'avoid'];
const CONSEGNA_PRIORITY_LABELS: Record<ConsegnaPriorityState, string> = {
  prioritized: 'Prioritizzato',
  neutral: 'Presente',
  avoid: 'Evita',
};
const CONSEGNA_PRIORITY_CLASSES: Record<ConsegnaPriorityState, string> = {
  prioritized: 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:border-emerald-700',
  neutral: 'border-gray-300 bg-white text-gray-600 hover:border-gray-700',
  avoid: 'border-rose-300 bg-rose-50 text-rose-800 hover:border-rose-700',
};

function normalizeConsegnaPriorityState(value: unknown): ConsegnaPriorityState {
  return value === 'prioritized' || value === 'avoid' ? value : 'neutral';
}

function nextConsegnaPriorityState(value: unknown): ConsegnaPriorityState {
  const current = normalizeConsegnaPriorityState(value);
  const idx = CONSEGNA_PRIORITY_STATES.indexOf(current);
  return CONSEGNA_PRIORITY_STATES[(idx + 1) % CONSEGNA_PRIORITY_STATES.length];
}

type LinkedOrderRunBaseline = {
  enabled?: boolean;
  run_id?: number;
  mode?: string;
  summary?: { quantity?: number; value?: number; quantity_orders_scaled?: number; value_orders_scaled?: number; quantity_mp_scaled?: number; value_mp_scaled?: number };
  mp_scaled?: boolean;
  budget_scaled?: boolean;
  mp_usage_by_shop?: Record<string, Record<string, { quantity?: number; value?: number; source_tipi_filato?: Record<string, number> }>>;
  by_shop_mp?: Record<string, Record<string, { quantity?: number; value?: number; source_tipi_filato?: Record<string, number> }>>;
};

function baselineToRunInfo(baseline: LinkedOrderRunBaseline): LinkableOrderRun {
  return {
    run_id: Number(baseline.run_id || 0),
    status: baseline.enabled ? 'Configurata' : 'Non attiva',
    total_quantity: Number(baseline.summary?.quantity_orders_scaled ?? baseline.summary?.quantity ?? 0),
    total_value: Number(baseline.summary?.value_orders_scaled ?? baseline.summary?.value ?? 0),
    shops: Object.keys(baseline.mp_usage_by_shop || baseline.by_shop_mp || {}),
  };
}

const AUTOSAVE_DELAY_MS = 4000;

export default function MerchandisingPlanPage({
  onOptimizationLaunched,
  onBackToOptimization,
  skipInitialLoad = false,
  entryMode = 'new-optimization',
  resultContext,
  initialCatalogMode,
  initialIncognito = false,
  resumeDraft = false,
}: {
  onOptimizationLaunched?: () => void;
  onBackToOptimization?: () => void;
  skipInitialLoad?: boolean;
  entryMode?: MerchandisingPlanEntryMode;
  resultContext?: OptimizationResultContext;
  initialCatalogMode?: CatalogMode;
  /** SPJ-1372: ottimizzazione in incognito scelta in fase di creazione. */
  initialIncognito?: boolean;
  /** SPJ-1366: riprende la configurazione in corso (pending-config-id) invece di crearne una nuova dalla run. */
  resumeDraft?: boolean;
} = {}) {
  const dispatch = useAppDispatch();
  const thread = useAppSelector((state) => state.thread);
  const appState = useAppSelector(selectThreadAppState);
  // Senza run riuscite (bozza di un progetto nuovo) non c'e' nulla da importare ne' a cui tornare.
  const projectHasRuns = hasSucceededRun(appState);
  const currentMessageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
  const appOptimGender = appState?.['optim-gender'];
  const [activeTab, setActiveTab] = useState<'input-data' | 'perimetro-budget' | 'vincoli'>(entryMode === 'from-result' ? 'perimetro-budget' : 'input-data');
  const [pageData, setPageData] = useState<MerchandisingPlanPageData>(EMPTY_PAGE_DATA);
  const [selectedShopId, setSelectedShopId] = useState<string>('');
  const [configDetails, setConfigDetails] = useState<any>(null);
  const [configDraft, setConfigDraft] = useState<any>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [pendingMutationCount, setPendingMutationCount] = useState(0);
  const [hasPendingConfigSave, setHasPendingConfigSave] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  // SPJ-890: salvataggio esplicito. Le modifiche restano locali e vengono
  // persistite tutte insieme (un solo salvataggio + un solo ricalcolo) al click su "Salva".
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSavingAll, setIsSavingAll] = useState(false);
  // SPJ-1366: autosalvataggio. `editVersion` cambia a ogni modifica, cosi' il salvataggio parte
  // qualche secondo dopo l'ULTIMA modifica e non dopo la prima.
  const [editVersion, setEditVersion] = useState(0);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const markUnsaved = useCallback(() => {
    setHasUnsavedChanges(true);
    setEditVersion((v) => v + 1);
  }, []);
  // SPJ-1372: rispecchia il flag locale di "modifiche non salvate" nello store, cosi' la
  // guardia di navigazione interna (conferma prima di lasciare l'editor MP) puo' leggerlo.
  // Il flag locale resta la fonte per la UI del bottone; questo effetto lo mantiene allineato
  // (true quando si modifica, false al salvataggio) e al mount parte da false.
  useEffect(() => {
    dispatch(setHasUnsavedMpChanges(hasUnsavedChanges));
  }, [dispatch, hasUnsavedChanges]);
  // Allo smontaggio dell'editor il flag va azzerato: fuori dalla MP non c'e' nulla da proteggere.
  useEffect(() => {
    return () => {
      dispatch(setHasUnsavedMpChanges(false));
    };
  }, [dispatch]);
  const pendingConfigDirtyRef = useRef(false);
  const pendingMpShopsRef = useRef<Set<string>>(new Set());
  const pendingBudgetShopsRef = useRef<Set<string>>(new Set());
  const pendingSkuMinRef = useRef<Map<string, Map<string, number>>>(new Map());
  const [showSkuTextArea, setShowSkuTextArea] = useState(false);
  const [pastedSkuText, setPastedSkuText] = useState('');
  const [isUploadingSkuList, setIsUploadingSkuList] = useState(false);
  const [isClearingSkuBounds, setIsClearingSkuBounds] = useState(false);
  const [skuListMessage, setSkuListMessage] = useState<string | null>(null);
  const [skuListError, setSkuListError] = useState<string | null>(null);
  const [skuValidationReview, setSkuValidationReview] = useState<SkuValidationReview | null>(null);
  const [expandedTfRows, setExpandedTfRows] = useState<Record<string, boolean>>({});
  const [tipoFilatoMinDrafts, setTipoFilatoMinDrafts] = useState<Record<string, string>>({});
  const [skuMinDrafts, setSkuMinDrafts] = useState<Record<string, string>>({});
  const [selectedSkuKeys, setSelectedSkuKeys] = useState<Record<string, boolean>>({});
  const [activeConstraintEditor, setActiveConstraintEditor] = useState<string | null>(null);
  const [constraintDraftValue, setConstraintDraftValue] = useState<string>('');
  const [transferModalState, setTransferModalState] = useState<TipoFilatoTransferModalState | null>(null);
  const [isTransferSaving, setIsTransferSaving] = useState(false);
  const [defaultSkuListMinValue, setDefaultSkuListMinValue] = useState<number>(0);
  const [skuTargetShopIds, setSkuTargetShopIds] = useState<string[]>([]);
  const [merchandisingSearch, setMerchandisingSearch] = useState('');
  const [inputDataCompleted, setInputDataCompleted] = useState(false);
  const [isInputDataReady, setIsInputDataReady] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationProgress, setOptimizationProgress] = useState(0);
  const [optimizationError, setOptimizationError] = useState<string | null>(null);
  const [isPreparingFromResult, setIsPreparingFromResult] = useState(false);
  const [isImportingOptimization, setIsImportingOptimization] = useState(false);
  const [showImportOptimizationRunModal, setShowImportOptimizationRunModal] = useState(false);
  const [manualImportOptimizationRunIdDraft, setManualImportOptimizationRunIdDraft] = useState('');
  const [importOptimizationLockTaglieDraft, setImportOptimizationLockTaglieDraft] = useState(false);
  const [isImportingMinimumValues, setIsImportingMinimumValues] = useState(false);
  const [isLinkingOrderRun, setIsLinkingOrderRun] = useState(false);
  const [showLinkedOrderRunModal, setShowLinkedOrderRunModal] = useState(false);
  const [linkableOrderRuns, setLinkableOrderRuns] = useState<LinkableOrderRun[]>([]);
  const [selectedLinkedOrderRunId, setSelectedLinkedOrderRunId] = useState<number | null>(null);
  const [manualLinkedOrderRunIdDraft, setManualLinkedOrderRunIdDraft] = useState('');
  const [scaleLinkedRunMp, setScaleLinkedRunMp] = useState(true);
  const [linkedOrderRunInfo, setLinkedOrderRunInfo] = useState<LinkableOrderRun | null>(null);
  const [showLinkedOrderRunInfo, setShowLinkedOrderRunInfo] = useState(false);
  const [isLoadingLinkableOrderRuns, setIsLoadingLinkableOrderRuns] = useState(false);
  const [linkedOrderRunError, setLinkedOrderRunError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [preparedFromResultThreadId, setPreparedFromResultThreadId] = useState<string | null>(null);
  const [optimizationScopeMode, setOptimizationScopeMode] = useState<'master' | 'lista'>('master');
  const [shopMappingLabels, setShopMappingLabels] = useState<Record<string, string>>({});
  // SPJ-929: anteprima immagine + prezzo whs al passaggio del mouse su una SKU
  const [skuPreview, setSkuPreview] = useState<{ sku: string; priceWhs?: number; priceRtl?: number; imagePath?: string; x: number; y: number } | null>(null);

  const configStateRef = useRef<any>(null);
  const pageDataRef = useRef<MerchandisingPlanPageData>(EMPTY_PAGE_DATA);
  const mutationQueueRef = useRef<Promise<void>>(Promise.resolve());
  const contextRequestIdRef = useRef(0);
  const pendingConfigSaveTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const pendingConfigSaveWaitersRef = useRef<Array<{ resolve: (value: any) => void; reject: (reason?: unknown) => void }>>([]);
  const isFlushingPendingConfigSaveRef = useRef(false);

  useEffect(() => {
    if (configDraft && typeof configDraft === 'object') {
      configStateRef.current = configDraft;
      return;
    }
    if (configDetails && typeof configDetails === 'object') {
      configStateRef.current = configDetails;
    }
  }, [configDetails, configDraft]);

  useEffect(() => {
    pageDataRef.current = pageData;
  }, [pageData]);

  const commitConfigState = useCallback((nextConfig: any) => {
    if (!nextConfig || typeof nextConfig !== 'object') return;
    configStateRef.current = nextConfig;
    setConfigDetails(nextConfig);
    setConfigDraft(nextConfig);
  }, []);

  const getLatestConfigSnapshot = useCallback(() => {
    const source = configStateRef.current;
    if (!source || typeof source !== 'object') {
      return null;
    }
    return JSON.parse(JSON.stringify(source));
  }, []);

  const applyPageData = useCallback((nextPageData: MerchandisingPlanPageData) => {
    pageDataRef.current = nextPageData;
    setPageData(nextPageData);
  }, []);

  const enqueueMutation = useCallback(<T,>(job: () => Promise<T>): Promise<T> => {
    setPendingMutationCount((count) => count + 1);
    const wrappedJob = async () => {
      try {
        return await job();
      } finally {
        setPendingMutationCount((count) => Math.max(0, count - 1));
      }
    };
    const run = mutationQueueRef.current.then(wrappedJob, wrappedJob);
    mutationQueueRef.current = run.then(() => undefined, () => undefined);
    return run;
  }, []);

  const resolvePendingConfigSaveWaiters = useCallback((result?: any, error?: unknown) => {
    const waiters = pendingConfigSaveWaitersRef.current.splice(0);
    waiters.forEach(({ resolve, reject }) => {
      if (error !== undefined) {
        reject(error);
      } else {
        resolve(result);
      }
    });
  }, []);

  useEffect(() => () => {
    if (pendingConfigSaveTimerRef.current !== null) {
      window.clearTimeout(pendingConfigSaveTimerRef.current);
      pendingConfigSaveTimerRef.current = null;
    }
    setHasPendingConfigSave(false);
    resolvePendingConfigSaveWaiters(undefined, new Error('Config save cancelled'));
  }, [resolvePendingConfigSaveWaiters]);

  const inputDataFormId = 'merchandising-plan-input-data-form';
  const currentShop = useMemo(() => pageData.byShop[selectedShopId], [pageData, selectedShopId]);
  const isMutationQueueBusy = isSavingConfig || pendingMutationCount > 0 || hasPendingConfigSave;
  const availableTipoFilati = useMemo(
    () => Array.from(
      new Set(
        Object.values(pageData.byShop)
          .flatMap((shop) => shop.tipoFilatoSections.map((section) => section.tipoFilato))
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b)),
    [pageData.byShop],
  );
  const isAreaTabSelected = selectedShopId === AREA_TAB_ID;
  const resolvedImportRunId = useMemo(
    () => parseOptionalNumericId(resultContext?.runId) ?? parseOptionalNumericId(appState?.['optim-optimization-id']),
    [appState, resultContext?.runId],
  );
  const resolvedImportConfigId = useMemo(
    // Prefer the working/pending config (the one "Esegui" will launch) over the
    // source run's config: the lock must write into the config that gets run,
    // otherwise the launched run executes an un-locked fork.
    () => parseOptionalNumericId(appState?.['pending-config-id'])
      ?? parseOptionalNumericId(pageData.configId)
      ?? parseOptionalNumericId(configDetails?.id)
      ?? parseOptionalNumericId(appState?.['optim-config']),
    [appState, configDetails?.id, pageData.configId],
  );
  const linkedOrderRunId = useMemo(
    () => parseOptionalNumericId(configDraft?.preparation?.linked_order_run_id),
    [configDraft],
  );

  const taglieLockedSkuKeys = useMemo(() => {
    const config = configDraft && typeof configDraft === 'object' ? configDraft : configDetails;
    const fixedByShop = config?.constraints?.fixed_taglie_per_shop;
    if (!fixedByShop || typeof fixedByShop !== 'object') return new Set<string>();
    const keys = new Set<string>();
    Object.entries(fixedByShop as Record<string, any>).forEach(([shopId, skuMap]) => {
      if (!skuMap || typeof skuMap !== 'object') return;
      Object.entries(skuMap as Record<string, any>).forEach(([sku, fixed]) => {
        const taglie = fixed?.taglie && typeof fixed.taglie === 'object' ? fixed.taglie as Record<string, unknown> : {};
        const taglieTotal = Object.entries(taglie).reduce((sum, [size, qty]) => {
          if (size === 'cod_tipo_taglia' || size === 'codTipoTaglia') return sum;
          const parsed = Number(qty ?? 0);
          return sum + (Number.isFinite(parsed) && parsed > 0 ? parsed : 0);
        }, 0);
        if (taglieTotal > 0) {
          keys.add(`${shopId}:${sku}`);
        }
      });
    });
    return keys;
  }, [configDetails, configDraft]);

  const isSkuTaglieLocked = useCallback((shopId: string, sku: string) => taglieLockedSkuKeys.has(`${shopId}:${sku}`), [taglieLockedSkuKeys]);

  const buildCompatibleRunParams = useCallback((options?: { sameDatasetOnly?: boolean }) => {
    const params: Record<string, unknown> = {};
    if (resolvedImportRunId !== null) params['optim-run-id'] = resolvedImportRunId;
    if (resolvedImportConfigId !== null) params['optim-config-id'] = resolvedImportConfigId;
    const optimDataset = appState?.['optim-dataset'] && typeof appState?.['optim-dataset'] === 'object'
      ? appState?.['optim-dataset'] as Record<string, unknown>
      : {};
    const datasetId = parseOptionalNumericId(appState?.['optim-dataset-id'])
      ?? parseOptionalNumericId(appState?.['dataset-id'])
      ?? parseOptionalNumericId(optimDataset.id);
    if (datasetId !== null) params['optim-dataset-id'] = datasetId;
    if (options?.sameDatasetOnly) params['same_dataset_only'] = true;
    const codStagione = optimDataset.cod_stagione ?? appState?.['optim-season'];
    const gender = optimDataset.gender ?? appState?.['optim-gender'];
    const consegna = optimDataset.consegna ?? appState?.['optim-consegna'];
    if (codStagione !== undefined && codStagione !== null && String(codStagione).trim() !== '') params['optim-cod-stagione'] = codStagione;
    if (gender !== undefined && gender !== null && String(gender).trim() !== '') params['optim-gender'] = gender;
    if (consegna !== undefined && consegna !== null && String(consegna).trim() !== '') params['optim-consegna'] = consegna;
    return params;
  }, [appState, resolvedImportConfigId, resolvedImportRunId]);

  useEffect(() => {
    if (entryMode === 'from-result') {
      setActiveTab((prev) => (prev === 'input-data' ? 'perimetro-budget' : prev));
      setInputDataCompleted(true);
      return;
    }
    setPreparedFromResultThreadId(null);
  }, [entryMode]);

  const budgetComputedActuals = useMemo(() => {
    if (!currentShop) {
      return {
        totalValue: 0,
        totalItems: 0,
        byConsegnaValue: {} as Record<string, number>,
        byConsegnaItems: {} as Record<string, number>,
      };
    }

    const sectionActuals = currentShop.tipoFilatoSections.map((section) => computeSectionActuals(section, configDraft));
    const totalEconomicValue = sectionActuals.reduce((sum, actuals) => sum + actuals.economicValue, 0);
    const totalItems = sectionActuals.reduce((sum, actuals) => sum + actuals.items, 0);

    return {
      totalValue: totalEconomicValue,
      totalItems,
      byConsegnaValue: distributeByBounds(
        totalEconomicValue,
        currentShop.budget.actuals,
        (consegna) => currentShop.budget.upperBounds[consegna]?.value,
      ),
      byConsegnaItems: distributeByBounds(
        totalItems,
        currentShop.budget.actuals,
        (consegna) => currentShop.budget.upperBounds[consegna]?.items,
      ),
    };
  }, [configDraft, currentShop]);

  const budgetTotals = useMemo(() => {
    if (!currentShop) {
      return {
        valueBound: undefined as number | undefined,
        itemsBound: undefined as number | undefined,
      };
    }

    const boundsList = currentShop.budget.actuals.map((entry) => currentShop.budget.upperBounds[entry.consegna]);
    const hasAnyValueBound = boundsList.some((bounds) => bounds?.value !== undefined);
    const hasAnyItemsBound = boundsList.some((bounds) => bounds?.items !== undefined);

    return {
      valueBound: hasAnyValueBound
        ? boundsList.reduce((sum, bounds) => sum + (Number(bounds?.value) || 0), 0)
        : undefined,
      itemsBound: hasAnyItemsBound
        ? boundsList.reduce((sum, bounds) => sum + (Number(bounds?.items) || 0), 0)
        : undefined,
    };
  }, [currentShop]);

  const filteredTipoFilatoSections = useMemo(() => {
    if (!currentShop) return [] as TipoFilatoSection[];

    const query = merchandisingSearch.trim().toLowerCase();
    const matchingSections = !query
      ? currentShop.tipoFilatoSections
      : currentShop.tipoFilatoSections.filter((section) => {
          if (section.tipoFilato.toLowerCase().includes(query)) return true;
          return section.skuRows.some((row) => row.sku.toLowerCase().includes(query));
        });

    return [...matchingSections].sort((a, b) => (
      getEconomicConstraintSortValue(b) - getEconomicConstraintSortValue(a)
    ));
  }, [currentShop, merchandisingSearch]);

  const getTipoFilatoTransferTargets = useCallback((sourceTipoFilato: string, metric: TipoFilatoTransferMetric) => {
    if (!currentShop) return [] as TipoFilatoSection[];
    return currentShop.tipoFilatoSections
      .filter((section) => section.tipoFilato !== sourceTipoFilato && hasTipoFilatoMetricConstraint(section, metric))
      .sort((a, b) => a.tipoFilato.localeCompare(b.tipoFilato, 'it', { numeric: true }));
  }, [currentShop]);

  const openTipoFilatoTransferModal = useCallback((sourceTipoFilato: string) => {
    if (!currentShop || isAreaTabSelected) return;
    const source = currentShop.tipoFilatoSections.find((section) => section.tipoFilato === sourceTipoFilato);
    if (!source) return;
    const candidateMetrics: TipoFilatoTransferMetric[] = ['items', 'economicValue'];
    const metric = candidateMetrics.find((candidate) => (
      hasTipoFilatoMetricConstraint(source, candidate) && getTipoFilatoTransferTargets(sourceTipoFilato, candidate).length > 0
    ));
    if (!metric) return;
    const firstTarget = getTipoFilatoTransferTargets(sourceTipoFilato, metric)[0];
    setTransferModalState({
      sourceTipoFilato,
      targetTipoFilato: firstTarget?.tipoFilato || '',
      metric,
      amount: '',
      error: null,
    });
  }, [currentShop, getTipoFilatoTransferTargets, isAreaTabSelected]);

  const transferTargetOptions = useMemo(() => {
    if (!transferModalState) return [] as TipoFilatoSection[];
    return getTipoFilatoTransferTargets(transferModalState.sourceTipoFilato, transferModalState.metric);
  }, [getTipoFilatoTransferTargets, transferModalState]);

  const transferPreview = useMemo(() => {
    if (!currentShop || !transferModalState) return null;
    const source = currentShop.tipoFilatoSections.find((section) => section.tipoFilato === transferModalState.sourceTipoFilato);
    const target = currentShop.tipoFilatoSections.find((section) => section.tipoFilato === transferModalState.targetTipoFilato);
    if (!source || !target) return null;
    const metric = transferModalState.metric;
    const amount = Number(transferModalState.amount || 0);
    const sourceCurrent = Number(source.constraints[metric].upperBound ?? 0);
    const targetCurrent = Number(target.constraints[metric].upperBound ?? 0);
    return {
      sourceCurrent,
      sourceNext: sourceCurrent - amount,
      targetCurrent,
      targetNext: targetCurrent + amount,
    };
  }, [currentShop, transferModalState]);

  const transferCanSubmit = useMemo(() => {
    if (!transferModalState || !transferPreview) return false;
    const amount = Number(transferModalState.amount);
    return Number.isFinite(amount)
      && amount > 0
      && (transferModalState.metric !== 'items' || Number.isInteger(amount))
      && Boolean(transferModalState.targetTipoFilato)
      && transferPreview.sourceNext >= 0
      && !isTransferSaving;
  }, [isTransferSaving, transferModalState, transferPreview]);

  const budgetValueBroken = useMemo(
    () => hasDefinedBound(budgetTotals.valueBound)
      && Number(budgetComputedActuals.totalValue.toFixed(2)) > Number(budgetTotals.valueBound ?? 0),
    [budgetComputedActuals.totalValue, budgetTotals.valueBound],
  );

  const budgetItemsBroken = useMemo(
    () => hasDefinedBound(budgetTotals.itemsBound)
      && Number(budgetComputedActuals.totalItems.toFixed(2)) > Number(budgetTotals.itemsBound ?? 0),
    [budgetComputedActuals.totalItems, budgetTotals.itemsBound],
  );

  const launchValidationMessages = useMemo(() => {
    const activeConstraints = configDraft?.constraints?.active_constraints || {};
    const messages: string[] = [];
    const shopsToValidate = pageData.shops
      .filter((shop) => shop.id !== AREA_TAB_ID && pageData.byShop[shop.id])
      .map((shop) => pageData.byShop[shop.id]);

    if (shopsToValidate.length === 0) return messages;

    const formatShopViolations = (violations: Array<{ shopLabel: string; values?: string[] }>) => (
      violations
        .map((violation) => {
          if (!violation.values || violation.values.length === 0) return violation.shopLabel;
          return `${violation.shopLabel}: ${violation.values.join(', ')}`;
        })
        .join(' | ')
    );

    const economicViolations: Array<{ shopLabel: string; values: string[] }> = [];
    const itemsViolations: Array<{ shopLabel: string; values: string[] }> = [];
    const uniqueModelsViolations: Array<{ shopLabel: string; values: string[] }> = [];
    const skuCountViolations: Array<{ shopLabel: string; values: string[] }> = [];
    const budgetValueViolations: Array<{ shopLabel: string }> = [];
    const budgetItemsViolations: Array<{ shopLabel: string }> = [];

    shopsToValidate.forEach((shop) => {
      const violatedEconomicTfs = shop.tipoFilatoSections
        .filter((section) => {
          const actuals = computeSectionActuals(section, configDraft);
          const rawBound = section.constraints.economicValue.upperBound;
          return hasDefinedBound(rawBound) && actuals.economicValue > Number(rawBound ?? 0);
        })
        .map((section) => section.tipoFilato);

      const violatedItemsTfs = shop.tipoFilatoSections
        .filter((section) => {
          const actuals = computeSectionActuals(section, configDraft);
          const rawBound = section.constraints.items.upperBound;
          return hasDefinedBound(rawBound) && actuals.items > Number(rawBound ?? 0);
        })
        .map((section) => section.tipoFilato);

      const violatedUniqueModelsTfs = shop.tipoFilatoSections
        .filter((section) => {
          const actuals = computeSectionActuals(section, configDraft);
          const rawBound = section.constraints.uniqueModels.upperBound;
          return hasDefinedBound(rawBound) && actuals.uniqueModels > Number(rawBound ?? 0);
        })
        .map((section) => section.tipoFilato);

      const violatedSkuCountTfs = shop.tipoFilatoSections
        .filter((section) => {
          const actuals = computeSectionActuals(section, configDraft);
          const rawBound = section.constraints.skuCount.upperBound;
          return hasDefinedBound(rawBound) && actuals.skuCount > Number(rawBound ?? 0);
        })
        .map((section) => section.tipoFilato);

      if (violatedEconomicTfs.length > 0) {
        economicViolations.push({ shopLabel: shop.shopLabel, values: violatedEconomicTfs });
      }
      if (violatedItemsTfs.length > 0) {
        itemsViolations.push({ shopLabel: shop.shopLabel, values: violatedItemsTfs });
      }
      if (violatedUniqueModelsTfs.length > 0) {
        uniqueModelsViolations.push({ shopLabel: shop.shopLabel, values: violatedUniqueModelsTfs });
      }
      if (violatedSkuCountTfs.length > 0) {
        skuCountViolations.push({ shopLabel: shop.shopLabel, values: violatedSkuCountTfs });
      }
      const sectionActuals = shop.tipoFilatoSections.map((section) => computeSectionActuals(section, configDraft));
      const totalEconomicValue = sectionActuals.reduce((sum, actuals) => sum + actuals.economicValue, 0);
      const totalItems = sectionActuals.reduce((sum, actuals) => sum + actuals.items, 0);
      const boundsList = shop.budget.actuals.map((entry) => shop.budget.upperBounds[entry.consegna]);
      const hasAnyValueBound = boundsList.some((bounds) => hasDefinedBound(bounds?.value));
      const hasAnyItemsBound = boundsList.some((bounds) => hasDefinedBound(bounds?.items));
      const valueBound = hasAnyValueBound
        ? boundsList.reduce((sum, bounds) => sum + (Number(bounds?.value) || 0), 0)
        : undefined;
      const itemsBound = hasAnyItemsBound
        ? boundsList.reduce((sum, bounds) => sum + (Number(bounds?.items) || 0), 0)
        : undefined;

      if (hasDefinedBound(valueBound) && Number(totalEconomicValue.toFixed(2)) > Number(valueBound ?? 0)) {
        budgetValueViolations.push({ shopLabel: shop.shopLabel });
      }
      if (hasDefinedBound(itemsBound) && Number(totalItems.toFixed(2)) > Number(itemsBound ?? 0)) {
        budgetItemsViolations.push({ shopLabel: shop.shopLabel });
      }
    });

    const minAllowedHint = 'Nota: i controlli usano la quantità effettiva dell’ottimizzatore: se uno SKU ha valore > 0 viene alzato almeno a min_allowed_value o min_allowed_value_per_tipo_filato.';

    if (activeConstraints.group_capacity && economicViolations.length > 0) {
      messages.push(`Disabilita il vincolo group_capacity o aggiusta il Valore Economico dei tipo filato evidenziati: ${formatShopViolations(economicViolations)}. Verifica anche min_allowed_value / min_allowed_value_per_tipo_filato.`);
    }

    if ((activeConstraints.group_quantity || activeConstraints.group_quaantity) && itemsViolations.length > 0) {
      messages.push(`Disabilita il vincolo group_quantity o aggiusta gli Items dei tipo filato evidenziati: ${formatShopViolations(itemsViolations)}. Verifica anche min_allowed_value / min_allowed_value_per_tipo_filato.`);
    }

    if (activeConstraints.budget && budgetValueViolations.length > 0) {
      messages.push(`Disabilita il vincolo budget o aggiusta il Valore Economico del Totale consegne evidenziato nei negozi: ${formatShopViolations(budgetValueViolations)}. Verifica anche min_allowed_value / min_allowed_value_per_tipo_filato.`);
    }

    if (activeConstraints.budget_items && budgetItemsViolations.length > 0) {
      messages.push(`Disabilita il vincolo budget_items o aggiusta gli Items del Totale consegne evidenziato nei negozi: ${formatShopViolations(budgetItemsViolations)}. Verifica anche min_allowed_value / min_allowed_value_per_tipo_filato.`);
    }

    if (activeConstraints.group_models_unique && uniqueModelsViolations.length > 0) {
      messages.push(`Disabilita il vincolo group_models_unique o aggiusta i Modelli Unici dei tipo filato evidenziati: ${formatShopViolations(uniqueModelsViolations)}. ${minAllowedHint}`);
    }
    if (activeConstraints.group_sku_count && skuCountViolations.length > 0) {
      messages.push(`Disabilita il vincolo group_sku_count o aggiusta gli SKU Unici dei tipo filato evidenziati: ${formatShopViolations(skuCountViolations)}. ${minAllowedHint}`);
    }

    return messages;
  }, [configDraft, pageData]);

  const canAccessEditorTabs = entryMode === 'from-result'
    ? !isPreparingFromResult && Object.keys(pageData.byShop).length > 0
    : inputDataCompleted;
  const isLaunchDisabled = launchValidationMessages.length > 0
    || !canAccessEditorTabs
    || isOptimizing
    || isImportingOptimization
    || isLinkingOrderRun
    || isMutationQueueBusy
    || hasUnsavedChanges   // SPJ-890: nessun lancio senza aver prima salvato+ricalcolato
    || isSavingAll;
  const launchButtonTitle = !canAccessEditorTabs
    ? entryMode === 'from-result'
      ? 'Attendi il caricamento della configurazione derivata dall\'ottimizzazione'
      : 'Completa prima Input Data e configura l\'ottimizzazione'
    : isSavingAll
      ? 'Salvataggio e ricalcolo in corso…'
      : hasUnsavedChanges
        ? 'Salva le modifiche (ricalcolo metriche) prima di eseguire l\'ottimizzazione'
        : launchValidationMessages.length > 0
          ? launchValidationMessages.join('\n')
          : isMutationQueueBusy
            ? 'Attendi il completamento del salvataggio modifiche'
            : isImportingOptimization
              ? 'Importazione in corso'
              : isLinkingOrderRun
                ? 'Collegamento run ordini in corso'
                : isOptimizing
                  ? 'Ottimizzazione in corso'
                  : 'Esegui ottimizzazione';

  useEffect(() => {
    setSelectedShopId((prev) => (pageData.byShop[prev] ? prev : pageData.defaultShopId));
    setSkuTargetShopIds((prev) => {
      const filtered = prev.filter((shopId) => pageData.byShop[shopId] && shopId !== AREA_TAB_ID);
      if (filtered.length > 0) {
        return filtered;
      }
      const firstRealShopId = pageData.shops.find((shop) => shop.id !== AREA_TAB_ID)?.id ?? '';
      return firstRealShopId ? [firstRealShopId] : [];
    });
  }, [pageData]);


  const loadShopMappingLabels = useCallback(async () => {
    const threadId = thread.threadId;
    const messageId = currentMessageId;
    const gender = String((configDraft as any)?.gender ?? (configDetails as any)?.gender ?? appOptimGender ?? '').trim();
    if (!threadId || !messageId || !gender) {
      return;
    }

    try {
      const result = await performAction({
        threadId,
        messageId,
        actionName: 'optim-get-shop-mapping',
        params: { 'optim-gender': gender },
      });
      if (result && typeof result === 'object' && !Array.isArray(result)) {
        setShopMappingLabels(result as Record<string, string>);
      }
    } catch (err) {
      console.error('Error loading shop mapping labels:', err);
    }
  }, [appOptimGender, configDetails, configDraft, currentMessageId, thread.threadId]);

  const fetchLinkedOrderRunBaseline = useCallback(async (linkedRunId: number, scaleMp = scaleLinkedRunMp): Promise<LinkedOrderRunBaseline | null> => {
    const threadId = thread.threadId;
    const messageId = currentMessageId;
    if (!threadId || !messageId) return null;
    try {
      const result = await performAction({
        threadId,
        messageId,
        actionName: 'optim-preview-linked-order-run',
        params: { 'optim-linked-order-run-id': linkedRunId, 'optim-scale-linked-run-mp': scaleMp },
      });
      return result && typeof result === 'object' ? result as LinkedOrderRunBaseline : null;
    } catch (err) {
      console.error('Error previewing linked order run:', err);
      return null;
    }
  }, [currentMessageId, scaleLinkedRunMp, thread.threadId]);

  // SPJ-1364: ordini dello SKU negli altri negozi, per il popup ordini fissato.
  const loadSkuOrdersByShop = useCallback(async (sku: string): Promise<SkuOrdersShopEntry[]> => {
    const threadId = thread.threadId;
    const messageId = currentMessageId;
    if (!threadId || !messageId) throw new Error('Thread non disponibile');
    const result = await performAction({
      threadId,
      messageId,
      actionName: 'optim-sku-orders-by-shop',
      params: buildSkuOrdersByShopParams(sku, appState),
    });
    return Array.isArray((result as any)?.shops) ? (result as any).shops as SkuOrdersShopEntry[] : [];
  }, [appState, currentMessageId, thread.threadId]);

  const loadConfigAndContext = useCallback(async (overrideConfigId?: number | null) => {
    const threadId = thread.threadId;
    const messageId = currentMessageId;
    if (!threadId || !messageId) return false;

    const requestId = ++contextRequestIdRef.current;
    setIsLoadingConfig(true);
    setIsLoadingContext(true);
    try {
      const configParams = overrideConfigId ? { 'optim-config-id': overrideConfigId } : {};
      const [configResult, contextResult] = await Promise.all([
        performAction({
          threadId,
          messageId,
          actionName: 'optim-get-config-details',
          params: configParams,
        }),
        performAction({
          threadId,
          messageId,
          actionName: 'optim-get-config-shop-sku-context',
          params: configParams,
        }),
      ]);

      if (requestId !== contextRequestIdRef.current) {
        return false;
      }

      commitConfigState(configResult);
      const gender = String((configResult as any)?.gender ?? appOptimGender ?? '').trim();
      let loadedShopMapping: Record<string, string> | null = null;
      if (threadId && messageId && gender) {
        try {
          const mappingResult = await performAction({
            threadId,
            messageId,
            actionName: 'optim-get-shop-mapping',
            params: { 'optim-gender': gender },
          });
          if (requestId === contextRequestIdRef.current && mappingResult && typeof mappingResult === 'object' && !Array.isArray(mappingResult)) {
            loadedShopMapping = mappingResult as Record<string, string>;
            setShopMappingLabels(loadedShopMapping);
          }
        } catch (mappingErr) {
          console.error('Error loading shop mapping labels:', mappingErr);
        }
      }
      const defaultUbQuantity = Number((configResult as any)?.constraints?.default_ub_quantity ?? 100000000000000000);
      setOptimizationScopeMode(defaultUbQuantity === 0 ? 'lista' : 'master');
      const configuredLinkedRunId = parseOptionalNumericId((configResult as any)?.preparation?.linked_order_run_id);
      const configuredScaleLinkedRunMp = (configResult as any)?.preparation?.scale_linked_run_mp !== false;
      setScaleLinkedRunMp(configuredScaleLinkedRunMp);
      const linkedBaseline = configuredLinkedRunId !== null ? await fetchLinkedOrderRunBaseline(configuredLinkedRunId, configuredScaleLinkedRunMp) : null;
      if (linkedBaseline?.enabled) {
        setLinkedOrderRunInfo(baselineToRunInfo(linkedBaseline));
      } else if (configuredLinkedRunId !== null) {
        setLinkedOrderRunInfo({ run_id: configuredLinkedRunId, status: 'Configurata' });
      } else {
        setLinkedOrderRunInfo(null);
        setShowLinkedOrderRunInfo(false);
      }
      const nextPageData = mapContextResponseToPageData(contextResult, {
        ...EMPTY_PAGE_DATA,
        configId: String((configResult as any)?.id ?? ''),
      });
      const persistedShopMapping = readPersistedMerchandisingShopMapping(threadId);
      const effectiveShopMapping = loadedShopMapping && Object.keys(loadedShopMapping).length > 0 ? loadedShopMapping : persistedShopMapping;
      const remappedPageData = applyShopLabels(nextPageData, effectiveShopMapping);
      const hasShopData = Object.keys(remappedPageData.byShop).length > 0;
      applyPageData(remappedPageData);
      if (remappedPageData.defaultShopId) {
        const preferredShopId = entryMode === 'from-result' && resultContext?.selectedShopId && remappedPageData.byShop[resultContext.selectedShopId]
          ? resultContext.selectedShopId
          : remappedPageData.defaultShopId;
        setSelectedShopId(preferredShopId);
        setSkuTargetShopIds((prev) => {
          const filtered = prev.filter((shopId) => remappedPageData.byShop[shopId] && shopId !== AREA_TAB_ID);
          if (filtered.length > 0) {
            return filtered;
          }
          const fallbackShopId = remappedPageData.shops.find((shop) => shop.id !== AREA_TAB_ID)?.id ?? preferredShopId;
          return fallbackShopId && fallbackShopId !== AREA_TAB_ID ? [fallbackShopId] : [];
        });
      }
      return hasShopData;
    } catch (err) {
      console.error('Error loading merchandising plan config/context:', err);
      return false;
    } finally {
      setIsLoadingConfig(false);
      setIsLoadingContext(false);
    }
  }, [appOptimGender, currentMessageId, entryMode, fetchLinkedOrderRunBaseline, resultContext?.selectedShopId, thread.threadId]);

  useEffect(() => {
    if (skipInitialLoad || entryMode !== 'new-optimization') {
      return;
    }
    void loadConfigAndContext();
  }, [entryMode, loadConfigAndContext, skipInitialLoad]);

  useEffect(() => {
    if (skipInitialLoad || entryMode !== 'from-result' || !thread.threadId || preparedFromResultThreadId === thread.threadId) {
      return;
    }

    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
    if (!messageId) {
      return;
    }

    let cancelled = false;

    const prepareFromResult = async () => {
      setIsPreparingFromResult(true);
      setImportError(null);
      try {
        const hasData = await enqueueMutation(async () => {
          // SPJ-1366: ripresa della bozza, niente fork; il config di lavoro lo risolve persona
          // da pending-config-id.
          if (!resumeDraft) {
            await performAction({
              threadId: thread.threadId as string,
              messageId,
              actionName: 'optim-fork-config',
              params: {},
            });
          }
          return await loadConfigAndContext();
        });
        if (!cancelled) {
          setPreparedFromResultThreadId(thread.threadId);
          setInputDataCompleted(hasData);
          setActiveTab('perimetro-budget');
        }
      } catch (err) {
        console.error('Error preparing merchandising plan from optimization result:', err);
        if (!cancelled) {
          setPreparedFromResultThreadId(thread.threadId);
          setImportError(err instanceof Error ? err.message : 'Errore nella preparazione della nuova configurazione.');
        }
      } finally {
        if (!cancelled) {
          setIsPreparingFromResult(false);
        }
      }
    };

    void prepareFromResult();

    return () => {
      cancelled = true;
    };
  }, [entryMode, enqueueMutation, loadConfigAndContext, preparedFromResultThreadId, resumeDraft, skipInitialLoad, thread.threadData, thread.threadId]);

  const persistConfigNow = useCallback(
    async (nextConfig?: any) => {
      const threadId = thread.threadId;
      const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
      if (!threadId || !messageId) {
        throw new Error('Thread or message ID not available');
      }

      const configToPersist = nextConfig ?? getLatestConfigSnapshot();
      if (!configToPersist) {
        throw new Error('Config not available');
      }

      const savedConfig = await performAction({
        threadId,
        messageId,
        actionName: 'optim-update-config',
        params: {
          'optim-config-data': configToPersist,
        },
      });
      const resolvedConfig = savedConfig && typeof savedConfig === 'object' ? savedConfig : configToPersist;
      // Some backend instances may normalize/strip neutral delivery-priority rows
      // in the response. Preserve the user's submitted draft locally so the
      // editor does not reset cells to "Presente" immediately after saving.
      const submittedPriority = configToPersist?.constraints?.consegna_priority_by_tipo_filato;
      if (submittedPriority && typeof submittedPriority === 'object' && !Array.isArray(submittedPriority)) {
        if (!resolvedConfig.constraints || typeof resolvedConfig.constraints !== 'object') {
          resolvedConfig.constraints = {};
        }
        resolvedConfig.constraints.consegna_priority_by_tipo_filato = submittedPriority;
      }
      commitConfigState(resolvedConfig);
      return resolvedConfig;
    },
    [commitConfigState, getLatestConfigSnapshot, thread.threadId, thread.threadData],
  );

  const queuePersistLatestConfig = useCallback(async () => {
    return enqueueMutation(async () => {
      setIsSavingConfig(true);
      try {
        return await persistConfigNow();
      } finally {
        setIsSavingConfig(false);
      }
    });
  }, [enqueueMutation, persistConfigNow]);

  const flushPendingConfigSave = useCallback(async () => {
    if (pendingConfigSaveTimerRef.current === null || isFlushingPendingConfigSaveRef.current) {
      return;
    }

    window.clearTimeout(pendingConfigSaveTimerRef.current);
    pendingConfigSaveTimerRef.current = null;
    setHasPendingConfigSave(false);
    isFlushingPendingConfigSaveRef.current = true;
    try {
      const result = await queuePersistLatestConfig();
      resolvePendingConfigSaveWaiters(result);
    } catch (error) {
      resolvePendingConfigSaveWaiters(undefined, error);
      throw error;
    } finally {
      isFlushingPendingConfigSaveRef.current = false;
    }
  }, [queuePersistLatestConfig, resolvePendingConfigSaveWaiters]);

  const saveConfigDraft = useCallback(
    async (nextConfig: any) => {
      commitConfigState(nextConfig);
      if (pendingConfigSaveTimerRef.current !== null) {
        window.clearTimeout(pendingConfigSaveTimerRef.current);
        pendingConfigSaveTimerRef.current = null;
      }
      setHasPendingConfigSave(false);
      try {
        const result = await queuePersistLatestConfig();
        resolvePendingConfigSaveWaiters(result);
        return result;
      } catch (error) {
        resolvePendingConfigSaveWaiters(undefined, error);
        throw error;
      }
    },
    [commitConfigState, queuePersistLatestConfig, resolvePendingConfigSaveWaiters],
  );

  const saveConfigDraftDebounced = useCallback((nextConfig: any) => {
    commitConfigState(nextConfig);
    return new Promise<any>((resolve, reject) => {
      pendingConfigSaveWaitersRef.current.push({ resolve, reject });
      if (pendingConfigSaveTimerRef.current !== null) {
        window.clearTimeout(pendingConfigSaveTimerRef.current);
      }
      setHasPendingConfigSave(true);
      pendingConfigSaveTimerRef.current = window.setTimeout(() => {
        void flushPendingConfigSave();
      }, 250);
    });
  }, [commitConfigState, flushPendingConfigSave]);

  const runSerializedMutation = useCallback(async <T,>(job: () => Promise<T>): Promise<T> => {
    await flushPendingConfigSave();
    return enqueueMutation(job);
  }, [enqueueMutation, flushPendingConfigSave]);

  const refreshShopSkuContext = useCallback(
    async () => {
      const threadId = thread.threadId;
      const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
      if (!threadId || !messageId) {
        throw new Error('Thread or message ID not available');
      }

      const requestId = ++contextRequestIdRef.current;
      const contextResult = await performAction({
        threadId,
        messageId,
        actionName: 'optim-get-config-shop-sku-context',
        params: {},
      });

      if (requestId !== contextRequestIdRef.current) {
        return;
      }

      const fallbackWithConfigId: MerchandisingPlanPageData = {
        ...pageDataRef.current,
        configId: String((configStateRef.current as any)?.id ?? pageDataRef.current.configId),
      };
      const nextPageData = mapContextResponseToPageData(contextResult, fallbackWithConfigId);
      const persistedShopMapping = readPersistedMerchandisingShopMapping(threadId);
      const effectiveShopMapping = Object.keys(shopMappingLabels).length > 0 ? shopMappingLabels : persistedShopMapping;
      applyPageData(applyShopLabels(nextPageData, effectiveShopMapping));
    },
    [applyPageData, shopMappingLabels, thread.threadId, thread.threadData],
  );

  const validateSkuList = useCallback(
    async (skus: string[]) => {
      const threadId = thread.threadId;
      const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
      if (!threadId || !messageId) {
        throw new Error('Thread or message ID not available');
      }

      const gender = String((configDraft as any)?.gender ?? (configDetails as any)?.gender ?? appState?.['optim-gender'] ?? '').trim();
      const codStagione = String(
        (configDraft as any)?.cod_stagione
          ?? (configDraft as any)?.season
          ?? (configDetails as any)?.cod_stagione
          ?? (configDetails as any)?.season
          ?? appState?.['optim-season']
          ?? '',
      ).trim();

      return performAction({
        threadId,
        messageId,
        actionName: 'optim-validate-sku-list',
        params: {
          'optim-skus-list': skus,
          'optim-gender': gender,
          'optim-cod-stagione': codStagione,
        },
      });
    },
    [appState, configDetails, configDraft, thread.threadId, thread.threadData],
  );

  const uploadSkuListForShopNow = useCallback(
    async (shopId: string, skus: string[], lbValues: number[] | number, lockedSkus?: string[]) => {
      const threadId = thread.threadId;
      const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
      if (!threadId || !messageId) {
        throw new Error('Thread or message ID not available');
      }

      const params: Record<string, unknown> = {
        'optim-skus-list': skus,
        'optim-lb-quantity': lbValues,
        'optim-shop-id': shopId,
      };
      // Gli SKU bloccati vanno dichiarati: altrimenti il backend riapre l'upper bound e
      // il blocco impostato prima del salvataggio si perde.
      if (lockedSkus && lockedSkus.length > 0) {
        params['optim-locked-skus'] = lockedSkus;
      }

      const nextConfig = await performAction({
        threadId,
        messageId,
        actionName: 'optim-upload-skus-list',
        params,
      });

      if (nextConfig && typeof nextConfig === 'object') {
        commitConfigState(nextConfig);
      }

      return nextConfig;
    },
    [commitConfigState, thread.threadId, thread.threadData],
  );

  // SPJ-890: aggiorna la config SOLO in locale (niente persist immediato); il
  // salvataggio backend + ricalcolo avviene tutto insieme al click su "Salva".
  const saveConfigDeferred = useCallback((nextConfig: any): Promise<void> => {
    commitConfigState(nextConfig);
    pendingConfigDirtyRef.current = true;
    markUnsaved();
    return Promise.resolve();
  }, [commitConfigState]);

  // SPJ-890: salvataggio esplicito unico. Persiste in un colpo solo tutte le
  // modifiche accumulate (config, min pezzi/upload SKU, vincoli tipo filato,
  // vincoli budget) e fa UN solo ricalcolo del contesto/metriche.
  const handleSaveAll = useCallback(async () => {
    if (!hasUnsavedChanges || isSavingAll || isMutationQueueBusy) return;
    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
    if (!threadId || !messageId) return;
    const numericOrNull = (value: unknown) => (hasDefinedBound(value) ? Number(value) : null);
    setIsSavingAll(true);
    try {
      await runSerializedMutation(async () => {
        // 1. Config unica (lock/unlock, rimozioni SKU, priorità consegna, upper bound bloccati)
        if (pendingConfigDirtyRef.current) {
          await persistConfigNow(getLatestConfigSnapshot());
          pendingConfigDirtyRef.current = false;
        }
        // 2. Upload liste SKU (min pezzi) accumulati, per negozio.
        // Il backend (update-config-w-sku-list) applica UN lb_quantity scalare a tutta
        // la lista, quindi raggruppo gli SKU per valore e faccio una upload per valore.
        for (const [shopId, skuValues] of pendingSkuMinRef.current.entries()) {
          const skusByValue = new Map<number, string[]>();
          for (const [sku, value] of skuValues.entries()) {
            const list = skusByValue.get(value) || [];
            list.push(sku);
            skusByValue.set(value, list);
          }
          // Quali SKU sono bloccati (min=max) al momento del salvataggio. Vanno dichiarati
          // all'upload: senza, il backend riaprirebbe l'upper bound e cancellerebbe un
          // blocco appena impostato al passo 1, che e' esattamente il caso "modifico i
          // minimi, blocco, salvo" in cui il blocco spariva.
          const lockedSkus = new Set<string>();
          (pageDataRef.current.byShop[shopId]?.tipoFilatoSections || []).forEach((section) => {
            section.skuRows.forEach((row) => {
              if (Number(row.ubQuantityPerShop) === Number(row.lbQuantityPerShop)) {
                lockedSkus.add(row.sku);
              }
            });
          });
          for (const [value, skus] of skusByValue.entries()) {
            if (skus.length === 0) continue;
            await uploadSkuListForShopNow(shopId, skus, value, skus.filter((sku) => lockedSkus.has(sku)));
          }
        }
        pendingSkuMinRef.current.clear();
        // 3. Vincoli tipo filato — payload completo ricostruito dallo stato locale, per negozio modificato
        for (const shopId of Array.from(pendingMpShopsRef.current)) {
          const shop = pageDataRef.current.byShop[shopId];
          if (!shop) continue;
          // Si salva SEMPRE la capacita' grezza, mai quella residua mostrata a schermo:
          // la residua verrebbe riscalata alla lettura successiva e il piano si
          // consumerebbe a ogni salvataggio. Senza run collegata grezzo e residuo
          // coincidono, quindi il comportamento non cambia.
          const savedBound = (metric: ConstraintMetric) => numericOrNull(
            metric.rawUpperBound !== undefined ? metric.rawUpperBound : metric.upperBound,
          );
          const tipoFilatoPayload = Object.fromEntries(
            shop.tipoFilatoSections.map((section) => ([
              section.tipoFilato,
              {
                items_total: savedBound(section.constraints.items),
                currency_value: savedBound(section.constraints.economicValue),
                models_unique: numericOrNull(section.constraints.uniqueModels.upperBound),
                sku_count: numericOrNull(section.constraints.skuCount.upperBound),
                avg_price: numericOrNull(section.constraints.avgPrice.upperBound),
              },
            ])),
          );
          await performAction({
            threadId,
            messageId,
            actionName: 'optim-update-mp-line-payloads',
            params: { 'optim-mp-lines': [{ sub_idx: 1, cod_negozio: shopId, data: { tipo_filato: tipoFilatoPayload } }] },
          });
        }
        pendingMpShopsRef.current.clear();
        // 4. Vincoli budget per consegna — payload completo ricostruito dallo stato locale
        for (const shopId of Array.from(pendingBudgetShopsRef.current)) {
          const shop = pageDataRef.current.byShop[shopId];
          if (!shop) continue;
          const budgetPayload = shop.budget.actuals.map((entry) => {
            const currentBudget = shop.budget.upperBounds[entry.consegna] || { value: 0, items: 0 };
            return { sotto_consegna: entry.consegna, value: Number(currentBudget.value ?? 0), items: Number(currentBudget.items ?? 0) };
          });
          await performAction({
            threadId,
            messageId,
            actionName: 'optim-update-budget-line-payloads',
            params: { 'optim-budget-lines': [{ sub_idx: 1, cod_negozio: shopId, data: { consegne: budgetPayload } }] },
          });
        }
        pendingBudgetShopsRef.current.clear();
        // 5. Un unico ricalcolo contesto/metriche
        await refreshShopSkuContext();
      });
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Errore durante il salvataggio delle modifiche:', err);
    } finally {
      setIsSavingAll(false);
    }
  }, [hasUnsavedChanges, isSavingAll, isMutationQueueBusy, thread.threadId, thread.threadData, runSerializedMutation, persistConfigNow, getLatestConfigSnapshot, uploadSkuListForShopNow, refreshShopSkuContext]);

  // Nessun autosalvataggio: il MP si salva solo esplicitamente con "Salva modifiche"; il
  // beforeunload sotto avvisa se si chiude/ricarica con modifiche non salvate.

  // Avviso del browser se si chiude o ricarica mentre c'e' qualcosa da salvare.
  useEffect(() => {
    if (!hasUnsavedChanges && !isSavingAll) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedChanges, isSavingAll]);

  const updateShopData = useCallback((shopId: string, updater: (shop: ShopMerchandisingPlan) => ShopMerchandisingPlan) => {
    const previousPageData = pageDataRef.current;
    const currentShopSnapshot = previousPageData.byShop[shopId];
    if (!currentShopSnapshot) {
      return undefined;
    }

    const nextShop = updater(currentShopSnapshot);
    const nextPageData = {
      ...previousPageData,
      byShop: {
        ...previousPageData.byShop,
        [shopId]: nextShop,
      },
    };
    applyPageData(nextPageData);
    return nextShop;
  }, [applyPageData]);

  const handleBudgetUpperBoundChange = useCallback((consegna: string, field: 'value' | 'items', value: number) => {
    updateShopData(selectedShopId, (shop) => ({
      ...shop,
      budget: {
        ...shop.budget,
        upperBounds: {
          ...shop.budget.upperBounds,
          [consegna]: {
            ...shop.budget.upperBounds[consegna],
            [field]: value,
          },
        },
      },
    }));
  }, [selectedShopId, updateShopData]);

  const handleTipoFilatoMinChange = useCallback(async (tipoFilato: string, value: number) => {
    if (isAreaTabSelected || !Number.isFinite(value) || value < 0) return;

    const currentSection = currentShop?.tipoFilatoSections.find((section) => section.tipoFilato === tipoFilato);
    if (!currentSection) return;

    const skus = currentSection.skuRows.map((row) => row.sku);
    const lockedUpperBounds = Object.fromEntries(
      currentSection.skuRows
        .filter((row) => Number(row.ubQuantityPerShop) === Number(row.lbQuantityPerShop))
        .map((row) => [row.sku, value]),
    );

    updateShopData(selectedShopId, (shop) => ({
      ...shop,
      tipoFilatoSections: shop.tipoFilatoSections.map((section) =>
        section.tipoFilato === tipoFilato
          ? {
              ...section,
              editableMinValue: value,
              skuRows: section.skuRows.map((row) => ({
                ...row,
                minValue: value,
                lbQuantity: value,
                lbQuantityPerShop: value,
                ubQuantity: row.ubQuantity,
                ubQuantityPerShop: Number(row.ubQuantityPerShop) === Number(row.lbQuantityPerShop) ? value : row.ubQuantityPerShop,
              })),
              constraints: {
                ...section.constraints,
                economicValue: {
                  ...section.constraints.economicValue,
                  lowerBound: value,
                },
              },
            }
          : section,
      ),
    }));

    // SPJ-890: accumula min pezzi (upload deferito) e upper-bound bloccati (config locale); nessun salvataggio/ricalcolo immediato.
    if (skus.length > 0) {
      const shopMap = pendingSkuMinRef.current.get(selectedShopId) || new Map<string, number>();
      skus.forEach((sku) => shopMap.set(sku, value));
      pendingSkuMinRef.current.set(selectedShopId, shopMap);
      if (Object.keys(lockedUpperBounds).length > 0) {
        const nextConfig = getLatestConfigSnapshot();
        if (nextConfig?.constraints) {
          nextConfig.constraints.ub_quantity_per_shop = nextConfig.constraints.ub_quantity_per_shop || {};
          nextConfig.constraints.ub_quantity_per_shop[selectedShopId] = nextConfig.constraints.ub_quantity_per_shop[selectedShopId] || {};
          Object.entries(lockedUpperBounds).forEach(([sku, upperBound]) => {
            nextConfig.constraints.ub_quantity_per_shop[selectedShopId][sku] = upperBound;
          });
          commitConfigState(nextConfig);
          pendingConfigDirtyRef.current = true;
        }
      }
      markUnsaved();
    }
  }, [currentShop, getLatestConfigSnapshot, commitConfigState, isAreaTabSelected, selectedShopId, updateShopData]);

  const handleTipoFilatoMinDraftChange = useCallback((tipoFilato: string, value: string) => {
    if (!/^\d*$/.test(value)) return;
    setTipoFilatoMinDrafts((prev) => ({ ...prev, [tipoFilato]: value }));
  }, []);

  const commitTipoFilatoMinDraft = useCallback((tipoFilato: string) => {
    const draftValue = tipoFilatoMinDrafts[tipoFilato];
    if (draftValue === undefined) return;

    setTipoFilatoMinDrafts((prev) => {
      const next = { ...prev };
      delete next[tipoFilato];
      return next;
    });

    if (draftValue.trim() === '') return;
    void handleTipoFilatoMinChange(tipoFilato, Number(draftValue));
  }, [handleTipoFilatoMinChange, tipoFilatoMinDrafts]);

  const getSkuDraftKey = useCallback((tipoFilato: string, sku: string) => `${selectedShopId}:${tipoFilato}:${sku}`, [selectedShopId]);
  const getSkuSelectionKey = useCallback((tipoFilato: string, sku: string) => `${selectedShopId}:${tipoFilato}:${sku}`, [selectedShopId]);

  const toggleSkuSelection = useCallback((tipoFilato: string, sku: string) => {
    const selectionKey = getSkuSelectionKey(tipoFilato, sku);
    setSelectedSkuKeys((prev) => ({
      ...prev,
      [selectionKey]: !prev[selectionKey],
    }));
  }, [getSkuSelectionKey]);

  const clearSkuSelections = useCallback((tipoFilato: string, skus: string[]) => {
    setSelectedSkuKeys((prev) => {
      const next = { ...prev };
      skus.forEach((sku) => {
        delete next[getSkuSelectionKey(tipoFilato, sku)];
      });
      return next;
    });
  }, [getSkuSelectionKey]);

  const handleSkuMinDraftChange = useCallback((tipoFilato: string, sku: string, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const draftKey = getSkuDraftKey(tipoFilato, sku);
    setSkuMinDrafts((prev) => ({ ...prev, [draftKey]: value }));
  }, [getSkuDraftKey]);

  const clearSkuMinDraft = useCallback((tipoFilato: string, sku: string) => {
    const draftKey = getSkuDraftKey(tipoFilato, sku);
    setSkuMinDrafts((prev) => {
      const next = { ...prev };
      delete next[draftKey];
      return next;
    });
  }, [getSkuDraftKey]);

  const handleSkuMinChange = useCallback((tipoFilato: string, sku: string, value: number) => {
    if (isAreaTabSelected) return;
    const targetRow = currentShop?.tipoFilatoSections
      .find((section) => section.tipoFilato === tipoFilato)
      ?.skuRows.find((row) => row.sku === sku);
    const shouldKeepLocked = targetRow ? Number(targetRow.ubQuantityPerShop) === Number(targetRow.lbQuantityPerShop) : false;

    updateShopData(selectedShopId, (shop) => ({
      ...shop,
      tipoFilatoSections: shop.tipoFilatoSections.map((section) =>
        section.tipoFilato === tipoFilato
          ? {
              ...section,
              skuRows: section.skuRows.map((row) => (row.sku === sku ? {
                ...row,
                minValue: value,
                lbQuantity: value,
                lbQuantityPerShop: value,
                ubQuantity: row.ubQuantity,
                ubQuantityPerShop: shouldKeepLocked ? value : row.ubQuantityPerShop,
              } : row)),
            }
          : section,
      ),
    }));

    // SPJ-890: accumula il min pezzi (upload deferito) + upper-bound bloccato in config locale.
    const shopMap = pendingSkuMinRef.current.get(selectedShopId) || new Map<string, number>();
    shopMap.set(sku, value);
    pendingSkuMinRef.current.set(selectedShopId, shopMap);
    if (shouldKeepLocked) {
      const nextConfig = getLatestConfigSnapshot();
      if (nextConfig?.constraints) {
        nextConfig.constraints.ub_quantity_per_shop = nextConfig.constraints.ub_quantity_per_shop || {};
        nextConfig.constraints.ub_quantity_per_shop[selectedShopId] = nextConfig.constraints.ub_quantity_per_shop[selectedShopId] || {};
        nextConfig.constraints.ub_quantity_per_shop[selectedShopId][sku] = value;
        commitConfigState(nextConfig);
        pendingConfigDirtyRef.current = true;
      }
    }
    markUnsaved();
  }, [currentShop, getLatestConfigSnapshot, commitConfigState, isAreaTabSelected, selectedShopId, updateShopData]);

  const commitSkuMinDraft = useCallback((tipoFilato: string, sku: string) => {
    const draftKey = getSkuDraftKey(tipoFilato, sku);
    const draftValue = skuMinDrafts[draftKey];
    if (draftValue === undefined) return;

    clearSkuMinDraft(tipoFilato, sku);
    if (draftValue.trim() === '') return;
    void handleSkuMinChange(tipoFilato, sku, Number(draftValue));
  }, [clearSkuMinDraft, getSkuDraftKey, handleSkuMinChange, skuMinDrafts]);

  const removeSkusFromShop = useCallback((tipoFilato: string, skusToRemove: string[]) => {
    if (isAreaTabSelected || skusToRemove.length === 0) return;
    updateShopData(selectedShopId, (shop) => ({
      ...shop,
      tipoFilatoSections: shop.tipoFilatoSections
        .map((section) =>
          section.tipoFilato === tipoFilato
            ? {
                ...section,
                skuRows: section.skuRows.filter((row) => !skusToRemove.includes(row.sku)),
              }
            : section,
        )
        .filter((section) => section.skuRows.length > 0),
    }));

    try {
      const nextConfig = getLatestConfigSnapshot();
      if (!nextConfig || typeof nextConfig !== 'object') {
        throw new Error('Config non disponibile');
      }

      if (!nextConfig.constraints || typeof nextConfig.constraints !== 'object') {
        nextConfig.constraints = {};
      }

      if (!nextConfig.constraints.lb_quantity_per_shop || typeof nextConfig.constraints.lb_quantity_per_shop !== 'object' || Array.isArray(nextConfig.constraints.lb_quantity_per_shop)) {
        nextConfig.constraints.lb_quantity_per_shop = {};
      }
      if (!nextConfig.constraints.ub_quantity_per_shop || typeof nextConfig.constraints.ub_quantity_per_shop !== 'object' || Array.isArray(nextConfig.constraints.ub_quantity_per_shop)) {
        nextConfig.constraints.ub_quantity_per_shop = {};
      }
      if (!nextConfig.constraints.lb_quantity_per_shop[selectedShopId] || typeof nextConfig.constraints.lb_quantity_per_shop[selectedShopId] !== 'object' || Array.isArray(nextConfig.constraints.lb_quantity_per_shop[selectedShopId])) {
        nextConfig.constraints.lb_quantity_per_shop[selectedShopId] = {};
      }
      if (!nextConfig.constraints.ub_quantity_per_shop[selectedShopId] || typeof nextConfig.constraints.ub_quantity_per_shop[selectedShopId] !== 'object' || Array.isArray(nextConfig.constraints.ub_quantity_per_shop[selectedShopId])) {
        nextConfig.constraints.ub_quantity_per_shop[selectedShopId] = {};
      }

      skusToRemove.forEach((sku) => {
        delete nextConfig.constraints.lb_quantity_per_shop[selectedShopId][sku];
        delete nextConfig.constraints.ub_quantity_per_shop[selectedShopId][sku];
      });

      // SPJ-890: applica in config locale; salvataggio + ricalcolo deferiti al "Salva".
      commitConfigState(nextConfig);
      pendingConfigDirtyRef.current = true;
      clearSkuSelections(tipoFilato, skusToRemove);
      markUnsaved();
    } catch (err) {
      console.error('Error removing merchandising plan SKUs:', err);
    }
  }, [clearSkuSelections, getLatestConfigSnapshot, commitConfigState, isAreaTabSelected, selectedShopId, updateShopData]);

  const handleRemoveSku = useCallback(async (tipoFilato: string, sku: string) => {
    await removeSkusFromShop(tipoFilato, [sku]);
  }, [removeSkusFromShop]);

  const handleRemoveSelectedSkus = useCallback(async (tipoFilato: string) => {
    const currentSection = currentShop?.tipoFilatoSections.find((section) => section.tipoFilato === tipoFilato);
    if (!currentSection) return;
    const skusToRemove = currentSection.skuRows
      .map((row) => row.sku)
      .filter((sku) => selectedSkuKeys[getSkuSelectionKey(tipoFilato, sku)]);
    await removeSkusFromShop(tipoFilato, skusToRemove);
  }, [currentShop, getSkuSelectionKey, removeSkusFromShop, selectedSkuKeys]);

  const handleImportFromOptimization = useCallback(async (
    overrideRunId?: number | null,
    options?: { lockTaglie?: boolean },
  ) => {
    const lockTaglie = Boolean(options?.lockTaglie);
    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
    if (!threadId || !messageId) {
      setImportError('Thread o messaggio non disponibili per l\'import.');
      return;
    }

    setImportOptimizationLockTaglieDraft(lockTaglie);
    setIsImportingOptimization(true);
    setImportError(null);
    setSkuListMessage(null);
    setSkuListError(null);

    try {
      const isManualRunOverride = overrideRunId !== undefined && overrideRunId !== null;
      const importRunId = isManualRunOverride
        ? overrideRunId
        : resolvedImportRunId;
      if (importRunId === null) {
        setManualImportOptimizationRunIdDraft((prev) => prev || '');
        setImportOptimizationLockTaglieDraft(lockTaglie);
        setShowImportOptimizationRunModal(true);
        return;
      }
      const params: Record<string, unknown> = {};
      params['optim-run-id'] = importRunId;
      if (lockTaglie) {
        params['optim-import-taglie'] = true;
      }
      params['optim-clear-existing-shop-bounds'] = true;
      if (resolvedImportConfigId !== null) {
        params['optim-config-id'] = resolvedImportConfigId;
      }

      const hasData = await runSerializedMutation(async () => {
        const importedConfig = await performAction({
          threadId,
          messageId,
          actionName: 'optim-update-config-w-run-lb',
          params,
        });
        let configToUse = importedConfig;
        if (importedConfig && typeof importedConfig === 'object') {
          const desiredDefaultUb = optimizationScopeMode === 'lista' ? 0 : 100000000000000000;
          if ((importedConfig as any)?.constraints?.default_ub_quantity !== desiredDefaultUb) {
            const nextConfig = JSON.parse(JSON.stringify(importedConfig));
            nextConfig.constraints = nextConfig.constraints || {};
            nextConfig.constraints.default_ub_quantity = desiredDefaultUb;
            configToUse = await persistConfigNow(nextConfig);
          } else {
            commitConfigState(importedConfig);
          }
        }
        const importedConfigId = parseOptionalNumericId((configToUse as any)?.id) ?? resolvedImportConfigId;

        await dispatch(fetchThread()).unwrap();
        return await loadConfigAndContext(importedConfigId);
      });
      setInputDataCompleted(hasData);
      setShowImportOptimizationRunModal(false);
      setManualImportOptimizationRunIdDraft('');
      setSkuListMessage(lockTaglie
        ? 'Quantità e taglie importate dall\'ottimizzazione con successo. Le quantità SKU sono bloccate min=max e le taglie importate verranno preservate nello split consegne.'
        : 'Quantità importate dall\'ottimizzazione con successo. Le taglie verranno ricalcolate dalla prossima ottimizzazione come nel comportamento storico.');
      if (resultContext?.selectedShopId) {
        setSelectedShopId(resultContext.selectedShopId);
      }
    } catch (err) {
      console.error('Error importing values from optimization result:', err);
      setImportError(err instanceof Error ? err.message : 'Errore durante l\'import da ottimizzazione.');
    } finally {
      setIsImportingOptimization(false);
    }
  }, [commitConfigState, dispatch, loadConfigAndContext, optimizationScopeMode, persistConfigNow, resolvedImportConfigId, resolvedImportRunId, resultContext?.selectedShopId, runSerializedMutation, thread.threadData, thread.threadId]);

  const loadCompatibleOptimizationRuns = useCallback(async () => {
    const threadId = thread.threadId;
    const messageId = currentMessageId;
    if (!threadId || !messageId) {
      throw new Error('Thread o messaggio non disponibili per caricare le run.');
    }
    const result = await performAction({
      threadId,
      messageId,
      actionName: 'optim-get-linkable-order-runs',
      params: buildCompatibleRunParams(),
    });
    return Array.isArray(result) ? result as LinkableOrderRun[] : [];
  }, [buildCompatibleRunParams, currentMessageId, thread.threadId]);

  const openLinkedOrderRunModal = useCallback(async () => {
    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
    if (!threadId || !messageId) {
      setImportError('Thread o messaggio non disponibili per collegare gli ordini.');
      return;
    }

    setShowLinkedOrderRunModal(true);
    setLinkedOrderRunError(null);
    setManualLinkedOrderRunIdDraft((prev) => prev || (resolvedImportRunId !== null ? String(resolvedImportRunId) : ''));
    setIsLoadingLinkableOrderRuns(true);
    try {
      const runs = await loadCompatibleOptimizationRuns();
      setLinkableOrderRuns(runs);
      setSelectedLinkedOrderRunId((prev) => {
        if (prev !== null && runs.some((run) => Number(run.run_id) === prev)) return prev;
        if (resolvedImportRunId !== null && runs.some((run) => Number(run.run_id) === resolvedImportRunId)) return resolvedImportRunId;
        return runs.length > 0 ? Number(runs[0].run_id) : null;
      });
    } catch (err) {
      console.error('Error loading linkable order runs:', err);
      setLinkedOrderRunError(err instanceof Error ? err.message : 'Errore nel caricamento delle run collegabili.');
      setLinkableOrderRuns([]);
      setSelectedLinkedOrderRunId(null);
    } finally {
      setIsLoadingLinkableOrderRuns(false);
    }
  }, [loadCompatibleOptimizationRuns, resolvedImportRunId, thread.threadData, thread.threadId]);

  const handleLinkSelectedRunAsOrders = useCallback(async () => {
    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
    const linkedRunId = selectedLinkedOrderRunId ?? parseOptionalNumericId(manualLinkedOrderRunIdDraft);
    if (!threadId || !messageId || linkedRunId === null) {
      setLinkedOrderRunError('Inserisci o seleziona una run valida da usare come ordini già piazzati.');
      return;
    }

    setIsLinkingOrderRun(true);
    setImportError(null);
    setLinkedOrderRunError(null);
    try {
      await runSerializedMutation(async () => {
        const updatedConfig = await performAction({
          threadId,
          messageId,
          actionName: 'optim-set-linked-order-run',
          params: {
            'optim-run-id': linkedRunId,
            'optim-config-id': resolvedImportConfigId,
            'optim-linked-order-run-id': linkedRunId,
            'optim-scale-orders-source': 'orders_and_linked_run',
            'optim-scale-linked-run-mp': scaleLinkedRunMp,
          },
        });
        if (updatedConfig && typeof updatedConfig === 'object') {
          commitConfigState(updatedConfig);
          await loadConfigAndContext(parseOptionalNumericId((updatedConfig as any).id) ?? resolvedImportConfigId);
        }
      });
      const previewBaseline = await fetchLinkedOrderRunBaseline(linkedRunId, scaleLinkedRunMp);
      const selectedRunInfo = previewBaseline?.enabled
        ? baselineToRunInfo(previewBaseline)
        : linkableOrderRuns.find((run) => Number(run.run_id) === linkedRunId) ?? {
          run_id: linkedRunId,
          status: 'Configurata',
        };
      setLinkedOrderRunInfo(selectedRunInfo);
      setShowLinkedOrderRunModal(false);
      setShowLinkedOrderRunInfo(true);
      setSkuListMessage(`La run #${linkedRunId} verrà usata come ordini già piazzati per quantità e taglie.`);
    } catch (err) {
      console.error('Error linking order run:', err);
      setLinkedOrderRunError(err instanceof Error ? err.message : 'Errore nel collegamento della run come ordini già piazzati.');
    } finally {
      setIsLinkingOrderRun(false);
    }
  }, [commitConfigState, fetchLinkedOrderRunBaseline, linkableOrderRuns, loadConfigAndContext, manualLinkedOrderRunIdDraft, resolvedImportConfigId, runSerializedMutation, scaleLinkedRunMp, selectedLinkedOrderRunId, thread.threadData, thread.threadId]);

  const handleClearLinkedOrderRun = useCallback(async () => {
    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
    if (!threadId || !messageId) {
      setImportError('Thread o messaggio non disponibili per rimuovere il collegamento run.');
      return;
    }

    setIsLinkingOrderRun(true);
    setImportError(null);
    setLinkedOrderRunError(null);
    try {
      await runSerializedMutation(async () => {
        const updatedConfig = await performAction({
          threadId,
          messageId,
          actionName: 'optim-clear-linked-order-run',
          params: {
            'optim-config-id': resolvedImportConfigId,
          },
        });
        if (updatedConfig && typeof updatedConfig === 'object') {
          commitConfigState(updatedConfig);
          await loadConfigAndContext(parseOptionalNumericId((updatedConfig as any).id) ?? resolvedImportConfigId);
        }
      });
      setLinkedOrderRunInfo(null);
      setSelectedLinkedOrderRunId(null);
      setManualLinkedOrderRunIdDraft('');
      setScaleLinkedRunMp(true);
      setShowLinkedOrderRunInfo(false);
      setShowLinkedOrderRunModal(false);
      setSkuListMessage('Collegamento alla run ordini rimosso. Gli ordini reali restano disponibili solo come informazione o scaling se abilitato.');
    } catch (err) {
      console.error('Error clearing linked order run:', err);
      const message = err instanceof Error ? err.message : 'Errore nella rimozione della run usata come ordini.';
      setImportError(message);
      setLinkedOrderRunError(message);
    } finally {
      setIsLinkingOrderRun(false);
    }
  }, [commitConfigState, loadConfigAndContext, resolvedImportConfigId, runSerializedMutation, thread.threadData, thread.threadId]);

  const handleImportFromMinimumValues = useCallback(async () => {
    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
    if (!threadId || !messageId) {
      setImportError('Thread o messaggio non disponibili per l\'import.');
      return;
    }

    setIsImportingMinimumValues(true);
    setImportError(null);
    setSkuListMessage(null);
    setSkuListError(null);

    try {
      const previousRunConfigId = await performAction({
        threadId,
        messageId,
        actionName: 'optim-get-previous-run-config-id',
        params: resolvedImportRunId !== null ? { 'optim-run-id': resolvedImportRunId } : {},
      });

      const sourceConfigId = parseOptionalNumericId(previousRunConfigId);
      if (sourceConfigId === null) {
        throw new Error('Config precedente non disponibile per importare i valori minimi.');
      }

      const previousConfig = await performAction({
        threadId,
        messageId,
        actionName: 'optim-get-config-details',
        params: {
          'optim-config-id': sourceConfigId,
        },
      }) as any;

      const nextConfig = getLatestConfigSnapshot();
      if (!nextConfig || typeof nextConfig !== 'object') {
        throw new Error('Config forked non disponibile.');
      }
      if (!previousConfig || typeof previousConfig !== 'object') {
        throw new Error('Config precedente non disponibile.');
      }

      if (!nextConfig.constraints || typeof nextConfig.constraints !== 'object') {
        nextConfig.constraints = {};
      }
      const previousConstraints = previousConfig.constraints && typeof previousConfig.constraints === 'object'
        ? previousConfig.constraints
        : {};

      if ('lb_quantity_per_shop' in previousConstraints) {
        nextConfig.constraints.lb_quantity_per_shop = JSON.parse(JSON.stringify(previousConstraints.lb_quantity_per_shop ?? {}));
      }
      if ('ub_quantity_per_shop' in previousConstraints) {
        nextConfig.constraints.ub_quantity_per_shop = JSON.parse(JSON.stringify(previousConstraints.ub_quantity_per_shop ?? {}));
      }
      if ('lb_quantity' in previousConstraints) {
        nextConfig.constraints.lb_quantity = JSON.parse(JSON.stringify(previousConstraints.lb_quantity ?? {}));
      }
      if ('ub_quantity' in previousConstraints) {
        nextConfig.constraints.ub_quantity = JSON.parse(JSON.stringify(previousConstraints.ub_quantity ?? {}));
      }

      commitConfigState(nextConfig);
      await runSerializedMutation(async () => {
        await persistConfigNow(nextConfig);
        await refreshShopSkuContext();
      });
      setInputDataCompleted(Object.keys(pageDataRef.current.byShop).length > 0);
      setSkuListMessage('Valori minimi importati con successo dalla configurazione precedente.');
      if (resultContext?.selectedShopId && pageDataRef.current.byShop[resultContext.selectedShopId]) {
        setSelectedShopId(resultContext.selectedShopId);
      }
    } catch (err) {
      console.error('Error importing minimum values from previous config:', err);
      setImportError(err instanceof Error ? err.message : 'Errore durante l\'import dei valori minimi.');
    } finally {
      setIsImportingMinimumValues(false);
    }
  }, [commitConfigState, getLatestConfigSnapshot, persistConfigNow, refreshShopSkuContext, resolvedImportRunId, resultContext?.selectedShopId, runSerializedMutation, thread.threadData, thread.threadId]);

  const updateConsegnaPriorityConfig = useCallback(async (
    updater: (current: Record<string, Record<string, ConsegnaPriorityState>>) => Record<string, Record<string, ConsegnaPriorityState>>,
  ) => {
    const nextConfig = getLatestConfigSnapshot();
    if (!nextConfig || typeof nextConfig !== 'object') return;
    if (!nextConfig.constraints || typeof nextConfig.constraints !== 'object') nextConfig.constraints = {};
    if (!nextConfig.constraints.active_constraints || typeof nextConfig.constraints.active_constraints !== 'object') {
      nextConfig.constraints.active_constraints = {};
    }
    const current = nextConfig.constraints.consegna_priority_by_tipo_filato && typeof nextConfig.constraints.consegna_priority_by_tipo_filato === 'object'
      ? JSON.parse(JSON.stringify(nextConfig.constraints.consegna_priority_by_tipo_filato))
      : {};
    const cleaned = Object.fromEntries(
      Object.entries(updater(current)).map(([tf, byConsegna]) => {
        const entries = Object.entries(byConsegna || {}).filter(([, state]) => normalizeConsegnaPriorityState(state) !== 'neutral');
        return [tf, Object.fromEntries(entries)];
      }).filter(([, byConsegna]) => Object.keys(byConsegna as Record<string, ConsegnaPriorityState>).length > 0),
    );
    nextConfig.constraints.consegna_priority_by_tipo_filato = cleaned;
    nextConfig.constraints.active_constraints.consegna_priority = Object.keys(cleaned).length > 0;
    if (!nextConfig.objective || typeof nextConfig.objective !== 'object') nextConfig.objective = {};
    if (nextConfig.objective.lambda_consegna_priority === undefined) nextConfig.objective.lambda_consegna_priority = 1;
    saveConfigDeferred(nextConfig);
  }, [getLatestConfigSnapshot, saveConfigDeferred]);

  const handleConsegnaPriorityCellClick = useCallback(async (tipoFilato: string, consegna: string) => {
    await updateConsegnaPriorityConfig((current) => {
      const currentState = current[tipoFilato]?.[consegna] ?? 'neutral';
      const nextState = nextConsegnaPriorityState(currentState);
      return {
        ...current,
        [tipoFilato]: {
          ...(current[tipoFilato] || {}),
          [consegna]: nextState,
        },
      };
    });
  }, [updateConsegnaPriorityConfig]);

  const handleApplyEarlyConsegnaPreset = useCallback(async () => {
    const consegne = currentShop?.budget.actuals.map((entry) => entry.consegna) || [];
    const tipiFilato = currentShop?.tipoFilatoSections.map((section) => section.tipoFilato) || [];
    await updateConsegnaPriorityConfig((current) => {
      const next = { ...current };
      for (const tf of tipiFilato) {
        next[tf] = { ...(next[tf] || {}) };
        consegne.forEach((consegna, index) => {
          next[tf][consegna] = index <= 1 ? 'prioritized' : 'neutral';
        });
      }
      return next;
    });
  }, [currentShop, updateConsegnaPriorityConfig]);

  const handleApplyOuterwearConsegnaPreset = useCallback(async () => {
    const consegne = currentShop?.budget.actuals.map((entry) => entry.consegna) || [];
    const tipiFilato = currentShop?.tipoFilatoSections.map((section) => section.tipoFilato) || [];
    const pattern = /(GIACCA|GIACCHE|OUTERWEAR|PELLE|GIUBBOTTI PELLE)/i;
    await updateConsegnaPriorityConfig((current) => {
      const next = { ...current };
      for (const tf of tipiFilato.filter((name) => pattern.test(name))) {
        next[tf] = { ...(next[tf] || {}) };
        consegne.forEach((consegna, index) => {
          next[tf][consegna] = index <= 1 ? 'prioritized' : 'avoid';
        });
      }
      return next;
    });
  }, [currentShop, updateConsegnaPriorityConfig]);

  const handleConsegnaPriorityToggle = useCallback(async (enabled: boolean) => {
    const nextConfig = getLatestConfigSnapshot();
    if (!nextConfig || typeof nextConfig !== 'object') return;
    if (!nextConfig.constraints || typeof nextConfig.constraints !== 'object') nextConfig.constraints = {};
    if (!nextConfig.constraints.active_constraints || typeof nextConfig.constraints.active_constraints !== 'object') nextConfig.constraints.active_constraints = {};
    nextConfig.constraints.active_constraints.consegna_priority = enabled;
    if (!nextConfig.objective || typeof nextConfig.objective !== 'object') nextConfig.objective = {};
    if (nextConfig.objective.lambda_consegna_priority === undefined) nextConfig.objective.lambda_consegna_priority = 1;
    saveConfigDeferred(nextConfig);
  }, [getLatestConfigSnapshot, saveConfigDeferred]);

  const handleConsegnaPriorityLambdaChange = useCallback(async (value: number) => {
    if (!Number.isFinite(value) || value < 0) return;
    const nextConfig = getLatestConfigSnapshot();
    if (!nextConfig || typeof nextConfig !== 'object') return;
    if (!nextConfig.objective || typeof nextConfig.objective !== 'object') nextConfig.objective = {};
    nextConfig.objective.lambda_consegna_priority = value;
    saveConfigDeferred(nextConfig);
  }, [getLatestConfigSnapshot, saveConfigDeferred]);

  const handleSetTipoFilatoLockState = useCallback(async (tipoFilato: string, shouldLock: boolean) => {
    if (isAreaTabSelected) return;
    const targetSection = currentShop?.tipoFilatoSections.find((section) => section.tipoFilato === tipoFilato);
    if (!targetSection || !targetSection.skuRows.length) {
      return;
    }

    const nextUpperBounds = Object.fromEntries(
      targetSection.skuRows.map((row) => [row.sku, shouldLock ? Number(row.minValue) || 0 : 100000000000000000]),
    );

    updateShopData(selectedShopId, (shop) => ({
      ...shop,
      tipoFilatoSections: shop.tipoFilatoSections.map((section) =>
        section.tipoFilato === tipoFilato
          ? {
              ...section,
              skuRows: section.skuRows.map((row) => ({
                ...row,
                ubQuantity: row.ubQuantity,
                ubQuantityPerShop: shouldLock ? Number(row.minValue) || 0 : 100000000000000000,
              })),
            }
          : section,
      ),
    }));

    try {
      const nextConfig = getLatestConfigSnapshot();
      if (!nextConfig?.constraints) {
        throw new Error('Config non disponibile');
      }
      nextConfig.constraints.ub_quantity_per_shop = nextConfig.constraints.ub_quantity_per_shop || {};
      nextConfig.constraints.ub_quantity_per_shop[selectedShopId] = nextConfig.constraints.ub_quantity_per_shop[selectedShopId] || {};
      Object.entries(nextUpperBounds).forEach(([sku, upperBound]) => {
        nextConfig.constraints.ub_quantity_per_shop[selectedShopId][sku] = upperBound;
      });
      // SPJ-890: config locale, salvataggio + ricalcolo deferiti al "Salva".
      commitConfigState(nextConfig);
      pendingConfigDirtyRef.current = true;
      markUnsaved();
    } catch (err) {
      console.error('Error updating tipo filato lock state:', err);
    }
  }, [currentShop, getLatestConfigSnapshot, commitConfigState, isAreaTabSelected, selectedShopId, updateShopData]);

  const updateTipoFilatoConstraint = useCallback(async (
    tipoFilato: string,
    metric: 'economicValue' | 'items' | 'uniqueModels' | 'skuCount',
    nextUpperBound: number,
  ) => {
    if (isAreaTabSelected || !currentShop) return;

    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
    if (!threadId || !messageId) return;

    const nextSections = currentShop.tipoFilatoSections.map((section) => {
      if (section.tipoFilato !== tipoFilato) return section;
      const current = section.constraints[metric];
      // L'utente modifica la capacita' RESIDUA (quella a schermo). Sposto il grezzo dello
      // stesso delta invece di sovrascriverlo: cosi' la quota gia' consumata dalla run
      // collegata resta intatta e, se la run viene scollegata, il piano torna al suo valore.
      const delta = Number(nextUpperBound) - Number(current.upperBound ?? nextUpperBound);
      const nextRaw = current.rawUpperBound === undefined
        ? nextUpperBound
        : Math.max(0, Number(current.rawUpperBound) + delta);
      return {
        ...section,
        constraints: {
          ...section.constraints,
          [metric]: {
            ...current,
            upperBound: nextUpperBound,
            ...((metric === 'items' || metric === 'economicValue') ? { rawUpperBound: nextRaw } : {}),
          },
        },
      };
    });

    // SPJ-890: applica il vincolo tipo filato in locale; il payload viene ricostruito e salvato al "Salva".
    updateShopData(selectedShopId, (shop) => ({ ...shop, tipoFilatoSections: nextSections }));
    pendingMpShopsRef.current.add(selectedShopId);
    markUnsaved();
  }, [currentShop, isAreaTabSelected, selectedShopId, updateShopData, thread.threadData, thread.threadId]);

  const transferTipoFilatoConstraint = useCallback(async () => {
    if (isAreaTabSelected || !currentShop || !transferModalState) return;

    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
    if (!threadId || !messageId) return;

    const sourceSection = currentShop.tipoFilatoSections.find((section) => section.tipoFilato === transferModalState.sourceTipoFilato);
    const targetSection = currentShop.tipoFilatoSections.find((section) => section.tipoFilato === transferModalState.targetTipoFilato);
    const metric = transferModalState.metric;
    const amount = Number(transferModalState.amount);

    const fail = (message: string) => {
      setTransferModalState((prev) => (prev ? { ...prev, error: message } : prev));
    };

    if (!sourceSection || !targetSection) {
      fail('Tipo filato sorgente o destinazione non trovato.');
      return;
    }
    if (sourceSection.tipoFilato === targetSection.tipoFilato) {
      fail('Scegli un tipo filato di destinazione diverso da quello di partenza.');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      fail('Inserisci un importo positivo da spostare.');
      return;
    }
    if (metric === 'items' && !Number.isInteger(amount)) {
      fail('Per i pezzi inserisci un numero intero.');
      return;
    }
    if (!hasTipoFilatoMetricConstraint(sourceSection, metric)) {
      fail('Il tipo filato di partenza non ha un vincolo definito per questa metrica.');
      return;
    }
    if (!hasTipoFilatoMetricConstraint(targetSection, metric)) {
      fail('Il tipo filato di destinazione non ha un vincolo definito per questa metrica.');
      return;
    }

    const sourceCurrent = Number(sourceSection.constraints[metric].upperBound ?? 0);
    const targetCurrent = Number(targetSection.constraints[metric].upperBound ?? 0);
    if (amount > sourceCurrent) {
      fail(`Puoi spostare al massimo ${formatTransferMetricValue(metric, sourceCurrent)}.`);
      return;
    }

    // Lo spostamento va applicato anche alla capacita' grezza, che e' quella che viene
    // salvata: muovere solo la residua farebbe riscrivere il vecchio grezzo al "Salva",
    // annullando il trasferimento.
    const shiftBounds = (metric_: ConstraintMetric, delta: number): ConstraintMetric => ({
      ...metric_,
      upperBound: Number(metric_.upperBound ?? 0) + delta,
      ...(metric_.rawUpperBound === undefined
        ? {}
        : { rawUpperBound: Math.max(0, Number(metric_.rawUpperBound) + delta) }),
    });

    const nextSections = currentShop.tipoFilatoSections.map((section) => {
      if (section.tipoFilato === sourceSection.tipoFilato) {
        return {
          ...section,
          constraints: {
            ...section.constraints,
            [metric]: shiftBounds(section.constraints[metric], -amount),
          },
        };
      }
      if (section.tipoFilato === targetSection.tipoFilato) {
        return {
          ...section,
          constraints: {
            ...section.constraints,
            [metric]: shiftBounds(section.constraints[metric], amount),
          },
        };
      }
      return section;
    });

    const beforeTotal = currentShop.tipoFilatoSections.reduce((sum, section) => sum + (Number(section.constraints[metric].upperBound ?? 0) || 0), 0);
    const afterTotal = nextSections.reduce((sum, section) => sum + (Number(section.constraints[metric].upperBound ?? 0) || 0), 0);
    if (Math.abs(beforeTotal - afterTotal) > 0.0001) {
      fail('Il totale non risulta invariato. Operazione annullata.');
      return;
    }

    // SPJ-890: applica il trasferimento vincolo in locale; il payload viene ricostruito e salvato al "Salva".
    updateShopData(selectedShopId, (shop) => ({ ...shop, tipoFilatoSections: nextSections }));
    pendingMpShopsRef.current.add(selectedShopId);
    markUnsaved();
    setTransferModalState(null);
  }, [currentShop, isAreaTabSelected, selectedShopId, updateShopData, thread.threadData, thread.threadId, transferModalState]);

  const updateBudgetConstraint = useCallback(async (
    consegna: string,
    metric: 'value' | 'items',
    nextValue: number,
  ) => {
    if (isAreaTabSelected) return;
    const nextShop = updateShopData(selectedShopId, (shop) => ({
      ...shop,
      budget: {
        ...shop.budget,
        upperBounds: {
          ...shop.budget.upperBounds,
          [consegna]: {
            ...shop.budget.upperBounds[consegna],
            [metric]: nextValue,
          },
        },
      },
    }));

    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
    if (!threadId || !messageId || !nextShop) return;

    // SPJ-890: applica in locale (updateShopData sopra); il payload budget viene ricostruito e salvato al "Salva".
    pendingBudgetShopsRef.current.add(selectedShopId);
    markUnsaved();
  }, [isAreaTabSelected, selectedShopId, thread.threadData, thread.threadId, updateShopData]);

  const parseSkuText = useCallback((text: string): string[] => {
    if (!text || !text.trim()) return [];
    return text
      .split(/[\n,\s]+/)
      // Le SKU a catalogo sono tutte maiuscole: normalizziamo qui, che e' il punto in cui
      // la lista incollata diventa la lista mandata al backend. Senza, una sku minuscola
      // finisce fra le `missing_skus` e la review la fa sparire senza spiegare il perche'.
      .map((sku) => sku.trim().toUpperCase())
      .filter((sku) => sku.length > 0);
  }, []);

  const buildSkuValidationReview = useCallback((skus: string[], validation: any, targetShopIds: string[]): SkuValidationReview => {
    const missingSkus = new Set((Array.isArray(validation?.missing_skus) ? validation.missing_skus : []).map((sku: unknown) => String(sku)));
    const wrongGenderBySku = new Map(
      (Array.isArray(validation?.wrong_gender) ? validation.wrong_gender : [])
        .map((issue: any) => [String(issue?.sku ?? ''), issue])
        .filter(([sku]) => sku),
    );
    const wrongSeasonBySku = new Map(
      (Array.isArray(validation?.wrong_season) ? validation.wrong_season : [])
        .map((issue: any) => [String(issue?.sku ?? ''), issue])
        .filter(([sku]) => sku),
    );
    const hasExplicitValidList = Array.isArray(validation?.valid_skus);
    const validSkus = new Set((hasExplicitValidList ? validation.valid_skus : []).map((sku: unknown) => String(sku)));

    return {
      targetShopIds,
      rows: skus.map((sku) => {
        const issues: string[] = [];
        if (missingSkus.has(sku)) {
          issues.push('SKU non trovato');
        }
        const wrongGender = wrongGenderBySku.get(sku);
        if (wrongGender) {
          issues.push(`Linea errata: ${wrongGender.actual ?? 'NA'} ≠ ${wrongGender.expected ?? 'attesa'}`);
        }
        const wrongSeason = wrongSeasonBySku.get(sku);
        if (wrongSeason) {
          issues.push(`Stagione errata: ${wrongSeason.actual ?? 'NA'} ≠ ${wrongSeason.expected ?? 'attesa'}`);
        }
        if (hasExplicitValidList && !validSkus.has(sku) && issues.length === 0) {
          issues.push('SKU non valido');
        }

        return {
          sku,
          valid: issues.length === 0,
          issues,
        };
      }).sort((a, b) => Number(a.valid) - Number(b.valid)),
    };
  }, []);

  const uploadSkuListToTargetShops = useCallback(async (validSkus: string[], targetShopIds: string[]) => {
    if (!validSkus.length) {
      throw new Error('Nessuno SKU valido da caricare.');
    }

    await runSerializedMutation(async () => {
      for (const shopId of targetShopIds) {
        await uploadSkuListForShopNow(shopId, validSkus, defaultSkuListMinValue);
      }
      await refreshShopSkuContext();
    });
    const targetShopLabels = targetShopIds.map((shopId) => pageDataRef.current.byShop[shopId]?.shopLabel || shopId);
    setSkuListMessage(`${validSkus.length} SKU aggiunti con successo su ${targetShopLabels.join(', ')}`);
    setPastedSkuText('');
    setShowSkuTextArea(false);
  }, [defaultSkuListMinValue, refreshShopSkuContext, runSerializedMutation, uploadSkuListForShopNow]);

  const handleImportFixedTaglieExcel = useCallback(async (file: File) => {
    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
    if (!threadId || !messageId) {
      setSkuListError('Thread o messaggio non disponibili.');
      return;
    }

    const codStagione = String(
      (configDraft as any)?.cod_stagione
        ?? (configDraft as any)?.season
        ?? (configDetails as any)?.cod_stagione
        ?? (configDetails as any)?.season
        ?? appState?.['optim-season']
        ?? '',
    ).trim();
    if (!codStagione) {
      setSkuListError('Stagione non disponibile per ricostruire gli SKU dall\'Excel.');
      return;
    }

    setIsUploadingSkuList(true);
    setSkuListError(null);
    setSkuListMessage(null);
    setSkuValidationReview(null);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheetName = workbook.SheetNames.find((name) => name.trim().toLowerCase() === 'totale') || workbook.SheetNames[0];
      const ws = workbook.Sheets[sheetName];
      if (!ws) throw new Error('Foglio Excel non trovato.');

      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:A1');
      const headerRow = findExcelHeaderRow(ws);
      const labelToShopId = new Map<string, string>();
      pageDataRef.current.shops.forEach((shop) => {
        labelToShopId.set(shop.id.trim().toLowerCase(), shop.id);
        labelToShopId.set(shop.label.trim().toLowerCase(), shop.id);
      });

      const rawRows: Array<{ shopId: string; sku: string; row: number; blockStartCol: number }> = [];
      for (let blockStartCol = EXCEL_BASE_BLOCK_START_COL; blockStartCol <= range.e.c + 1; blockStartCol += EXCEL_BLOCK_WIDTH_COLS) {
        const blockLabel = normalizeExcelToken(excelCellValue(ws, 1, blockStartCol));
        if (!blockLabel) continue;
        const shopId = labelToShopId.get(blockLabel.trim().toLowerCase()) || blockLabel.trim();
        if (shopId === AREA_TAB_ID) continue;

        for (let row = headerRow + 1; row <= range.e.r + 1; row += 1) {
          const tfCell = normalizeExcelToken(excelCellValue(ws, row, 3)).toUpperCase();
          if (tfCell === 'TOTALISSIMO') break;
          const model = normalizeExcelToken(excelCellValue(ws, row, 5)).toUpperCase();
          const color = normalizeExcelToken(excelCellValue(ws, row, 6)).toUpperCase();
          if (!model || !color) continue;
          const totalQuantity = Number(excelCellValue(ws, row, blockStartCol + 17) || 0);
          if (!Number.isFinite(totalQuantity) || totalQuantity <= 0) continue;
          rawRows.push({ shopId, sku: `${codStagione}${model}${color}`, row, blockStartCol });
        }
      }

      if (!rawRows.length) throw new Error('Nessuna riga SKU/taglie trovata nel foglio Totale.');

      const skuList = Array.from(new Set(rawRows.map((row) => row.sku)));
      const completeExportInfoRaw = await performAction({
        threadId,
        messageId,
        actionName: 'add-missing-information-for-export',
        params: { 'sku-list': skuList },
      }) as Array<{ sku?: string; cod_tipo_taglia?: string | null }> | null;
      const infoBySku = new Map((completeExportInfoRaw || []).map((item) => [String(item?.sku || '').trim(), item]));
      const missingSkus = skuList.filter((sku) => !infoBySku.has(sku));
      if (missingSkus.length > 0) {
        throw new Error(`SKU non trovati a sistema: ${missingSkus.slice(0, 20).join(', ')}${missingSkus.length > 20 ? '…' : ''}`);
      }

      const codTipoTagliaList = Array.from(new Set(
        skuList
          .map((sku) => normalizeExcelCodTipoTaglia(infoBySku.get(sku)?.cod_tipo_taglia))
          .filter(Boolean),
      ));
      const taglieInfoRaw = await performAction({
        threadId,
        messageId,
        actionName: 'get-taglie-info',
        params: { cod_tipo_taglia_list: codTipoTagliaList },
      }) as Array<{ cod_tipo_taglia?: string; i_arr?: number | null; error?: string | null }> | null;
      const taglieInfoByCod = new Map(
        (taglieInfoRaw || [])
          .filter((entry) => !entry?.error)
          .map((entry) => [normalizeExcelCodTipoTaglia(entry?.cod_tipo_taglia), entry]),
      );

      const shopsPayload: Record<string, Record<string, { quantity: number; cod_tipo_taglia: string; taglie: Record<string, number>; source: string }>> = {};
      rawRows.forEach((raw) => {
        const codTipoTaglia = normalizeExcelCodTipoTaglia(infoBySku.get(raw.sku)?.cod_tipo_taglia);
        const taglieInfo = taglieInfoByCod.get(codTipoTaglia);
        const iArr = Number(taglieInfo?.i_arr);
        if (!codTipoTaglia || !Number.isFinite(iArr)) {
          throw new Error(`Codice tipo taglia non risolto per SKU ${raw.sku}`);
        }
        const legendRow = findExcelSizeLegendRow(ws, raw.blockStartCol, headerRow, iArr);
        if (!legendRow) {
          throw new Error(`Riga griglia taglie i_arr=${iArr} non trovata per SKU ${raw.sku}`);
        }

        const taglie: Record<string, number> = {};
        for (let offset = 0; offset < EXCEL_MAX_SIZE_COLS; offset += 1) {
          const size = normalizeExcelToken(excelCellValue(ws, legendRow, raw.blockStartCol + 1 + offset));
          const quantity = Number(excelCellValue(ws, raw.row, raw.blockStartCol + 1 + offset) || 0);
          if (!size || !Number.isFinite(quantity) || quantity <= 0) continue;
          taglie[size] = (taglie[size] || 0) + Math.trunc(quantity);
        }
        const taglieTotal = Object.values(taglie).reduce((sum, qty) => sum + qty, 0);
        if (taglieTotal <= 0) return;

        shopsPayload[raw.shopId] = shopsPayload[raw.shopId] || {};
        shopsPayload[raw.shopId][raw.sku] = {
          quantity: taglieTotal,
          cod_tipo_taglia: codTipoTaglia,
          taglie,
          source: `excel:${file.name}`,
        };
      });

      const importedCount = Object.values(shopsPayload).reduce((sum, skuMap) => sum + Object.keys(skuMap).length, 0);
      if (importedCount <= 0) throw new Error('Nessuna quantità taglia positiva trovata nell\'Excel.');

      await runSerializedMutation(async () => {
        const nextConfig = await performAction({
          threadId,
          messageId,
          actionName: 'optim-import-fixed-sku-taglie',
          params: {
            'optim-fixed-sku-taglie-shops': shopsPayload,
            'optim-clear-existing-shop-bounds': true,
            'optim-import-source': `excel:${file.name}`,
          },
        });
        if (nextConfig && typeof nextConfig === 'object') {
          commitConfigState(nextConfig);
        }
        await refreshShopSkuContext();
      });

      setSkuListMessage(`Import Excel completato: ${importedCount} SKU con quantità min=max e taglie preservate.`);
    } catch (err) {
      console.error('Error importing fixed taglie Excel:', err);
      setSkuListError(err instanceof Error ? err.message : 'Errore durante import Excel taglie.');
    } finally {
      setIsUploadingSkuList(false);
    }
  }, [appState, commitConfigState, configDetails, configDraft, refreshShopSkuContext, runSerializedMutation, thread.threadData, thread.threadId]);

  const handleAddSkuList = useCallback(async () => {
    const skus = parseSkuText(pastedSkuText);
    if (!skus.length) {
      setSkuListError('Nessuno SKU valido trovato nel testo inserito');
      return;
    }

    setSkuListError(null);
    setSkuListMessage(null);
    setSkuValidationReview(null);

    if (!skuTargetShopIds.length) {
      setSkuListError('Seleziona almeno uno shop destinazione');
      return;
    }

    setIsUploadingSkuList(true);

    try {
      const targetShopIds = skuTargetShopIds.filter((shopId) => shopId !== AREA_TAB_ID);
      if (!targetShopIds.length) {
        setSkuListError('Seleziona almeno uno shop destinazione valido');
        return;
      }

      const validation = await validateSkuList(skus) as any;
      if (!validation?.valid) {
        setSkuValidationReview(buildSkuValidationReview(skus, validation, targetShopIds));
        return;
      }

      const validSkus = Array.isArray(validation?.valid_skus) && validation.valid_skus.length > 0 ? validation.valid_skus : skus;
      await uploadSkuListToTargetShops(validSkus, targetShopIds);
    } catch (err) {
      console.error('Error adding merchandising plan SKU list:', err);
      setSkuListError(err instanceof Error ? err.message : 'Errore durante il salvataggio della lista SKU');
    } finally {
      setIsUploadingSkuList(false);
    }
  }, [buildSkuValidationReview, parseSkuText, pastedSkuText, skuTargetShopIds, uploadSkuListToTargetShops, validateSkuList]);

  const handleRemoveSkuFromValidationReview = useCallback((rowIndex: number) => {
    setSkuValidationReview((prev) => {
      if (!prev) return prev;
      const nextRows = prev.rows.filter((_, index) => index !== rowIndex);
      setPastedSkuText(nextRows.map((row) => row.sku).join('\n'));
      return { ...prev, rows: nextRows };
    });
  }, []);

  const handleRemoveInvalidSkusFromValidationReview = useCallback(() => {
    setSkuValidationReview((prev) => {
      if (!prev) return prev;
      const nextRows = prev.rows.filter((row) => row.valid);
      setPastedSkuText(nextRows.map((row) => row.sku).join('\n'));
      return { ...prev, rows: nextRows };
    });
  }, []);

  const handleProceedWithSkuValidationReview = useCallback(async () => {
    if (!skuValidationReview) return;
    const validSkus = skuValidationReview.rows.filter((row) => row.valid).map((row) => row.sku);
    if (!validSkus.length) {
      setSkuListError('Nessuno SKU valido da caricare.');
      return;
    }

    setIsUploadingSkuList(true);
    setSkuListError(null);
    setSkuListMessage(null);
    try {
      await uploadSkuListToTargetShops(validSkus, skuValidationReview.targetShopIds);
      setSkuValidationReview(null);
    } catch (err) {
      console.error('Error proceeding with corrected SKU list:', err);
      setSkuListError(err instanceof Error ? err.message : 'Errore durante il salvataggio della lista SKU corretta');
    } finally {
      setIsUploadingSkuList(false);
    }
  }, [skuValidationReview, uploadSkuListToTargetShops]);

  const handleClearAllSkuBounds = useCallback(async () => {
    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
    if (!threadId || !messageId) {
      setSkuListError('Thread o messaggio non disponibili.');
      return;
    }
    if (!window.confirm('Rimuovere tutti gli SKU dalla lista e cancellare tutti i vincoli LB/UB SKU?')) {
      return;
    }

    setIsClearingSkuBounds(true);
    setSkuListError(null);
    setSkuListMessage(null);
    setSkuValidationReview(null);
    try {
      await runSerializedMutation(async () => {
        const nextConfig = await performAction({
          threadId,
          messageId,
          actionName: 'optim-clear-sku-bounds',
          params: {},
        });
        if (nextConfig && typeof nextConfig === 'object') {
          commitConfigState(nextConfig);
        }
        setSelectedSkuKeys({});
        setSkuMinDrafts({});
        await refreshShopSkuContext();
      });
      setSkuListMessage('Tutti gli SKU e i vincoli LB/UB SKU sono stati rimossi.');
    } catch (err) {
      console.error('Error clearing SKU bounds:', err);
      setSkuListError(err instanceof Error ? err.message : 'Errore durante la rimozione degli SKU.');
    } finally {
      setIsClearingSkuBounds(false);
    }
  }, [commitConfigState, refreshShopSkuContext, runSerializedMutation, thread.threadData, thread.threadId]);

  const handleOptimizationScopeModeChange = useCallback(async (mode: 'master' | 'lista') => {
    if (mode === optimizationScopeMode) {
      return;
    }

    const nextConfig = getLatestConfigSnapshot();
    if (!nextConfig || typeof nextConfig !== 'object') {
      return;
    }
    if (!nextConfig.constraints || typeof nextConfig.constraints !== 'object') {
      nextConfig.constraints = {};
    }

    nextConfig.constraints.default_ub_quantity = mode === 'lista' ? 0 : 100000000000000000;
    setOptimizationScopeMode(mode);

    // Solo stato locale + "modifiche non salvate": verra' persistito al click su "Salva".
    commitConfigState(nextConfig);
    markUnsaved();
  }, [getLatestConfigSnapshot, optimizationScopeMode, commitConfigState, markUnsaved]);

  const handleLaunchOptimization = useCallback(async () => {
    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
    if (!threadId || !messageId || isOptimizing || isLaunchDisabled) {
      return;
    }

    setOptimizationError(null);
    setIsOptimizing(true);
    setOptimizationProgress(0);

    try {
      await performAction({
        threadId,
        messageId,
        actionName: 'optim-launch-optimization',
        params: {},
      });

      const startTime = Date.now();
      const totalDuration = 120000;
      const pollInterval = 2000;
      let optimizationComplete = false;

      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / totalDuration) * 100, 95);
        if (progress > 85) {
          const slowProgress = 85 + ((progress - 85) * 0.5);
          setOptimizationProgress(Math.floor(slowProgress));
        } else {
          setOptimizationProgress(Math.floor(progress));
        }
      }, 500);

      const pollOptimization = async (): Promise<void> => {
        try {
          const pollResult = await performAction({
            threadId,
            messageId,
            actionName: 'optim-poll-optimization',
            params: {},
          });

          const status = (pollResult as any)?.status;
          const solverStatus = (pollResult as any)?.metrics?.status;
          const message = (pollResult as any)?.message;

          if (status === 'failed') {
            optimizationComplete = true;
            clearInterval(progressInterval);
            setIsOptimizing(false);
            setOptimizationProgress(0);
            setOptimizationError(message || 'Ottimizzazione fallita. Rivedi i vincoli e riprova.');
            return;
          }

          if (status === 'succeeded') {
            optimizationComplete = true;
            clearInterval(progressInterval);
            setOptimizationProgress(100);

            if (solverStatus === 'INFEASIBLE') {
              setIsOptimizing(false);
              setOptimizationProgress(0);
              setOptimizationError(message || 'Il problema di ottimizzazione è infeasible. Rivedi i vincoli e riprova.');
              return;
            }

            await new Promise((resolve) => setTimeout(resolve, 500));
            await dispatch(fetchThread()).unwrap();
            setIsOptimizing(false);
            setOptimizationProgress(0);
            onOptimizationLaunched?.();
            return;
          }

          if (!optimizationComplete) {
            setTimeout(pollOptimization, pollInterval);
          }
        } catch (pollErr) {
          console.error('Error during optimization polling:', pollErr);
          if (!optimizationComplete) {
            setTimeout(pollOptimization, pollInterval);
          }
        }
      };

      setTimeout(pollOptimization, pollInterval);
    } catch (err) {
      console.error('Error launching optimization:', err);
      setOptimizationError(err instanceof Error ? err.message : 'Errore durante l\'ottimizzazione');
      setIsOptimizing(false);
      setOptimizationProgress(0);
    }
  }, [dispatch, isLaunchDisabled, isOptimizing, onOptimizationLaunched, thread.threadData, thread.threadId]);

  const showInitialLoader = (entryMode === 'from-result' || activeTab !== 'input-data') && (isLoadingConfig || isLoadingContext || isPreparingFromResult) && !currentShop;
  const showNoDataState = (entryMode === 'from-result' || activeTab !== 'input-data') && !showInitialLoader && !currentShop;

  return (
    <>
      {transferModalState && (
        <div className="fixed inset-0 z-[1000] flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6 sm:py-8">
          <div className="flex max-h-[calc(100vh-3rem)] w-full max-w-2xl flex-col border border-gray-300 bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-200 bg-white p-5 pb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Sposta budget / pezzi</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Ridistribuisci un vincolo tra due tipi filato dello stesso negozio. Il totale del merchandising plan resta invariato.
                </p>
              </div>
              <button
                type="button"
                className="text-xl leading-none text-gray-500 hover:text-black disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => setTransferModalState(null)}
                disabled={isTransferSaving}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
              <div className="border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                <span className="font-semibold text-gray-900">Da:</span> {transferModalState.sourceTipoFilato}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">Cosa spostare</span>
                  <select
                    value={transferModalState.metric}
                    onChange={(event) => {
                      const metric = event.target.value as TipoFilatoTransferMetric;
                      const firstTarget = getTipoFilatoTransferTargets(transferModalState.sourceTipoFilato, metric)[0]?.tipoFilato || '';
                      setTransferModalState((prev) => prev ? { ...prev, metric, targetTipoFilato: firstTarget, amount: '', error: null } : prev);
                    }}
                    disabled={isTransferSaving}
                    className="w-full border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-black disabled:bg-gray-100"
                  >
                    <option value="items">Pezzi</option>
                    <option value="economicValue">Valore economico</option>
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">Quantità da spostare</span>
                  <input
                    type="number"
                    min={0}
                    step={transferModalState.metric === 'items' ? 1 : 0.01}
                    value={transferModalState.amount}
                    onChange={(event) => setTransferModalState((prev) => prev ? { ...prev, amount: event.target.value, error: null } : prev)}
                    placeholder={transferModalState.metric === 'items' ? 'Es. 30' : 'Es. 10000'}
                    disabled={isTransferSaving}
                    className="w-full border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-black disabled:bg-gray-100"
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">A tipo filato</span>
                <select
                  value={transferModalState.targetTipoFilato}
                  onChange={(event) => setTransferModalState((prev) => prev ? { ...prev, targetTipoFilato: event.target.value, error: null } : prev)}
                  disabled={isTransferSaving || transferTargetOptions.length === 0}
                  className="w-full border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-black disabled:bg-gray-100"
                >
                  {transferTargetOptions.map((section) => (
                    <option key={section.tipoFilato} value={section.tipoFilato}>{section.tipoFilato}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Sono disponibili solo tipi filato con vincolo definito per la metrica selezionata. Il valore 0 è valido e può ricevere quantità.
                </p>
              </label>

              {transferPreview && (
                <div className="grid gap-3 rounded border border-gray-200 bg-white p-3 text-sm sm:grid-cols-2">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Partenza</div>
                    <div className="mt-1 font-medium text-gray-900">{transferModalState.sourceTipoFilato}</div>
                    <div className="mt-1 text-gray-600">
                      {formatTransferMetricValue(transferModalState.metric, transferPreview.sourceCurrent)} → {formatTransferMetricValue(transferModalState.metric, transferPreview.sourceNext)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Destinazione</div>
                    <div className="mt-1 font-medium text-gray-900">{transferModalState.targetTipoFilato || 'Seleziona destinazione'}</div>
                    <div className="mt-1 text-gray-600">
                      {formatTransferMetricValue(transferModalState.metric, transferPreview.targetCurrent)} → {formatTransferMetricValue(transferModalState.metric, transferPreview.targetNext)}
                    </div>
                  </div>
                </div>
              )}

              {transferModalState.error && (
                <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {transferModalState.error}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 z-10 flex justify-end gap-2 border-t border-gray-200 bg-white p-5">
              <button
                type="button"
                className="border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:border-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => setTransferModalState(null)}
                disabled={isTransferSaving}
              >
                Annulla
              </button>
              <button
                type="button"
                className={`border px-4 py-2 text-sm font-semibold ${transferCanSubmit ? 'border-black bg-black text-white hover:bg-gray-800' : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'}`}
                disabled={!transferCanSubmit}
                onClick={() => {
                  void transferTipoFilatoConstraint();
                }}
              >
                {isTransferSaving ? 'Salvataggio…' : 'Sposta'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showImportOptimizationRunModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-xl border border-gray-300 bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Importa da ottimizzazione precedente</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Copia i vincoli SKU dalla run selezionata nella configurazione derivata. Usa “Importa” per il comportamento storico solo quantità, oppure “Importa lock” per preservare anche le taglie.
                </p>
              </div>
              <button
                type="button"
                className="text-xl leading-none text-gray-500 hover:text-black"
                onClick={() => setShowImportOptimizationRunModal(false)}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <div className="mb-4 border border-gray-200 bg-gray-50 p-3">
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
                ID run ottimizzazione da importare
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={manualImportOptimizationRunIdDraft}
                onChange={(event) => setManualImportOptimizationRunIdDraft(event.target.value)}
                placeholder="Es. 80"
                className="mt-2 w-full border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-black"
              />
              <p className="mt-1 text-xs text-gray-500">
                Usa questo campo solo se la run sorgente non è già stata risolta automaticamente dal contesto.
              </p>
            </div>

            {importError && (
              <div className="mb-4 border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {importError}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:border-gray-900"
                onClick={() => setShowImportOptimizationRunModal(false)}
              >
                Annulla
              </button>
              <button
                type="button"
                className={`border px-4 py-2 text-sm font-semibold ${parseOptionalNumericId(manualImportOptimizationRunIdDraft) === null || isImportingOptimization ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400' : 'border-blue-700 bg-blue-50 text-blue-900 hover:bg-blue-100'}`}
                disabled={parseOptionalNumericId(manualImportOptimizationRunIdDraft) === null || isImportingOptimization}
                onClick={() => {
                  void handleImportFromOptimization(parseOptionalNumericId(manualImportOptimizationRunIdDraft), { lockTaglie: false });
                }}
              >
                {isImportingOptimization && !importOptimizationLockTaglieDraft ? 'Importazione…' : 'Importa'}
              </button>
              <button
                type="button"
                className={`border px-4 py-2 text-sm font-semibold ${parseOptionalNumericId(manualImportOptimizationRunIdDraft) === null || isImportingOptimization ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400' : 'border-black bg-black text-white hover:bg-gray-800'}`}
                disabled={parseOptionalNumericId(manualImportOptimizationRunIdDraft) === null || isImportingOptimization}
                onClick={() => {
                  void handleImportFromOptimization(parseOptionalNumericId(manualImportOptimizationRunIdDraft), { lockTaglie: true });
                }}
              >
                {isImportingOptimization && importOptimizationLockTaglieDraft ? 'Importazione…' : 'Importa lock'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showLinkedOrderRunModal && (
        <div className="fixed inset-0 z-[1000] flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6 sm:py-8">
          <div className="flex max-h-[calc(100vh-3rem)] w-full max-w-3xl flex-col border border-gray-300 bg-white shadow-2xl sm:max-h-[calc(100vh-4rem)]">
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-200 bg-white p-5 pb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Usa run precedente come ordini già piazzati</h3>
                <p className="mt-1 text-sm text-gray-600">
                  La run selezionata verrà usata come ordini già piazzati per scalare domanda e condizionare le taglie. Il budget non verrà modificato.
                </p>
              </div>
              <button
                type="button"
                className="text-xl leading-none text-gray-500 hover:text-black"
                onClick={() => setShowLinkedOrderRunModal(false)}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
            <div className="mb-4 border border-gray-200 bg-gray-50 p-3">
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
                ID run da usare come ordini già piazzati
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={manualLinkedOrderRunIdDraft}
                onChange={(event) => {
                  setManualLinkedOrderRunIdDraft(event.target.value);
                  setSelectedLinkedOrderRunId(null);
                }}
                placeholder="Es. 74"
                className="mt-2 w-full border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-black"
              />
              <p className="mt-1 text-xs text-gray-500">
                Puoi inserire direttamente una run id: dataset, config e dettagli baseline vengono risolti dal backend partendo dalla run.
              </p>
            </div>

            <label className="mb-4 flex cursor-pointer items-start gap-3 border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
              <input
                type="checkbox"
                className="mt-1"
                checked={scaleLinkedRunMp}
                onChange={(event) => setScaleLinkedRunMp(event.target.checked)}
              />
              <span>
                <strong>Scala Merchandising Plan</strong>
                <span className="mt-1 block text-xs text-amber-800">
                  Se attivo riduce solo items e valore del MP residuo. Il budget resta invariato.
                </span>
              </span>
            </label>

            {isLoadingLinkableOrderRuns ? (
              <div className="border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">Caricamento run compatibili…</div>
            ) : linkedOrderRunError ? (
              <div className="border border-red-200 bg-red-50 p-4 text-sm text-red-700">{linkedOrderRunError}</div>
            ) : linkableOrderRuns.length === 0 ? (
              <div className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                Nessuna run compatibile precaricata. Inserisci manualmente la run id da usare come baseline ordini.
              </div>
            ) : (
              <div className="max-h-[min(360px,45vh)] overflow-y-auto border border-gray-200">
                {linkableOrderRuns.map((run) => {
                  const runId = Number(run.run_id);
                  const selected = selectedLinkedOrderRunId === runId;
                  const startedAt = run.started_at ? new Date(run.started_at).toLocaleString('it-IT') : 'Data non disponibile';
                  const shops = Array.isArray(run.shops) ? run.shops.filter(Boolean).join(', ') : '';
                  return (
                    <label
                      key={runId}
                      className={`flex cursor-pointer items-start gap-3 border-b border-gray-100 p-3 transition last:border-b-0 ${selected ? 'bg-amber-50' : 'bg-white hover:bg-gray-50'}`}
                    >
                      <input
                        type="radio"
                        name="linked-order-run"
                        checked={selected}
                        onChange={() => {
                          setSelectedLinkedOrderRunId(runId);
                          setManualLinkedOrderRunIdDraft(String(runId));
                        }}
                        className="mt-1"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-gray-900">Run #{runId}</span>
                          <span className="border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-semibold uppercase text-green-700">{run.status || 'succeeded'}</span>
                          {shops && <span className="text-xs text-gray-500">Shop: {shops}</span>}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">{startedAt}</div>
                        <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-700">
                          <span>Pezzi: <strong>{Number(run.total_quantity ?? 0).toLocaleString('it-IT')}</strong></span>
                          <span>Valore: <strong>€ {Math.round(Number(run.total_value ?? 0)).toLocaleString('it-IT')}</strong></span>
                          {run.config_id !== undefined && <span>Config: <strong>{run.config_id}</strong></span>}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            </div>

            <div className="sticky bottom-0 z-10 flex justify-end gap-2 border-t border-gray-200 bg-white p-5">
              <button
                type="button"
                className="border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:border-gray-900"
                onClick={() => setShowLinkedOrderRunModal(false)}
              >
                Annulla
              </button>
              <button
                type="button"
                className={`border px-4 py-2 text-sm font-semibold ${(selectedLinkedOrderRunId === null && parseOptionalNumericId(manualLinkedOrderRunIdDraft) === null) || isLinkingOrderRun ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400' : 'border-amber-700 bg-amber-600 text-white hover:bg-amber-700'}`}
                disabled={(selectedLinkedOrderRunId === null && parseOptionalNumericId(manualLinkedOrderRunIdDraft) === null) || isLinkingOrderRun}
                onClick={() => {
                  void handleLinkSelectedRunAsOrders();
                }}
              >
                {isLinkingOrderRun ? 'Collegamento…' : 'Usa come ordini già piazzati'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="min-h-full bg-white px-6 pb-6 pt-0">
        <div className="fixed left-0 right-28 top-[60px] z-20 border-b border-gray-200 bg-white px-6 pb-0 pt-3">
          <div className="flex items-end gap-3 overflow-visible whitespace-nowrap">
            <div className="flex flex-none items-center gap-2" role="tablist" aria-label="Tabs merchandising plan">
              {entryMode === 'from-result' && projectHasRuns && (
                <div className="relative -mb-px flex items-center">
                  <button
                    type="button"
                    onClick={onBackToOptimization}
                    className="group flex flex-row items-center gap-x-1 px-4 py-3 text-sm font-semibold text-gray-500 transition-colors hover:text-black"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out group-hover:motion-safe:-translate-x-0.5 motion-reduce:transform-none"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L9.06 10l3.71 3.71a.75.75 0 11-1.06 1.06l-4.24-4.24a.75.75 0 010-1.06l4.24-4.24a.75.75 0 011.08.02z" clipRule="evenodd" />
                    </svg>
                    <span>Torna all&apos;ottimizzazione</span>
                  </button>
                </div>
              )}
              {entryMode === 'new-optimization' && (
                <div className="relative -mb-px flex items-center">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'input-data'}
                    onClick={() => setActiveTab('input-data')}
                    className={`flex flex-row items-center gap-x-1 px-4 py-3 text-sm font-semibold transition-colors ${
                      activeTab === 'input-data'
                        ? 'border-b-2 border-black text-black'
                        : 'border-b-2 border-transparent text-gray-500 hover:text-black'
                    }`}
                  >
                    Input Data
                  </button>
                </div>
              )}
              <div className="relative -mb-px flex items-center">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'perimetro-budget'}
                  onClick={() => canAccessEditorTabs && setActiveTab('perimetro-budget')}
                  disabled={!canAccessEditorTabs}
                  className={`flex flex-row items-center gap-x-1 px-4 py-3 text-sm font-semibold transition-colors ${
                    activeTab === 'perimetro-budget'
                      ? 'border-b-2 border-black text-black'
                      : 'border-b-2 border-transparent text-gray-500 hover:text-black'
                  } ${!canAccessEditorTabs ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  Perimetro &amp; Budget
                  {!canAccessEditorTabs && (
                    <div className="relative ml-0.5 group">
                      <div className="flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-gray-300 text-xs text-gray-400">
                        ?
                      </div>
                      <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-800 px-3 py-2 text-xs text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                        {entryMode === 'from-result' ? 'Attendi il caricamento della configurazione' : 'Completa prima Input Data'}
                        <div className="absolute bottom-full left-1/2 h-0 w-0 -translate-x-1/2 border-b-4 border-l-4 border-r-4 border-b-gray-800 border-l-transparent border-r-transparent"></div>
                      </div>
                    </div>
                  )}
                </button>
              </div>
              <div className="relative -mb-px flex items-center">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'vincoli'}
                  onClick={() => canAccessEditorTabs && setActiveTab('vincoli')}
                  disabled={!canAccessEditorTabs}
                  className={`flex flex-row items-center gap-x-1 px-4 py-3 text-sm font-semibold transition-colors ${
                    activeTab === 'vincoli'
                      ? 'border-b-2 border-black text-black'
                      : 'border-b-2 border-transparent text-gray-500 hover:text-black'
                  } ${!canAccessEditorTabs ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  Vincoli
                  {!canAccessEditorTabs && (
                    <div className="relative ml-0.5 group">
                      <div className="flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-gray-300 text-xs text-gray-400">
                        ?
                      </div>
                      <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-800 px-3 py-2 text-xs text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                        {entryMode === 'from-result' ? 'Attendi il caricamento della configurazione' : 'Completa prima Input Data'}
                        <div className="absolute bottom-full left-1/2 h-0 w-0 -translate-x-1/2 border-b-4 border-l-4 border-r-4 border-b-gray-800 border-l-transparent border-r-transparent"></div>
                      </div>
                    </div>
                  )}
                </button>
              </div>
            </div>

            <div className="mb-3 hidden h-8 w-px flex-none bg-gray-300 md:block" aria-hidden="true" />

            {linkedOrderRunId !== null && (
              <div className="relative mb-3 flex-none">
                <button
                  type="button"
                  onClick={() => setShowLinkedOrderRunInfo((prev) => !prev)}
                  className="inline-flex items-center gap-2 border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] font-semibold uppercase tracking-wide text-amber-900 hover:border-amber-700 hover:bg-amber-100"
                  title="Visualizza la run usata come ordini già piazzati"
                >
                  <span className="h-2 w-2 rounded-full bg-amber-600" aria-hidden="true" />
                  Run ordini #{linkedOrderRunId}
                </button>
                {showLinkedOrderRunInfo && (
                  <div className="absolute left-0 top-full z-50 mt-2 w-80 whitespace-normal border border-gray-300 bg-white p-4 text-sm shadow-xl">
                    <div className="mb-2 flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">Ordini già piazzati</div>
                        <div className="text-lg font-semibold text-gray-900">Run #{linkedOrderRunId}</div>
                      </div>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-black"
                        onClick={() => setShowLinkedOrderRunInfo(false)}
                        aria-label="Chiudi info run ordini"
                      >
                        ×
                      </button>
                    </div>
                    <p className="mb-3 whitespace-normal break-words text-xs leading-relaxed text-gray-600">
                      Questa run verrà trattata come ordini già piazzati: scala domanda e taglie. Il budget non viene modificato.
                    </p>
                    <div className="space-y-1 text-gray-700">
                      <div className="flex justify-between gap-3"><span>Status</span><strong>{linkedOrderRunInfo?.status ?? 'Configurata'}</strong></div>
                      <div className="flex justify-between gap-3"><span>Scala MP</span><strong>{configDraft?.preparation?.scale_linked_run_mp === false ? 'No' : 'Sì'}</strong></div>
                      <div className="flex justify-between gap-3"><span>Scala Budget</span><strong>No</strong></div>
                      {linkedOrderRunInfo?.started_at && (
                        <div className="flex justify-between gap-3"><span>Data</span><strong>{new Date(linkedOrderRunInfo.started_at).toLocaleString('it-IT')}</strong></div>
                      )}
                      {Array.isArray(linkedOrderRunInfo?.shops) && linkedOrderRunInfo.shops.length > 0 && (
                        <div className="flex justify-between gap-3"><span>Shop</span><strong>{linkedOrderRunInfo.shops.filter(Boolean).join(', ')}</strong></div>
                      )}
                      {linkedOrderRunInfo?.config_id !== undefined && (
                        <div className="flex justify-between gap-3"><span>Config</span><strong>{linkedOrderRunInfo.config_id}</strong></div>
                      )}
                      {linkedOrderRunInfo?.total_quantity !== undefined && (
                        <div className="flex justify-between gap-3"><span>Pezzi</span><strong>{Number(linkedOrderRunInfo.total_quantity).toLocaleString('it-IT')}</strong></div>
                      )}
                      {linkedOrderRunInfo?.total_value !== undefined && (
                        <div className="flex justify-between gap-3"><span>Valore</span><strong>€ {Math.round(Number(linkedOrderRunInfo.total_value)).toLocaleString('it-IT')}</strong></div>
                      )}
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-2 border-t border-gray-200 pt-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowLinkedOrderRunInfo(false);
                          void openLinkedOrderRunModal();
                        }}
                        disabled={isLinkingOrderRun}
                        className="border border-gray-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-700 hover:border-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Cambia run
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          void handleClearLinkedOrderRun();
                        }}
                        disabled={isLinkingOrderRun}
                        className="border border-red-300 bg-red-50 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-red-700 hover:border-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isLinkingOrderRun ? 'Rimozione…' : 'Rimuovi collegamento'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mb-3 ml-auto flex flex-none items-center justify-end gap-2 whitespace-nowrap">
              {isMutationQueueBusy && (
                <div className="inline-flex items-center gap-2 border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                  <span className="h-2 w-2 rounded-full bg-amber-500 motion-safe:animate-pulse" aria-hidden="true" />
                  Salvataggio modifiche…
                </div>
              )}
              {entryMode === 'from-result' && projectHasRuns && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      void handleImportFromMinimumValues();
                    }}
                    disabled={isImportingMinimumValues || isImportingOptimization || isPreparingFromResult || isLinkingOrderRun}
                    title="Importa vincoli dalla configurazione precedente"
                    className={`flex-none border px-2.5 py-2 text-[11px] font-semibold transition ${
                      isImportingMinimumValues || isImportingOptimization || isPreparingFromResult || isLinkingOrderRun
                        ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {isImportingMinimumValues ? 'IMPORTAZIONE…' : <><span className="hidden 2xl:inline">IMPORTA DA CONFIG PRECEDENTE</span><span className="2xl:hidden">IMPORTA CONFIG</span></>}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void handleImportFromOptimization(undefined, { lockTaglie: false });
                    }}
                    disabled={isImportingOptimization || isImportingMinimumValues || isPreparingFromResult || isLinkingOrderRun}
                    title="Importa solo le quantità/vincoli SKU dalla run precedente come valori minimi: le taglie verranno ricalcolate dall'ottimizzatore"
                    className={`flex-none border px-2.5 py-2 text-[11px] font-semibold transition ${
                      isImportingOptimization || isImportingMinimumValues || isPreparingFromResult || isLinkingOrderRun
                        ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                        : 'border-blue-300 bg-blue-50 text-blue-900 hover:border-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    {isImportingOptimization ? 'IMPORTAZIONE…' : <><span className="hidden 2xl:inline">IMPORTA DA OTTIMIZZAZIONE</span><span className="2xl:hidden">IMPORTA RUN</span></>}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void handleImportFromOptimization(undefined, { lockTaglie: true });
                    }}
                    disabled={isImportingOptimization || isImportingMinimumValues || isPreparingFromResult || isLinkingOrderRun}
                    title="Importa quantità e taglie dalla run precedente: quantità min=max e taglie preservate nello split consegne"
                    className={`flex-none border px-2.5 py-2 text-[11px] font-semibold transition ${
                      isImportingOptimization || isImportingMinimumValues || isPreparingFromResult || isLinkingOrderRun
                        ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                        : 'border-emerald-300 bg-emerald-50 text-emerald-900 hover:border-emerald-700 hover:bg-emerald-100'
                    }`}
                  >
                    {isImportingOptimization ? 'IMPORTAZIONE…' : <><span className="hidden 2xl:inline">IMPORTA OTTIMIZZAZIONE LOCK</span><span className="2xl:hidden">LOCK RUN</span></>}
                  </button>
                  <div className="flex flex-none items-center gap-1 border border-gray-300 bg-white p-1">
                    <button
                      type="button"
                      onClick={() => {
                        void handleOptimizationScopeModeChange('master');
                      }}
                      className={`border px-2.5 py-1.5 text-[11px] font-semibold transition ${
                        optimizationScopeMode === 'master'
                          ? 'border-black bg-black text-white'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Master
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        void handleOptimizationScopeModeChange('lista');
                      }}
                      className={`border px-2.5 py-1.5 text-[11px] font-semibold transition ${
                        optimizationScopeMode === 'lista'
                          ? 'border-black bg-black text-white'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Lista
                    </button>
                  </div>
                </>
              )}
              {(entryMode === 'new-optimization' || entryMode === 'from-result') && canAccessEditorTabs && (
                <button
                  type="button"
                  onClick={() => {
                    void openLinkedOrderRunModal();
                  }}
                  disabled={isLinkingOrderRun || isImportingMinimumValues || isImportingOptimization || isPreparingFromResult}
                  title="Usa una run precedente come ordini già piazzati: quantità e taglie verranno scalate nella prossima ottimizzazione"
                  className={`flex-none border px-2.5 py-2 text-[11px] font-semibold transition ${
                    isLinkingOrderRun || isImportingMinimumValues || isImportingOptimization || isPreparingFromResult
                      ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                      : 'border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-700 hover:bg-amber-100'
                  }`}
                >
                  {isLinkingOrderRun ? 'COLLEGAMENTO…' : linkedOrderRunId !== null ? 'CAMBIA RUN ORDINI' : 'USA RUN COME ORDINI'}
                </button>
              )}
              {entryMode === 'new-optimization' && activeTab === 'input-data' && isInputDataReady && !inputDataCompleted && (
                <button
                  type="submit"
                  form={inputDataFormId}
                  className="flex-none border border-black bg-black px-3 py-2 text-[11px] font-semibold text-white transition hover:bg-gray-800"
                >
                  CONFIGURA PERIMETRO &amp; BUDGET
                </button>
              )}
              {canAccessEditorTabs && (
                <button
                  type="button"
                  onClick={() => { void handleSaveAll(); }}
                  disabled={!hasUnsavedChanges || isSavingAll || isMutationQueueBusy}
                  title={hasUnsavedChanges ? 'Salva tutte le modifiche e ricalcola le metriche' : 'Nessuna modifica da salvare'}
                  className={`flex-none border px-3 py-2 text-[11px] font-semibold transition ${
                    hasUnsavedChanges && !isSavingAll && !isMutationQueueBusy
                      ? 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700'
                      : 'cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400'
                  }`}
                >
                  {isSavingAll ? 'SALVATAGGIO…' : hasUnsavedChanges ? <><span className="hidden xl:inline">SALVA MODIFICHE</span><span className="xl:hidden">SALVA</span></> : 'SALVATO'}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  void handleLaunchOptimization();
                }}
                disabled={isLaunchDisabled}
                title={launchButtonTitle}
                aria-disabled={isLaunchDisabled}
                className={`flex-none border px-3 py-2 text-[11px] font-semibold transition ${
                  isLaunchDisabled
                    ? 'cursor-not-allowed border-gray-300 bg-gray-200 text-gray-500'
                    : 'border-black bg-black text-white hover:bg-gray-800'
                }`}
              >
                {isOptimizing ? `OTTIMIZZAZIONE ${optimizationProgress}%` : <><span className="hidden xl:inline">ESEGUI OTTIMIZZAZIONE</span><span className="xl:hidden">ESEGUI</span></>}
              </button>
            </div>
          </div>
        </div>

        <div className="h-20" />
        <div className="mx-auto max-w-7xl">
          {importError && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {importError}
            </div>
          )}

          {optimizationError && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {optimizationError}
            </div>
          )}

          {isOptimizing && (
            <div className={entryMode === 'new-optimization' ? "mt-4 mb-4 border border-blue-200 bg-blue-50 p-4" : "mb-4 border border-blue-200 bg-blue-50 p-4"}>
              <div className="mb-2 flex items-center justify-between text-sm font-medium text-blue-900">
                <span>Ottimizzazione in corso...</span>
                <span>{optimizationProgress}%</span>
              </div>
              <div className="h-3 overflow-hidden bg-blue-100">
                <div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${optimizationProgress}%` }} />
              </div>
            </div>
          )}

          {showInitialLoader && (
            <div className="fixed inset-x-0 bottom-0 top-[60px] z-40 bg-white/78 backdrop-blur-[2px]">
              <div className="flex min-h-full items-start justify-center px-6 pt-24">
                <div className="flex min-w-[240px] flex-col items-center gap-4 border border-gray-200 bg-white/95 px-8 py-7 shadow-[0_18px_48px_rgba(17,24,39,0.14)]">
                  <svg className="h-12 w-12 animate-spin text-[#9F6460]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <div className="text-sm font-medium text-gray-700">Caricamento merchandising plan…</div>
                </div>
              </div>
            </div>
          )}

          <section className="bg-white p-5">
            {entryMode === 'new-optimization' && (
              <div className={activeTab === 'input-data' ? '' : 'hidden'}>
                <NewProjectModal
                  isOpen
                  embedded
                  projectType="optimization"
                  initialCatalogMode={initialCatalogMode || ''}
                  initialIncognito={initialIncognito}
                  onClose={() => {}}
                  onProjectCreated={() => {}}
                  formId={inputDataFormId}
                  hideSubmitButton
                  onOptimizationReadyChange={setIsInputDataReady}
                  onInputDirtyChange={() => {
                    setInputDataCompleted(false);
                    setIsInputDataReady(false);
                  }}
                  onOptimizationConfigured={async () => {
                    const hasData = await loadConfigAndContext();
                    setInputDataCompleted(hasData);
                    if (hasData) {
                      setActiveTab('perimetro-budget');
                    }
                  }}
                />
              </div>
            )}

            {(entryMode === 'from-result' || activeTab !== 'input-data') && (
              showInitialLoader ? (
                <div className="min-h-[50vh]" aria-hidden="true" />
              ) : showNoDataState ? (
                <div className="mt-6 flex min-h-[50vh] items-center justify-center text-sm font-medium text-gray-500">
                  Nessun dato merchandising disponibile.
                </div>
              ) : activeTab === 'perimetro-budget' ? (
              <div className="mt-6 grid gap-5 lg:h-[calc(100vh-11rem)] lg:grid-cols-[260px_minmax(0,1fr)] lg:overflow-hidden">
                <aside className="self-start border border-gray-300 bg-white p-4 lg:h-full lg:overflow-y-auto lg:overscroll-contain">
                  <div className="mb-4 bg-white">
                    {!showSkuTextArea ? (
                      <div className="space-y-2">
                        <button
                          type="button"
                          onClick={() => {
                            setShowSkuTextArea(true);
                            setSkuListError(null);
                            setSkuListMessage(null);
                          }}
                          disabled={isClearingSkuBounds}
                          className="w-full border border-gray-900 px-3 py-2 text-sm font-medium text-gray-900 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          AGGIUNGI LISTA SKU
                        </button>
                        {SHOW_EXCEL_SKU_IMPORT_BUTTON && (
                        <label className={`block w-full cursor-pointer border border-blue-700 px-3 py-2 text-center text-xs font-semibold text-blue-700 transition hover:bg-blue-50 ${isUploadingSkuList || isClearingSkuBounds || isMutationQueueBusy ? 'pointer-events-none opacity-50' : ''}`}>
                          {isUploadingSkuList ? 'IMPORTAZIONE…' : 'IMPORTA EXCEL TOTALE/TAGLIE'}
                          <input
                            type="file"
                            accept=".xlsx,.xls"
                            className="hidden"
                            disabled={isUploadingSkuList || isClearingSkuBounds || isMutationQueueBusy}
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              event.currentTarget.value = '';
                              if (file) void handleImportFixedTaglieExcel(file);
                            }}
                          />
                        </label>
                        )}
                        <button
                          type="button"
                          onClick={handleClearAllSkuBounds}
                          disabled={isClearingSkuBounds || isUploadingSkuList || isMutationQueueBusy}
                          className="w-full border border-red-700 px-3 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isClearingSkuBounds ? 'RIMOZIONE…' : 'RIMUOVI TUTTI GLI SKU'}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Shop destinazione</div>
                          <div className="flex flex-wrap gap-2">
                            {pageData.shops.filter((shop) => shop.id !== AREA_TAB_ID).map((shop) => {
                              const selected = skuTargetShopIds.includes(shop.id);
                              return (
                                <button
                                  key={`sku-target-${shop.id}`}
                                  type="button"
                                  onClick={() => setSkuTargetShopIds((prev) => prev.includes(shop.id) ? prev.filter((id) => id !== shop.id) : [...prev, shop.id])}
                                  className={`border px-3 py-1 text-[11px] font-medium transition ${
                                    selected
                                      ? 'border-black bg-black text-white'
                                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-900 hover:bg-gray-50'
                                  }`}
                                >
                                  {shop.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <label className="block text-xs text-gray-700">
                          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">Min value default</span>
                          <input
                            type="number"
                            min={0}
                            value={defaultSkuListMinValue}
                            onChange={(e) => setDefaultSkuListMinValue(Number(e.target.value))}
                            className="w-full border border-gray-300 px-2 py-2 text-sm focus:border-gray-900 focus:outline-none"
                            disabled={isUploadingSkuList}
                          />
                        </label>

                        {/* `uppercase` e' solo visivo: la normalizzazione vera la fa parseSkuText.
                            Serve perche' il buyer veda subito cosa verra' mandato, invece di
                            scoprirlo dalla review. Il placeholder resta leggibile. */}
                        <textarea
                          value={pastedSkuText}
                          onChange={(e) => setPastedSkuText(e.target.value)}
                          placeholder="Incolla la lista SKU (uno per riga o separati da virgola)"
                          className="min-h-[120px] w-full resize-y border border-gray-300 px-2 py-2 text-xs uppercase placeholder:normal-case focus:border-gray-900 focus:outline-none"
                          disabled={isUploadingSkuList}
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handleAddSkuList}
                            disabled={isUploadingSkuList || !pastedSkuText.trim()}
                            className="border border-black bg-black px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                          >
                            {isUploadingSkuList ? 'SALVATAGGIO…' : 'UPLOAD LISTA'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowSkuTextArea(false);
                              setPastedSkuText('');
                              setSkuListError(null);
                            }}
                            disabled={isUploadingSkuList}
                            className="border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:border-gray-900 disabled:opacity-50"
                          >
                            ANNULLA
                          </button>
                        </div>
                      </div>
                    )}
                    {skuListMessage && <div className="mt-2 text-xs text-green-700">{skuListMessage}</div>}
                    {skuListError && <div className="mt-2 text-xs text-red-700">{skuListError}</div>}
                  </div>

                  <div className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">Negozi</div>
                  <div className="mt-3 space-y-2" role="tablist" aria-label="Selezione negozio merchandising plan">
                    {pageData.shops.map((shop) => {
                      const selected = shop.id === selectedShopId;
                      return (
                        <button
                          key={shop.id}
                          type="button"
                          role="tab"
                          aria-selected={selected}
                          onClick={() => setSelectedShopId(shop.id)}
                          className={`w-full border px-3 py-3 text-left text-sm transition ${
                            selected
                              ? 'border-black bg-black text-white'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          {shop.label}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-5 border border-gray-200 bg-white p-3">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">Budget vincoli</div>
                    <div className="space-y-3">
                      <div className="border border-gray-200 bg-white p-3">
                        <div className="mb-2 text-xs font-semibold text-gray-900">Totale consegne</div>
                        <div className={budgetValueBroken ? 'rounded border border-red-300 bg-red-50 p-2' : ''}>
                          <MiniBar
                            label="Valore"
                            actual={Number(budgetComputedActuals.totalValue.toFixed(2))}
                            bound={budgetTotals.valueBound}
                            tone="rose"
                          />
                        </div>
                        <div className="mt-2" />
                        <div className={budgetItemsBroken ? 'rounded border border-red-300 bg-red-50 p-2' : ''}>
                          <MiniBar
                            label="Items"
                            actual={Number(budgetComputedActuals.totalItems.toFixed(2))}
                            bound={budgetTotals.itemsBound}
                            tone="blue"
                          />
                        </div>
                      </div>

                      {currentShop.budget.actuals.map((entry) => {
                        const bounds = currentShop.budget.upperBounds[entry.consegna];
                        return (
                          <div key={entry.consegna} className="border border-gray-200 bg-white p-3">
                            <div className="mb-2 text-xs font-semibold text-gray-900">{entry.consegna}</div>
                            <div className={budgetValueBroken ? 'rounded border border-red-300 bg-red-50 p-2' : ''}>
                            <MiniBar label="Valore" actual={Number((budgetComputedActuals.byConsegnaValue[entry.consegna] ?? 0).toFixed(2))} bound={bounds.value} tone="rose" />
                            </div>
                            <div className="mt-1 flex justify-end">
                              {!isAreaTabSelected && activeConstraintEditor === `budget:${entry.consegna}:value` ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    value={constraintDraftValue}
                                    onChange={(e) => setConstraintDraftValue(e.target.value)}
                                    className="w-24 border border-gray-300 px-2 py-1 text-xs focus:border-gray-900 focus:outline-none"
                                  />
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      await updateBudgetConstraint(entry.consegna, 'value', Number(constraintDraftValue || 0));
                                      setActiveConstraintEditor(null);
                                    }}
                                    className="border border-gray-300 px-1.5 py-1 text-[11px] hover:border-gray-900"
                                  >
                                    ✓
                                  </button>
                                </div>
                              ) : !isAreaTabSelected ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveConstraintEditor(`budget:${entry.consegna}:value`);
                                    setConstraintDraftValue(String(bounds.value ?? 0));
                                  }}
                                  className="text-xs text-gray-500"
                                  aria-label={`Modifica vincolo valore ${entry.consegna}`}
                                >
                                  ✎
                                </button>
                              ) : null}
                            </div>
                            <div className="mt-2" />
                            <div className={budgetItemsBroken ? 'rounded border border-red-300 bg-red-50 p-2' : ''}>
                            <MiniBar label="Items" actual={Number((budgetComputedActuals.byConsegnaItems[entry.consegna] ?? 0).toFixed(2))} bound={bounds.items} tone="blue" />
                            </div>
                            <div className="mt-1 flex justify-end">
                              {!isAreaTabSelected && activeConstraintEditor === `budget:${entry.consegna}:items` ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    value={constraintDraftValue}
                                    onChange={(e) => setConstraintDraftValue(e.target.value)}
                                    className="w-24 border border-gray-300 px-2 py-1 text-xs focus:border-gray-900 focus:outline-none"
                                  />
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      await updateBudgetConstraint(entry.consegna, 'items', Number(constraintDraftValue || 0));
                                      setActiveConstraintEditor(null);
                                    }}
                                    className="border border-gray-300 px-1.5 py-1 text-[11px] hover:border-gray-900"
                                  >
                                    ✓
                                  </button>
                                </div>
                              ) : !isAreaTabSelected ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveConstraintEditor(`budget:${entry.consegna}:items`);
                                    setConstraintDraftValue(String(bounds.items ?? 0));
                                  }}
                                  className="text-xs text-gray-500"
                                  aria-label={`Modifica vincolo items ${entry.consegna}`}
                                >
                                  ✎
                                </button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </aside>

                <div className="min-w-0 space-y-6 border border-gray-300 bg-white pr-1 lg:h-full lg:overflow-y-auto lg:overscroll-contain lg:pr-3">
                  <section className="bg-white p-4">
                    <div className="mb-5 border-b border-[#e6ddd9] pb-3">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <h2 className="text-lg font-semibold text-gray-900">MERCHANDISING PLAN VINCOLI</h2>
                        <div className="w-full md:max-w-sm">
                          {isAreaTabSelected && (
                            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
                              AREA ★ è in sola lettura.
                            </div>
                          )}
                          <input
                            type="text"
                            value={merchandisingSearch}
                            onChange={(e) => setMerchandisingSearch(e.target.value)}
                            placeholder="Cerca tipo filato o SKU"
                            className="w-full border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:outline-none"
                            aria-label="Cerca tipo filato o SKU"
                          />
                        </div>
                      </div>
                    </div>
                  {filteredTipoFilatoSections.map((section, sectionIndex) => {
                    const sectionActuals = computeSectionActuals(section, configDraft);
                    const isExpanded = expandedTfRows[section.tipoFilato] ?? false;
                    const totalMin = section.skuRows.reduce((sum, row) => sum + getEffectiveSkuQuantity(row, section.tipoFilato, configDraft), 0);
                    const hasSkus = section.skuRows.length > 0;
                    const isSectionLocked = hasSkus && section.skuRows.every((row) => Number(row.ubQuantityPerShop) === Number(row.lbQuantityPerShop));
                    const tfEconomicBroken = hasDefinedBound(section.constraints.economicValue.upperBound)
                      && sectionActuals.economicValue > Number(section.constraints.economicValue.upperBound ?? 0);
                    const tfItemsBroken = hasDefinedBound(section.constraints.items.upperBound)
                      && sectionActuals.items > Number(section.constraints.items.upperBound ?? 0);
                    const tfModelsBroken = hasDefinedBound(section.constraints.uniqueModels.upperBound)
                      && sectionActuals.uniqueModels > Number(section.constraints.uniqueModels.upperBound ?? 0);
                    const tfSkuCountBroken = hasDefinedBound(section.constraints.skuCount.upperBound)
                      && sectionActuals.skuCount > Number(section.constraints.skuCount.upperBound ?? 0);
                    const canOpenTransferModal = !isAreaTabSelected && (['items', 'economicValue'] as TipoFilatoTransferMetric[]).some((metric) => (
                      hasTipoFilatoMetricConstraint(section, metric) && getTipoFilatoTransferTargets(section.tipoFilato, metric).length > 0
                    ));

                    return (
                      <section
                        key={section.tipoFilato}
                        className={sectionIndex === 0 ? 'pb-6' : 'border-t border-[#e6ddd9] py-6'}
                      >
                        <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <h2 className="text-lg font-semibold text-gray-900">{section.tipoFilato}</h2>
                          </div>
                          {entryMode === 'from-result' && (
                            <div>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!hasSkus) return;
                                  void handleSetTipoFilatoLockState(section.tipoFilato, !isSectionLocked);
                                }}
                                disabled={!hasSkus || isAreaTabSelected}
                                className={`border px-3 py-1.5 text-xs font-semibold transition ${
                                  !hasSkus
                                    ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                                    : isSectionLocked
                                      ? 'border-black bg-black text-white hover:bg-gray-800'
                                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-900 hover:bg-gray-50'
                                }`}
                                title={!hasSkus ? 'Nessuno SKU disponibile' : isSectionLocked ? 'Sblocca SKU del tipo filato' : 'Blocca SKU del tipo filato'}
                                aria-label={isSectionLocked ? `Sblocca SKU ${section.tipoFilato}` : `Blocca SKU ${section.tipoFilato}`}
                              >
                                {isSectionLocked ? 'SBLOCCA' : 'BLOCCA'}
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_300px]">
                          <div className="overflow-auto border border-gray-200 bg-white">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    <div className="flex flex-col items-start gap-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (!hasSkus) return;
                                          setExpandedTfRows((prev) => ({ ...prev, [section.tipoFilato]: !isExpanded }));
                                        }}
                                        disabled={!hasSkus}
                                        className={`flex h-8 w-8 items-center justify-center border border-gray-300 transition ${hasSkus ? 'text-gray-700 hover:border-gray-900 hover:bg-gray-50' : 'cursor-not-allowed text-gray-300 bg-gray-50'}`}
                                        aria-label={isExpanded ? `Chiudi lista SKU ${section.tipoFilato}` : `Apri lista SKU ${section.tipoFilato}`}
                                        title={hasSkus ? (isExpanded ? 'Chiudi lista SKU' : 'Apri lista SKU') : 'Nessuno SKU disponibile'}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : 'rotate-0'}`}
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                          aria-hidden="true"
                                        >
                                          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L10.94 10 7.23 6.29a.75.75 0 111.06-1.06l4.24 4.24a.75.75 0 010 1.06l-4.24 4.24a.75.75 0 01-1.08 0z" clipRule="evenodd" />
                                        </svg>
                                      </button>
                                      <span>SKU</span>
                                    </div>
                                  </th>
                                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    <div className="flex flex-col items-end gap-2">
                                      <label className="text-right text-[11px] font-medium normal-case tracking-normal text-gray-700">
                                        <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500"># PEZZI (TF)</span>
                                        <div className="inline-flex items-center gap-1">
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            value={tipoFilatoMinDrafts[section.tipoFilato] ?? ''}
                                            placeholder={String(section.editableMinValue || 0)}
                                            onChange={(e) => handleTipoFilatoMinDraftChange(section.tipoFilato, e.target.value)}
                                            onBlur={() => {
                                              setTipoFilatoMinDrafts((prev) => {
                                                const next = { ...prev };
                                                delete next[section.tipoFilato];
                                                return next;
                                              });
                                            }}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                e.preventDefault();
                                                commitTipoFilatoMinDraft(section.tipoFilato);
                                                e.currentTarget.blur();
                                              }
                                              if (e.key === 'Escape') {
                                                setTipoFilatoMinDrafts((prev) => {
                                                  const next = { ...prev };
                                                  delete next[section.tipoFilato];
                                                  return next;
                                                });
                                                e.currentTarget.blur();
                                              }
                                            }}
                                            className="w-20 border border-gray-300 px-2 py-1 text-right text-sm placeholder:text-gray-400 focus:border-gray-900 focus:outline-none"
                                            disabled={isSectionLocked || isAreaTabSelected || savingKey !== null}
                                          />
                                          <span className="border border-gray-200 px-1.5 py-1 text-[10px] font-semibold leading-none text-gray-400" title="Premi Invio per salvare" aria-hidden="true">↵</span>
                                        </div>
                                      </label>
                                      <span># PEZZI</span>
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {!isExpanded ? (
                                  <tr className="bg-[#f9f6f5]">
                                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">Totale {section.tipoFilato}</td>
                                    <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">{totalMin}</td>
                                  </tr>
                                ) : (
                                  section.skuRows.map((row) => {
                                    const imageUrl = buildCloudinaryImageFromPath(row.imagePath, 44, 60);
                                    const skuDraftKey = getSkuDraftKey(section.tipoFilato, row.sku);
                                    const skuSelectionKey = getSkuSelectionKey(section.tipoFilato, row.sku);
                                    const isSkuSelected = Boolean(selectedSkuKeys[skuSelectionKey]);
                                    const hasLockedTaglie = Boolean(row.taglieLocked) || isSkuTaglieLocked(selectedShopId, row.sku);
                                    return (
                                      <tr key={row.sku} className={`hover:bg-gray-50 ${isSkuSelected ? 'bg-red-50/60' : ''}`}>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                          <div className="flex items-center gap-2">
                                            <input
                                              type="checkbox"
                                              checked={isSkuSelected}
                                              onChange={() => toggleSkuSelection(section.tipoFilato, row.sku)}
                                              disabled={isAreaTabSelected || savingKey !== null}
                                              className="h-4 w-4 border-gray-300 text-black focus:ring-black"
                                              aria-label={`Seleziona SKU ${row.sku}`}
                                            />
                                            <span>{row.sku}</span>
                                            {hasLockedTaglie && (
                                              <span
                                                className="whitespace-nowrap border border-emerald-300 bg-emerald-50 px-1 py-0.5 text-[8px] font-bold uppercase tracking-tight text-emerald-800"
                                                title="Taglie bloccate: questo SKU mantiene le taglie importate dalla run sorgente; il totale è bloccato, la consegna può essere ridistribuita."
                                              >
                                                Taglie lock
                                              </span>
                                            )}
                                            {imageUrl ? (
                                              // L'anteprima ingrandita con i prezzi si apre solo passando sull'immagine:
                                              // prima era agganciata all'intera cella e compariva anche solo sfiorando
                                              // il codice SKU o la casella di selezione.
                                              <img
                                                src={imageUrl}
                                                alt={row.sku}
                                                className="h-[40px] w-6 rounded border border-gray-300 object-cover"
                                                onMouseEnter={(e) => setSkuPreview({ sku: row.sku, priceWhs: row.priceWhs, priceRtl: row.priceRtl, imagePath: row.imagePath, x: e.clientX, y: e.clientY })}
                                                onMouseMove={(e) => setSkuPreview((prev) => (prev && prev.sku === row.sku ? { ...prev, x: e.clientX, y: e.clientY } : prev))}
                                                onMouseLeave={() => setSkuPreview((prev) => (prev && prev.sku === row.sku ? null : prev))}
                                                onError={(e) => {
                                                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                                                }}
                                              />
                                            ) : null}
                                            {!!row.weborderTags?.length && (
                                              <div className="flex flex-col flex-wrap items-start gap-1" style={{ maxHeight: '40px', overflow: 'hidden' }}>
                                                {row.weborderTags.map((tag) => {
                                                  const backgroundColor = getTagColor(tag);
                                                  return (
                                                    <span
                                                      key={`${row.sku}-${tag}`}
                                                      title={tag}
                                                      className="px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.04em]"
                                                      style={{
                                                        backgroundColor,
                                                        color: getContrastColor(backgroundColor),
                                                        borderRadius: '8px',
                                                        lineHeight: '1.2',
                                                        opacity: 0.9,
                                                      }}
                                                    >
                                                      {tag}
                                                    </span>
                                                  );
                                                })}
                                              </div>
                                            )}
                                            <SkuOrderForecastBadges
                                              orders={{
                                                orderQuantity: row.orderQuantity ?? 0,
                                                taglieOrder: row.taglieOrder,
                                                orderQuantityVenduta: row.orderQuantityVenduta,
                                                taglieOrderVenduta: row.taglieOrderVenduta,
                                                linkedOrderQuantity: row.linkedOrderQuantity,
                                                taglieLinkedOrder: row.taglieLinkedOrder,
                                              }}
                                              forecast={row.forecast ?? 0}
                                              compareTaglie={compareTaglie}
                                              ordersByShop={{
                                                sku: row.sku,
                                                defaultShopIds: parseShopIds(selectedShopId),
                                                load: loadSkuOrdersByShop,
                                              }}
                                            />
                                          </div>
                                        </td>
                                            <td className="px-4 py-3 text-right">
                                          <div className="inline-flex items-center gap-2">
                                            <label className="inline-flex items-center gap-2">
                                              <span className="sr-only">Minimo per {row.sku}</span>
                                              <div className="inline-flex items-center gap-1">
                                                <input
                                                  type="text"
                                                  inputMode="numeric"
                                                  pattern="[0-9]*"
                                                  value={skuMinDrafts[skuDraftKey] ?? ''}
                                                  placeholder={String(row.minValue ?? 0)}
                                                  onChange={(e) => handleSkuMinDraftChange(section.tipoFilato, row.sku, e.target.value)}
                                                  onBlur={() => clearSkuMinDraft(section.tipoFilato, row.sku)}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                      e.preventDefault();
                                                      commitSkuMinDraft(section.tipoFilato, row.sku);
                                                      e.currentTarget.blur();
                                                    }
                                                    if (e.key === 'Escape') {
                                                      clearSkuMinDraft(section.tipoFilato, row.sku);
                                                      e.currentTarget.blur();
                                                    }
                                                  }}
                                                  className="w-12 border border-gray-300 px-1 py-1 text-right text-sm placeholder:text-gray-400 focus:border-gray-900 focus:outline-none"
                                                  disabled={isSectionLocked || isAreaTabSelected || savingKey !== null}
                                                />
                                                <span className="border border-gray-200 px-1.5 py-1 text-[10px] font-semibold leading-none text-gray-400" title="Premi Invio per salvare" aria-hidden="true">↵</span>
                                              </div>
                                            </label>
                                            <button
                                              type="button"
                                              onClick={() => handleRemoveSku(section.tipoFilato, row.sku)}
                                              disabled={isAreaTabSelected || savingKey !== null}
                                              className="flex h-8 w-8 items-center justify-center border border-red-200 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                                              aria-label={`Rimuovi SKU ${row.sku}`}
                                              title="Rimuovi SKU"
                                            >
                                              ×
                                            </button>
                                            {savingKey === `${selectedShopId}:${row.sku}` && (
                                              <span className="text-[11px] font-medium text-gray-500">save…</span>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>

                          <div className="space-y-3 border border-gray-200 bg-white p-4">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-gray-900">Vincolo {section.tipoFilato}</div>
                              {!isAreaTabSelected && (
                                <button
                                  type="button"
                                  onClick={() => openTipoFilatoTransferModal(section.tipoFilato)}
                                  disabled={!canOpenTransferModal || savingKey !== null || isTransferSaving || isMutationQueueBusy}
                                  className="border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:border-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                  title={canOpenTransferModal ? 'Sposta pezzi o valore verso un altro tipo filato' : 'Servono almeno due tipi filato con vincoli compatibili'}
                                >
                                  Sposta budget / pezzi
                                </button>
                              )}
                              {!isAreaTabSelected && (() => {
                                const selectedCount = section.skuRows.filter((row) => selectedSkuKeys[getSkuSelectionKey(section.tipoFilato, row.sku)]).length;
                                return selectedCount > 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      void handleRemoveSelectedSkus(section.tipoFilato);
                                    }}
                                    disabled={savingKey !== null}
                                    className="border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                                  >
                                    Rimuovi selezionati ({selectedCount})
                                  </button>
                                ) : null;
                              })()}
                            </div>
                            <div className={tfEconomicBroken ? 'rounded border border-red-300 bg-red-50 p-2' : ''}>
                            <MiniBar
                              label="Valore economico"
                              actual={Number(sectionActuals.economicValue.toFixed(2))}
                              bound={section.constraints.economicValue.upperBound}
                              tone="rose"
                            />
                            </div>
                            <div className="mt-1 flex justify-end">
                              {!isAreaTabSelected && activeConstraintEditor === `tf:${section.tipoFilato}:economicValue` ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    value={constraintDraftValue}
                                    onChange={(e) => setConstraintDraftValue(e.target.value)}
                                    className="w-24 border border-gray-300 px-2 py-1 text-xs focus:border-gray-900 focus:outline-none"
                                  />
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      await updateTipoFilatoConstraint(section.tipoFilato, 'economicValue', Number(constraintDraftValue || 0));
                                      setActiveConstraintEditor(null);
                                    }}
                                    className="border border-gray-300 px-1.5 py-1 text-[11px] hover:border-gray-900"
                                  >
                                    ✓
                                  </button>
                                </div>
                              ) : !isAreaTabSelected ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveConstraintEditor(`tf:${section.tipoFilato}:economicValue`);
                                    setConstraintDraftValue(String(section.constraints.economicValue.upperBound ?? 0));
                                  }}
                                  className="text-xs text-gray-500"
                                  aria-label={`Modifica vincolo economico ${section.tipoFilato}`}
                                >
                                  ✎
                                </button>
                              ) : null}
                            </div>
                            <div className={tfItemsBroken ? 'rounded border border-red-300 bg-red-50 p-2' : ''}>
                            <MiniBar
                              label="Items"
                              actual={sectionActuals.items}
                              bound={section.constraints.items.upperBound}
                              tone="blue"
                            />
                            </div>
                            <div className="mt-1 flex justify-end">
                              {!isAreaTabSelected && activeConstraintEditor === `tf:${section.tipoFilato}:items` ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    value={constraintDraftValue}
                                    onChange={(e) => setConstraintDraftValue(e.target.value)}
                                    className="w-24 border border-gray-300 px-2 py-1 text-xs focus:border-gray-900 focus:outline-none"
                                  />
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      await updateTipoFilatoConstraint(section.tipoFilato, 'items', Number(constraintDraftValue || 0));
                                      setActiveConstraintEditor(null);
                                    }}
                                    className="border border-gray-300 px-1.5 py-1 text-[11px] hover:border-gray-900"
                                  >
                                    ✓
                                  </button>
                                </div>
                              ) : !isAreaTabSelected ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveConstraintEditor(`tf:${section.tipoFilato}:items`);
                                    setConstraintDraftValue(String(section.constraints.items.upperBound ?? 0));
                                  }}
                                  className="text-xs text-gray-500"
                                  aria-label={`Modifica vincolo items ${section.tipoFilato}`}
                                >
                                  ✎
                                </button>
                              ) : null}
                            </div>
                            <div className={tfModelsBroken ? 'rounded border border-red-300 bg-red-50 p-2' : ''}>
                            <MiniBar
                              label="Modelli unici"
                              actual={sectionActuals.uniqueModels}
                              bound={section.constraints.uniqueModels.upperBound}
                              tone="sand"
                            />
                            </div>
                            <div className="mt-1 flex justify-end">
                              {!isAreaTabSelected && activeConstraintEditor === `tf:${section.tipoFilato}:uniqueModels` ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    value={constraintDraftValue}
                                    onChange={(e) => setConstraintDraftValue(e.target.value)}
                                    className="w-24 border border-gray-300 px-2 py-1 text-xs focus:border-gray-900 focus:outline-none"
                                  />
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      await updateTipoFilatoConstraint(section.tipoFilato, 'uniqueModels', Number(constraintDraftValue || 0));
                                      setActiveConstraintEditor(null);
                                    }}
                                    className="border border-gray-300 px-1.5 py-1 text-[11px] hover:border-gray-900"
                                  >
                                    ✓
                                  </button>
                                </div>
                              ) : !isAreaTabSelected ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveConstraintEditor(`tf:${section.tipoFilato}:uniqueModels`);
                                    setConstraintDraftValue(String(section.constraints.uniqueModels.upperBound ?? 0));
                                  }}
                                  className="text-xs text-gray-500"
                                  aria-label={`Modifica vincolo modelli unici ${section.tipoFilato}`}
                                >
                                  ✎
                                </button>
                              ) : null}
                            </div>
                            {section.constraints.skuCount.active || hasDefinedBound(section.constraints.skuCount.upperBound) ? (
                              <>
                                <div className={tfSkuCountBroken ? 'rounded border border-red-300 bg-red-50 p-2' : ''}>
                                <MiniBar
                                  label="SKU unici"
                                  actual={sectionActuals.skuCount}
                                  bound={section.constraints.skuCount.upperBound}
                                  tone="sand"
                                />
                                </div>
                                <div className="mt-1 flex justify-end">
                                  {!isAreaTabSelected && activeConstraintEditor === `tf:${section.tipoFilato}:skuCount` ? (
                                    <div className="flex items-center gap-1">
                                      <input
                                        type="number"
                                        value={constraintDraftValue}
                                        onChange={(e) => setConstraintDraftValue(e.target.value)}
                                        className="w-24 border border-gray-300 px-2 py-1 text-xs focus:border-gray-900 focus:outline-none"
                                      />
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          await updateTipoFilatoConstraint(section.tipoFilato, 'skuCount', Number(constraintDraftValue || 0));
                                          setActiveConstraintEditor(null);
                                        }}
                                        className="border border-gray-300 px-1.5 py-1 text-[11px] hover:border-gray-900"
                                      >
                                        ✓
                                      </button>
                                    </div>
                                  ) : !isAreaTabSelected ? (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setActiveConstraintEditor(`tf:${section.tipoFilato}:skuCount`);
                                        setConstraintDraftValue(String(section.constraints.skuCount.upperBound ?? 0));
                                      }}
                                      className="text-xs text-gray-500"
                                      aria-label={`Modifica vincolo SKU unici ${section.tipoFilato}`}
                                    >
                                      ✎
                                    </button>
                                  ) : null}
                                </div>
                              </>
                            ) : null}
                            <div>
                            <MiniBar
                              label="Avg price"
                              actual={Number(sectionActuals.avgPrice.toFixed(2))}
                              bound={section.constraints.avgPrice.upperBound}
                              tone="rose"
                            />
                            </div>
                          </div>
                        </div>
                      </section>
                    );
                  })}
                  {filteredTipoFilatoSections.length === 0 && (
                    <div className="py-8 text-sm font-medium text-gray-500">Nessun tipo filato o SKU trovato.</div>
                  )}
                  </section>
                </div>
              </div>
            ) : (
              <div className="mt-6 p-4">
                {(isLoadingConfig || isLoadingContext) && <div className="mb-3 text-right text-sm text-gray-500">Caricamento config…</div>}

                {configDraft ? (
                  <OptimizationConfigEditor
                    config={configDraft}
                    availableTipoFilati={availableTipoFilati}
                    availableConsegne={currentShop?.budget.actuals.map((entry) => entry.consegna) || []}
                    onChange={setConfigDraft}
                    onSave={saveConfigDeferred}
                    isSaving={isMutationQueueBusy}
                  />
                ) : (
                  <div className="border border-gray-200 bg-white p-4 text-sm text-gray-500">
                    Config non ancora disponibile.
                  </div>
                )}
              </div>
            ))}
          </section>
        </div>
      </div>

      {skuValidationReview && (() => {
        const invalidRows = skuValidationReview.rows.filter((row) => !row.valid);
        const validRows = skuValidationReview.rows.filter((row) => row.valid);
        const canProceed = invalidRows.length === 0 && validRows.length > 0 && !isUploadingSkuList;

        return (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="sku-validation-review-title">
            <div className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
                <div>
                  <h3 id="sku-validation-review-title" className="text-lg font-semibold text-gray-900">Correggi lista SKU</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Alcuni SKU non sono validi. Rimuovi le righe evidenziate, poi procedi con il caricamento.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSkuValidationReview(null)}
                  className="rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                  aria-label="Chiudi revisione SKU"
                >
                  ×
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-6 py-3 text-sm">
                <span className="rounded-full bg-green-50 px-3 py-1 font-medium text-green-700">Validi: {validRows.length}</span>
                <span className="rounded-full bg-red-50 px-3 py-1 font-medium text-red-700">Da correggere: {invalidRows.length}</span>
                <span className="text-gray-500">Totale: {skuValidationReview.rows.length}</span>
              </div>

              <div className="min-h-0 flex-1 overflow-auto bg-white px-6 pb-4">
                <table className="relative min-w-full border-separate border-spacing-0 text-sm">
                  <thead>
                    <tr>
                      <th className="sticky top-0 z-20 bg-white px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 shadow-[inset_0_-1px_0_#e5e7eb]">SKU</th>
                      <th className="sticky top-0 z-20 bg-white px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 shadow-[inset_0_-1px_0_#e5e7eb]">Stato</th>
                      <th className="sticky top-0 z-20 bg-white px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 shadow-[inset_0_-1px_0_#e5e7eb]">Dettaglio</th>
                      <th className="sticky top-0 z-20 bg-white px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 shadow-[inset_0_-1px_0_#e5e7eb]">Azione</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {skuValidationReview.rows.map((row, index) => (
                      <tr key={`${row.sku}-${index}`} className={row.valid ? 'bg-white' : 'bg-red-50'}>
                        <td className="px-3 py-2 font-semibold text-gray-900">{row.sku}</td>
                        <td className="px-3 py-2">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${row.valid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {row.valid ? 'Valido' : 'Errore'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {row.valid ? 'Pronto per il caricamento' : row.issues.join(' • ')}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {!row.valid && (
                            <button
                              type="button"
                              onClick={() => handleRemoveSkuFromValidationReview(index)}
                              className="rounded border border-red-200 px-3 py-1 text-xs font-medium text-red-700 transition hover:bg-red-100"
                            >
                              Rimuovi
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-6 py-4">
                <div className="text-xs text-gray-500">
                  La textarea verrà aggiornata con gli SKU rimasti nella lista.
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSkuValidationReview(null)}
                    className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                    disabled={isUploadingSkuList}
                  >
                    Annulla
                  </button>
                  {invalidRows.length > 0 && (
                    <button
                      type="button"
                      onClick={handleRemoveInvalidSkusFromValidationReview}
                      className="rounded border border-red-200 px-4 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50"
                      disabled={isUploadingSkuList}
                    >
                      Rimuovi non validi
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleProceedWithSkuValidationReview}
                    disabled={!canProceed}
                    title={invalidRows.length > 0 ? 'Rimuovi prima gli SKU non validi' : undefined}
                    className={`rounded border px-4 py-2 text-sm font-semibold transition ${
                      canProceed
                        ? 'border-black bg-black text-white hover:bg-gray-800'
                        : 'cursor-not-allowed border-gray-300 bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isUploadingSkuList ? 'SALVATAGGIO…' : 'Correggi & procedi'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* SPJ-929: anteprima immagine ingrandita + prezzo whs al passaggio del mouse su una SKU */}
      {skuPreview && (skuPreview.imagePath || skuPreview.priceWhs !== undefined) && (
        <div
          className="pointer-events-none fixed z-[9999]"
          style={{
            left: Math.min(skuPreview.x + 16, window.innerWidth - 232),
            top: Math.min(skuPreview.y + 16, window.innerHeight - 340),
          }}
        >
          <div className="flex flex-col items-center gap-1 rounded border border-gray-300 bg-white p-2 shadow-2xl">
            {(() => {
              const previewUrl = buildCloudinaryImageFromPath(skuPreview.imagePath, 200, 267);
              return previewUrl ? (
                <img src={previewUrl} alt={skuPreview.sku} className="h-[267px] w-[200px] rounded object-cover" />
              ) : (
                <div className="flex h-[120px] w-[200px] items-center justify-center rounded bg-gray-100 text-xs text-gray-400">Nessuna immagine</div>
              );
            })()}
            <div className="text-center">
              <div className="text-xs font-semibold text-gray-900">{skuPreview.sku}</div>
              <div className="text-sm font-bold text-gray-900">
                Prezzo WHS: {skuPreview.priceWhs ? `€ ${skuPreview.priceWhs.toLocaleString('it-IT', { maximumFractionDigits: 2 })}` : 'n/d'}
              </div>
              <div className="text-sm font-bold text-gray-900">
                Prezzo RTL: {skuPreview.priceRtl ? `€ ${skuPreview.priceRtl.toLocaleString('it-IT', { maximumFractionDigits: 2 })}` : 'n/d'}
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
